/**
 * ElecBid — SmartAssessment (Universal Assessment Form)
 * Browning Electric
 *
 * The single, universal assessment form for all service types.
 * Replaces rigid per-type forms with a flexible, scope-first approach.
 *
 * Design principles:
 *   - Mobile-first — used on phones in the field
 *   - Scope notes is the primary input (free-form text + voice)
 *   - Auto-save with 2-second debounce to Supabase
 *   - Key measurements are collapsible — only expand if needed
 *   - Clean dark theme with lime green (#a5ce3a) accents
 *
 * Route: /assess/smart/:id
 *   - :id = "new" for fresh assessment
 *   - :id = UUID for loading existing draft
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useSession } from "../App";
import { queueVideo, listQueued, removeQueued } from "../lib/videoQueue";
import { apiFetch } from "../lib/apiFetch";
import { RichText } from "../lib/richText";
import { initialThinkPref } from "../lib/chatPrefs";
import { siblingLinesFor, siblingReason, normDesc, augmentMatches } from "../lib/propagatePick";
import { RATE_BY_TECH, isTypedRate, carryTypedRates } from "../lib/scopeTypedRate";
import FlowStrip from "./FlowStrip";
import { Lightbox } from "./MediaGallery";
import { ActivityTrail, startStep, argStep, finishStep, stepNote } from "./ChatActivity";

// ── Constants ───────────────────────────────────────────────────────────────

/** Service types aligned with strategy_defaults in elecbid_config */
const SERVICE_TYPES = [
  { key: "hot_tub_feed",      label: "Hot Tub Feed" },
  { key: "ev_charger",        label: "EV Charger" },
  { key: "service_upgrade",   label: "Service Upgrade" },
  { key: "panel_swap",        label: "Panel Swap" },
  { key: "generator_install", label: "Generator Install" },
  { key: "generator_service", label: "Generator Service" },
  { key: "solar_battery",     label: "Solar / Battery" },
  { key: "lighting",          label: "Lighting" },
  { key: "new_build",         label: "New Build" },
  { key: "commercial",        label: "Commercial" },
  { key: "other",             label: "Other" },
];

/** Workflow state display config */
const WORKFLOW_LABELS = {
  new:                    { label: "New",                color: "#a5ce3a" },
  needs_triage:           { label: "Needs Triage",       color: "#f59e0b" },
  awaiting_assessment:    { label: "Awaiting Assessment", color: "#f59e0b" },
  ready_for_ai_draft:     { label: "Ready for Quote",    color: "#3b82f6" },
  ai_draft_generated:     { label: "AI Draft Ready",     color: "#8b5cf6" },
  under_review:           { label: "Under Review",       color: "#8b5cf6" },
  approved_internal:      { label: "Approved",           color: "#10b981" },
  pushed_to_jobber:       { label: "Pushed to Jobber",   color: "#10b981" },
  sent_to_client:         { label: "Sent to Client",     color: "#10b981" },
  won:                    { label: "Won",                color: "#10b981" },
  lost:                   { label: "Lost",               color: "#ef4444" },
  needs_post_job_review:  { label: "Post-Job Review",    color: "#f59e0b" },
};

// ── Site-walkthrough video ───────────────────────────────────────────────────
const VIDEO_BUCKET = "assessment-videos";
const VIDEO_STATUS_LABELS = {
  uploading: "Uploading…",
  queued:    "Queued — will upload when online",
  uploaded:  "Saved",
  error:     "Upload failed",
};

const ANALYSIS_LABELS = {
  starting:     "Starting…",
  downloading:  "Loading video…",
  transcribing: "Transcribing narration…",
  sectioning:   "Finding stops…",
  capturing:    "Grabbing screenshots…",
  captioning:   "Captioning photos…",
  saving:       "Saving…",
};

function videoExtFromMime(mime, name) {
  if (name && name.includes(".")) {
    const e = name.split(".").pop().toLowerCase();
    if (e && e.length <= 5) return e;
  }
  if (!mime) return "mp4";
  if (mime.includes("quicktime")) return "mov";
  if (mime.includes("webm")) return "webm";
  if (mime.includes("matroska")) return "mkv";
  if (mime.includes("3gpp")) return "3gp";
  return "mp4";
}

function formatDuration(seconds) {
  const s = Math.round(Number(seconds) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

function formatVideoSize(bytes) {
  const b = Number(bytes) || 0;
  if (b >= 1024 * 1024 * 1024) return `${(b / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (b >= 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(0)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
}

// Read a video file's duration via a throwaway <video> element.
function getVideoDuration(file) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => {
        const d = v.duration;
        URL.revokeObjectURL(url);
        resolve(Number.isFinite(d) ? d : 0);
      };
      v.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
      v.src = url;
    } catch {
      resolve(0);
    }
  });
}

function makeVideoId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `vid_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

// Files the Scope Chat can actually reason over. Video is sampled into frames
// (Claude has no native video input); anything else is rejected with a note.
const CHAT_ATTACH_ACCEPT = "image/*,application/pdf,video/*,text/plain,text/csv,.pdf,.txt,.csv";
const CHAT_VIDEO_FRAMES = 8; // frames sampled from an attached clip

// Quote-brain memory tools (owner-only; the server decides whether to offer them).
const MEMORY_TOOL_STATUS = {
  remember_this:   "🧠 saving to the brain…",
  recall_memories: "🧠 checking what's saved…",
  forget_memory:   "🧠 retiring a rule…",
};

/**
 * Build the receipt chip for a finished memory tool call.
 *
 * Driven entirely by the server's tool_result — this is the tech's proof that a
 * rule landed. Before the tools existed the model would reply "got it, noted as
 * a standing rule" and write nothing, and there was no way to tell from the
 * chat. A failure has to be as loud as a success or we've rebuilt that bug with
 * extra steps.
 */
function memoryChipFor(ev) {
  const d = ev.detail || {};
  if (ev.name === "remember_this") {
    if (!d.ok) return { tone: "bad", text: `⚠️ NOT saved — ${d.error || "the brain rejected it"}. Say it again?` };
    const where = d.applies_to === "_all" ? "every quote" : String(d.applies_to || "").replace(/_/g, " ");
    return { tone: "good", text: `🧠 Saved to the brain — applies to ${where}` };
  }
  if (ev.name === "forget_memory") {
    return d.ok
      ? { tone: "good", text: "🧠 Rule retired — it won't shape new quotes" }
      : { tone: "bad", text: `⚠️ Couldn't retire that rule — ${d.error || "not found"}` };
  }
  if (ev.name === "recall_memories" && d.ok) {
    return { tone: "muted", text: `🧠 Read ${d.count} saved rule${d.count === 1 ? "" : "s"}` };
  }
  return null;
}

// A pricebook pick pins master_product_id on a materials line (Phase 3). The
// scope-chat AI re-emits the whole doc every turn and is instructed to copy
// pins verbatim — this is the client-side net for when it drops one anyway:
// carry each pin forward onto the line with the same normalized description in
// the same list (top level / per-option / per-section).
function carryMaterialPins(prevDoc, nextDoc) {
  if (!prevDoc || !nextDoc || typeof prevDoc !== "object") return nextDoc;
  const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const carryList = (prevList, nextList) => {
    if (!Array.isArray(prevList) || !Array.isArray(nextList)) return nextList;
    const pins = new Map();
    for (const p of prevList) {
      if (p && p.master_product_id && p.description) pins.set(norm(p.description), p.master_product_id);
    }
    if (!pins.size) return nextList;
    return nextList.map((n) => (n && !n.master_product_id && pins.has(norm(n.description))
      ? { ...n, master_product_id: pins.get(norm(n.description)) }
      : n));
  };
  const out = { ...nextDoc, materials: carryList(prevDoc.materials, nextDoc.materials) };
  if (Array.isArray(nextDoc.options)) {
    out.options = nextDoc.options.map((o, i) => (o ? { ...o, materials: carryList(prevDoc.options?.[i]?.materials, o.materials) } : o));
  }
  if (Array.isArray(nextDoc.sections)) {
    out.sections = nextDoc.sections.map((s, i) => (s ? { ...s, materials: carryList(prevDoc.sections?.[i]?.materials, s.materials) } : s));
  }
  return out;
}

function attachKindFor(file) {
  const t = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  if (t.startsWith("image/")) return "image";
  if (t === "application/pdf" || name.endsWith(".pdf")) return "document";
  if (t.startsWith("video/")) return "video";
  if (t === "text/plain" || t === "text/csv" || name.endsWith(".txt") || name.endsWith(".csv")) return "text";
  return "unsupported";
}

// Strip UI-only fields (status/error/previewUrl) before persisting/sending.
function cleanAttachment(a) {
  const out = { id: a.id, kind: a.kind, name: a.name || "file", mime: a.mime || "" };
  if (a.path) out.path = a.path;
  if (a.text) out.text = String(a.text).slice(0, 16000);
  if (Array.isArray(a.frames)) out.frames = a.frames.map((f) => ({ path: f.path }));
  return out;
}

function blobToB64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(String(r.result).split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

function pickAudioRecMime() {
  if (typeof MediaRecorder === "undefined") return "";
  for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"]) {
    try { if (MediaRecorder.isTypeSupported(m)) return m; } catch { /* ignore */ }
  }
  return "";
}

// Extract a (speed-compressed) audio-only blob from a playing <video> via Web
// Audio. Routing through a MediaStreamDestination (not ctx.destination) means
// nothing plays out the speakers. Returns a Blob; rejects if unsupported.
function extractAudioFromVideo(videoEl, speed) {
  return new Promise((resolve, reject) => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx || typeof MediaRecorder === "undefined") { reject(new Error("audio capture unsupported")); return; }
      const ctx = new AudioCtx();
      let source;
      try { source = ctx.createMediaElementSource(videoEl); } catch (e) { try { ctx.close(); } catch { /* */ } reject(e); return; }
      const dest = ctx.createMediaStreamDestination();
      source.connect(dest);
      const mime = pickAudioRecMime();
      const rec = new MediaRecorder(dest.stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      let settled = false;
      const finish = () => { if (settled) return; settled = true; try { ctx.close(); } catch { /* */ } resolve(new Blob(chunks, { type: rec.mimeType || mime || "audio/webm" })); };
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
      rec.onstop = finish;
      rec.onerror = () => { try { if (rec.state !== "inactive") rec.stop(); } catch { /* */ } };
      videoEl.onended = () => { try { if (rec.state !== "inactive") rec.stop(); } catch { /* */ } };
      try { videoEl.preservesPitch = true; videoEl.mozPreservesPitch = true; videoEl.webkitPreservesPitch = true; } catch { /* */ }
      videoEl.playbackRate = speed || 1;
      videoEl.currentTime = 0;
      rec.start();
      const p = videoEl.play();
      if (p && p.catch) p.catch((err) => { try { if (rec.state !== "inactive") rec.stop(); } catch { /* */ } try { ctx.close(); } catch { /* */ } reject(err); });
      const maxMs = Math.max(15000, ((videoEl.duration || 600) / (speed || 1)) * 1000 + 10000);
      setTimeout(() => { try { if (rec.state !== "inactive") rec.stop(); } catch { /* */ } }, maxMs);
    } catch (e) { reject(e); }
  });
}

// Seek a (paused) <video> to time t and grab a JPEG frame. Returns { blob, base64 }.
function extractFrameAt(videoEl, t) {
  return new Promise((resolve, reject) => {
    let done = false;
    const grab = () => {
      if (done) return; done = true;
      try {
        const w = videoEl.videoWidth || 1280, h = videoEl.videoHeight || 720;
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(videoEl, 0, 0, w, h);
        canvas.toBlob((b) => {
          if (!b) { reject(new Error("frame encode failed")); return; }
          const r = new FileReader();
          r.onloadend = () => resolve({ blob: b, base64: String(r.result).split(",")[1] });
          r.onerror = reject;
          r.readAsDataURL(b);
        }, "image/jpeg", 0.7);
      } catch (e) { reject(e); }
    };
    const onSeeked = () => { videoEl.removeEventListener("seeked", onSeeked); grab(); };
    videoEl.addEventListener("seeked", onSeeked);
    try { videoEl.pause(); } catch { /* */ }
    try { videoEl.currentTime = t; } catch (e) { videoEl.removeEventListener("seeked", onSeeked); reject(e); return; }
    setTimeout(() => { if (!done) { videoEl.removeEventListener("seeked", onSeeked); grab(); } }, 2500);
  });
}

// Thumbnail that fetches a short-lived signed URL for a private Storage frame.
function FrameThumb({ path, caption }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.storage.from(VIDEO_BUCKET).createSignedUrl(path, 3600);
        if (!cancelled && !error) setUrl(data.signedUrl);
      } catch { /* leave blank */ }
    })();
    return () => { cancelled = true; };
  }, [path]);
  return (
    <figure className="sa-frame">
      {url
        ? <img className="sa-frame-img" src={url} alt={caption || "frame"} loading="lazy" />
        : <div className="sa-frame-ph">…</div>}
      {caption && <figcaption className="sa-frame-cap">{caption}</figcaption>}
    </figure>
  );
}

// Site-photo thumbnail: signed URL from the private bucket + status/pushed
// badges + remove control.
function PhotoThumb({ photo, onRemove, disabled }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (photo.status !== "uploaded") return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.storage.from(VIDEO_BUCKET).createSignedUrl(photo.path, 3600);
        if (!cancelled && !error) setUrl(data.signedUrl);
      } catch { /* leave placeholder */ }
    })();
    return () => { cancelled = true; };
  }, [photo.path, photo.status]);
  return (
    <div className="sa-photo-card">
      {url
        ? <img className="sa-photo-img" src={url} alt={photo.name || "site photo"} loading="lazy" />
        : <div className="sa-photo-ph">{photo.status === "error" ? "⚠" : "…"}</div>}
      <button
        type="button"
        className="sa-photo-del"
        title="Remove photo"
        disabled={disabled}
        onClick={() => onRemove(photo)}
      >✕</button>
      {photo.status === "uploading" && <div className="sa-photo-badge">Uploading…</div>}
      {photo.status === "error" && <div className="sa-photo-badge err">Upload failed — remove &amp; retake</div>}
      {photo.status === "uploaded" && photo.pushed_at && <div className="sa-photo-badge ok">In Jobber ✓</div>}
    </div>
  );
}
/** Debounce delay for auto-save (ms) */
const AUTOSAVE_DELAY = 2000;

// Stamp `selected` on every linked Jobber note once the tech has ticked some —
// the server's formatJobberNotesForPrompt reads the flags. No ticks → the notes
// pass through untouched (every note is context, as before the selector).
function flagSelectedNotes(notes, ids) {
  const list = Array.isArray(notes) ? notes : [];
  if (!Array.isArray(ids)) return list;            // never chose → untouched
  const set = new Set(ids.map(String));            // [] → every flag false = explicit NONE
  return list.map((n) => ({ ...n, selected: set.has(String(n.id)) }));
}

// Archived note photos ride to the AI as id + caption (the first chars of the
// note they were posted on). Once the tech has ticked notes, drop the photos
// that belong to UNTICKED notes — otherwise a junk note's caption + photo still
// reach the model and invite a look at the wrong site. Photo-only notes (no
// text to tick) are kept.
//
// ⭐ Match on the note GID, which job_media stores and indexes (`note_gid`,
// migration 20260730_job_media.sql). An earlier version of this matched the
// truncated 500-char `note_message` text instead, on the mistaken belief that
// the gid was not stored — that dropped photos off ticked notes whose text was
// cut, and let an unticked note's photo through when two notes shared a prefix.
// The text match is kept only as a fallback for rows archived before the gid
// was carried through the API.
function filterMediaBySelection(media, notes, ids) {
  const list = Array.isArray(media) ? media : [];
  if (!Array.isArray(ids)) return list;
  const norm = (s) => String(s || "").replace(/\s+/g, " ").trim().toLowerCase();
  const set = new Set(ids.map(String));
  const tickedNotes = (Array.isArray(notes) ? notes : []).filter((n) => n.id && set.has(String(n.id)));
  const tickedGids = new Set(tickedNotes.map((n) => String(n.id)));
  const tickedText = tickedNotes.map((n) => norm(n.message));
  const allGids = new Set((Array.isArray(notes) ? notes : []).map((n) => String(n.id)).filter(Boolean));
  return list.filter((m) => {
    const gid = m.noteGid ? String(m.noteGid) : null;
    // Exact: this photo's note is known and either ticked or not.
    if (gid && allGids.has(gid)) return tickedGids.has(gid);
    const cap = norm(m.noteMessage);
    if (!cap) return true;                     // photo-only note — nothing to tick against
    return tickedText.some((t) => t.startsWith(cap) || cap.startsWith(t));
  });
}

// Ids of the Jobber notes already shipped into the chat (user turns carry
// `note_ids`, persisted with the transcript) — Send never resends a note, so a
// long thread can go up in batches and a later tick just ADDS to the doc.
function sentNoteIdsOf(messages) {
  const out = new Set();
  for (const m of Array.isArray(messages) ? messages : []) {
    if (m && m.role === "user" && Array.isArray(m.note_ids)) for (const id of m.note_ids) out.add(String(id));
  }
  return out;
}


// ── Helper: generate UUID v4 (no external deps) ────────────────────────────
function uuidv4() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}


// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT: SmartAssessment
// ═══════════════════════════════════════════════════════════════════════════

export default function SmartAssessment() {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useSession() || {};

  // ── Resolve assessment ID ──────────────────────────────────────────────
  // "new" → generate a fresh UUID; otherwise use the URL param
  const assessmentId = useMemo(() => {
    if (paramId === "new") return uuidv4();
    return paramId;
  }, [paramId]);

  // ── Seed data from navigation state (Jobber request or New Assessment modal)
  // Memoized — location.state is null on direct loads, and a fresh {} every
  // render would churn saveToSupabase's identity (it depends on seedData).
  const seedData = useMemo(() => location.state || {}, [location.state]);

  // ── Form state ─────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    workflow_state: "new",
    quote_strategy: "",
    jobber_request_id: seedData.jobberRequestId || "",
    jobber_client_id: seedData.jobberClientId || "",
    // Jobber note ids the tech ticked as describing THIS job. null = never
    // chose (every linked note rides as context, legacy); [] = ticked NONE
    // (the AI is told not to use any of them); [...] = only those notes.
    jobber_note_ids: null,
    client_name: seedData.clientName || "",
    phone: seedData.phone || "",
    email: seedData.email || "",
    address: seedData.address || "",
    service_type: seedData.serviceType || "",
    scope_notes: seedData.description || "",
    panel_size: "",
    available_spaces: "",
    panel_condition: "",
    load_size: "",
    run_distance_feet: "",
    routing_method: "",
    materials: [],
    misc_materials_amount: "",
    estimated_hours: "",
    crew_size: "",
    permit_required: false,
    permit_cost: "",
    subcontractor_required: false,
    subcontractor_description: "",
    subcontractor_cost: "",
    tech_notes: "",
    photos: [],
  });

  // ── UI state ───────────────────────────────────────────────────────────
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [loaded, setLoaded] = useState(false);
  // Job basics live in a compact header — collapsed by default, tap to edit.
  const [basicsOpen, setBasicsOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [whisperAvailable, setWhisperAvailable] = useState(null);

  // ── Site-walkthrough video state ───────────────────────────────────────
  const [videos, setVideos] = useState([]);   // lightweight metadata index
  const [playingUrl, setPlayingUrl] = useState(null);
  const [playLoading, setPlayLoading] = useState(false);
  // Per-video analysis progress: { [videoId]: { status, error } }
  const [analysisState, setAnalysisState] = useState({});

  // ── Scope Chat + live Scope Document (replaces the quick-scope textarea) ─
  // messages: { role:"user"|"assistant", content, attachments, thinking, streaming, status, stopped }
  const [chatMessages,  setChatMessages]  = useState([]);
  const [chatInput,     setChatInput]     = useState("");
  const [chatStreaming, setChatStreaming] = useState(false);
  // Staging tray: attachments the tech added but hasn't sent yet.
  // { id, kind, name, mime, size, status:"uploading"|"ready"|"error", path?, text?, frames?, error? }
  const [chatAttachments, setChatAttachments] = useState([]);
  const [attachBusy, setAttachBusy] = useState(false);
  const [scopeDoc,      setScopeDoc]      = useState(null);  // live doc from /api/scope-chat
  const [converting,    setConverting]    = useState(false); // Convert to Quote busy
  const [convertError,  setConvertError]  = useState(null);
  const [photoBusy,     setPhotoBusy]     = useState(false); // a site photo is uploading
  // Scope notes ride the FIRST Jobber push only. Re-converting the same
  // assessment must never re-post them (photos are guarded by their own
  // persisted pushed_at); this flags that the note already went up this session.
  const notePushedRef = useRef(false);
  // Ref mirror of `converting` — async guards must check the ref, not
  // closured state, or a double-tap fires a SECOND convert (two draft
  // quotes, double Jobber note).
  const convertingRef = useRef(false);
  const [modelPref, setModelPref] = useState(() => {
    try { return localStorage.getItem("sc-model") || "standard"; } catch { return "standard"; }
  });
  // Defaults ON (with a one-time off→standard migration) so the tech can watch
  // the AI think while it works — see src/lib/chatPrefs.js.
  const [thinkPref, setThinkPref] = useState(initialThinkPref);

  // ── Linked Jobber notes (request + its jobs) ─────────────────────────────
  // Read-only context pulled from Jobber so the tech sees the team's notes in
  // ElecBid and the scope chat builds quotes from them (no copy/paste).
  const [jobberNotes,        setJobberNotes]        = useState([]);
  const [jobberNotesLoading, setJobberNotesLoading] = useState(false);
  const [jobberNotesOpen,    setJobberNotesOpen]    = useState(true);
  const jobberNotesRef = useRef([]);
  jobberNotesRef.current = jobberNotes;
  // The notes' PHOTOS — archived copies served from OUR bucket (signed URLs;
  // never Jobber's short-lived S3 links). Shown as a strip in the notes card
  // and offered to the scope AI as ids it can view_job_photos on demand.
  const [jobberMedia, setJobberMedia] = useState([]);
  const [jobberMediaNote, setJobberMediaNote] = useState(null);
  const jobberMediaRef = useRef([]);
  jobberMediaRef.current = jobberMedia;
  // Ticked note ids as a Set for the card. Source of truth is
  // form.jobber_note_ids so the ticks autosave with the row and survive a
  // refresh / another device (and quote-brain reads the same column).
  const selectedNoteIds = useMemo(() => new Set((form.jobber_note_ids || []).map(String)), [form.jobber_note_ids]);
  // The tech's clarifications typed under the notes — sent WITH the ticked
  // notes as one chat message (it becomes chat text, so no column of its own;
  // localStorage keeps an unsent draft across a refresh, this browser only).
  const addendumKey = `scope-notes-add-${assessmentId || "new"}`;
  const [noteAddendum, setNoteAddendum] = useState(() => {
    try { return localStorage.getItem(addendumKey) || ""; } catch { return ""; }
  });
  const [noteIncludeTyped, setNoteIncludeTyped] = useState(true);
  // "Use typed scope" applies only before the chat starts: once a scope doc
  // exists, scope_notes is the doc mirror (applyScopeDoc overwrites it) and
  // resending it would pass a summary off as the tech's notes.
  const typedMeta = useMemo(() => {
    const typed = `${form.scope_notes || ""} ${form.tech_notes || ""}`.trim();
    return { show: chatMessages.length === 0 && !scopeDoc && !!typed, words: typed ? typed.split(/\s+/).filter(Boolean).length : 0, chars: typed.length };
  }, [form.scope_notes, form.tech_notes, chatMessages.length, scopeDoc]);
  // Notes already shipped into the chat (user turns carry note_ids).
  const sentNoteIds = useMemo(() => sentNoteIdsOf(chatMessages), [chatMessages]);

  // ── Refs ────────────────────────────────────────────────────────────────
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const videosRef = useRef([]);
  const recordInputRef = useRef(null);
  const uploadInputRef = useRef(null);
  const videoBlobCacheRef = useRef({}); // { [videoId]: File/Blob } — avoids re-download for analysis
  const photoCameraRef = useRef(null);  // native camera capture
  const photoPickRef = useRef(null);    // gallery / file picker (multiple)
  const isNewRef = useRef(paramId === "new");
  const formRef = useRef(form);
  formRef.current = form; // Always keep ref in sync with latest state

  // ── Scope Chat refs ─────────────────────────────────────────────────────
  const chatAbortRef    = useRef(null);   // AbortController for the live stream
  const chatLoadedRef   = useRef(false);  // localStorage history loaded yet?
  const voiceTargetRef  = useRef("chat"); // "chat" | "scope" | "notes" — where voice transcripts land
  const chatMessagesRef = useRef([]);
  chatMessagesRef.current = chatMessages;
  const chatAttachmentsRef = useRef([]);
  chatAttachmentsRef.current = chatAttachments;
  const scopeDocRef = useRef(null);
  scopeDocRef.current = scopeDoc;

  // ── Load existing draft on mount ───────────────────────────────────────
  useEffect(() => {
    if (paramId === "new") {
      setLoaded(true);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("smart_assessments")
          .select("*")
          .eq("id", assessmentId)
          .single();

        if (error || !data) {
          // No existing draft — treat as new
          if (!cancelled) setLoaded(true);
          return;
        }

        if (!cancelled) {
          setForm((prev) => ({
            ...prev,
            workflow_state: data.workflow_state || "new",
            quote_strategy: data.quote_strategy || "",
            jobber_request_id: data.jobber_request_id || "",
            jobber_client_id: data.jobber_client_id || "",
            jobber_note_ids: Array.isArray(data.jobber_note_ids) ? data.jobber_note_ids.map(String) : null,
            client_name: data.client_name || "",
            phone: data.phone || "",
            email: data.email || "",
            address: data.address || "",
            service_type: data.service_type || "",
            scope_notes: data.scope_notes || "",
            panel_size: data.panel_size || "",
            available_spaces: data.available_spaces != null ? String(data.available_spaces) : "",
            panel_condition: data.panel_condition || "",
            load_size: data.load_size || "",
            run_distance_feet: data.run_distance_feet != null ? String(data.run_distance_feet) : "",
            routing_method: data.routing_method || "",
            materials: data.materials || [],
            misc_materials_amount: data.misc_materials_amount != null ? String(data.misc_materials_amount) : "",
            estimated_hours: data.estimated_hours != null ? String(data.estimated_hours) : "",
            crew_size: data.crew_size || "",
            permit_required: data.permit_required || false,
            permit_cost: data.permit_cost != null ? String(data.permit_cost) : "",
            subcontractor_required: data.subcontractor_required || false,
            subcontractor_description: data.subcontractor_description || "",
            subcontractor_cost: data.subcontractor_cost != null ? String(data.subcontractor_cost) : "",
            tech_notes: data.tech_notes || "",
            photos: data.photos || [],
          }));
          setVideos(Array.isArray(data.videos) ? data.videos : []);

          // Restore the scope chat + live doc from the row. localStorage may
          // be AHEAD of the DB (offline / failed save) — keep whichever
          // transcript is longer; the doc follows the winning source.
          const dbChat = Array.isArray(data.scope_chat) ? data.scope_chat : [];
          if (dbChat.length && dbChat.length >= (chatMessagesRef.current || []).length) {
            setChatMessages(dbChat.map((m) => ({
              role: m.role === "assistant" ? "assistant" : "user",
              content: String(m.content || ""),
              attachments: Array.isArray(m.attachments) ? m.attachments : [],
              thinking: String(m.thinking || ""),
              streaming: false,
              status: null,
              stopped: !!m.stopped,
              memory: Array.isArray(m.memory) ? m.memory : [],
              steps: Array.isArray(m.steps) ? m.steps : [],
              note_ids: Array.isArray(m.note_ids) ? m.note_ids : [],
            })));
            if (data.scope_doc && typeof data.scope_doc === "object") setScopeDoc(data.scope_doc);
          } else if (data.scope_doc && typeof data.scope_doc === "object" && !scopeDocRef.current) {
            setScopeDoc(data.scope_doc);
          }
          setLoaded(true);
        }
      } catch {
        if (!cancelled) setLoaded(true);
      }
    })();

    return () => { cancelled = true; };
  }, [paramId, assessmentId]);

  // ── Pull linked Jobber notes when the assessment is tied to a request ────
  // Runs whenever the request id becomes known (seed nav state or DB load).
  const requestId = form.jobber_request_id;
  useEffect(() => {
    if (!requestId) { setJobberNotes([]); setJobberMedia([]); setJobberMediaNote(null); return; }
    let cancelled = false;
    setJobberNotesLoading(true);
    (async () => {
      try {
        const res = await apiFetch("/api/jobber", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "fetchLinkedNotes", requestId }),
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled) {
          setJobberNotes(Array.isArray(data?.notes) ? data.notes : []);
          setJobberMedia(Array.isArray(data?.media) ? data.media : []);
          setJobberMediaNote(data?.mediaNote || null);
        }
      } catch {
        if (!cancelled) { setJobberNotes([]); setJobberMedia([]); }
      } finally {
        if (!cancelled) setJobberNotesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [requestId]);

  // ── Save to Supabase (uses ref to always read latest form state) ─────
  const saveToSupabase = useCallback(async () => {
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    setSaveStatus("saving");

    const f = formRef.current;
    try {
      // Build the record — convert string numbers back to numeric types
      const record = {
        id: assessmentId,
        workflow_state: f.workflow_state || "new",
        quote_strategy: f.quote_strategy || null,
        jobber_request_id: f.jobber_request_id || null,
        // Only when known — quote-brain's linkClient stamps this server-side at
        // quote-push, and an unconditional null here would stomp that stamp on
        // the next auto-save from an already-open tab.
        ...(f.jobber_client_id ? { jobber_client_id: f.jobber_client_id } : {}),
        // Ticked Jobber notes: null = never chose (every note is context),
        // [] = ticked NONE (notes must not be used), [...] = only those.
        jobber_note_ids: Array.isArray(f.jobber_note_ids) ? f.jobber_note_ids : null,
        client_name: f.client_name || null,
        phone: f.phone || null,
        email: f.email || null,
        address: f.address || null,
        service_type: f.service_type || null,
        scope_notes: f.scope_notes || null,
        panel_size: f.panel_size || null,
        available_spaces: f.available_spaces ? parseInt(f.available_spaces, 10) : null,
        panel_condition: f.panel_condition || null,
        load_size: f.load_size || null,
        run_distance_feet: f.run_distance_feet ? parseFloat(f.run_distance_feet) : null,
        routing_method: f.routing_method || null,
        materials: f.materials,
        misc_materials_amount: f.misc_materials_amount ? parseFloat(f.misc_materials_amount) : 0,
        estimated_hours: f.estimated_hours ? parseFloat(f.estimated_hours) : null,
        crew_size: f.crew_size || null,
        permit_required: f.permit_required,
        permit_cost: f.permit_cost ? parseFloat(f.permit_cost) : 0,
        subcontractor_required: f.subcontractor_required,
        subcontractor_description: f.subcontractor_description || null,
        subcontractor_cost: f.subcontractor_cost ? parseFloat(f.subcontractor_cost) : 0,
        tech_notes: f.tech_notes || null,
        photos: f.photos,
        created_by: session?.user?.email || null,
        tech_id: session?.user?.email || null,
        updated_at: new Date().toISOString(),
        last_auto_saved_at: new Date().toISOString(),
      };

      // Scope chat + live doc persist with the row (20260712 migration) so
      // returning on any device restores the conversation and rough draft.
      let chatOut = (chatMessagesRef.current || [])
        // Never persist a reply that is still streaming. Its content grows on
        // every token, and the merge key now includes content LENGTH — so a
        // save that lands mid-stream writes a partial whose key can never match
        // the finished reply, and both end up in the transcript.
        .filter((m) => !m.streaming)
        .filter((m) => (m.content || "").trim() || (m.attachments && m.attachments.length))
        .map((m) => ({
          role: m.role,
          content: m.content,
          attachments: (m.attachments || []).map(cleanAttachment),
          thinking: (m.thinking || "").slice(0, 2000),
          stopped: !!m.stopped,
          memory: m.memory || [],   // the "saved to the brain" receipts — keep them on reload
          steps: (m.steps || []).slice(0, 30),  // the ⚙ activity trail — same deal
          note_ids: m.note_ids || [],           // Jobber notes shipped in this turn — Send never resends them
        }));
      // Concurrency: another writer (second device/tab, or an out-of-band
      // agent) may have appended turns since this tab loaded — a blind
      // overwrite deletes them (it stomped two correction messages on the
      // Whipple assessment). Merge: when the DB transcript has turns this tab
      // doesn't know, keep the DB's order and append only our genuinely-new
      // turns. Best-effort — a failed read falls back to the old overwrite.
      try {
        const { data: cur } = await supabase
          .from("smart_assessments").select("scope_chat").eq("id", assessmentId).maybeSingle();
        const dbChat = Array.isArray(cur?.scope_chat) ? cur.scope_chat : [];
        if (dbChat.length) {
          // Length is part of the key: two "Send N notes" batches share the
          // same ~330-char preamble and would otherwise collapse into one.
          const key = (m) => `${m.role}|${String(m.content || "").length}|${String(m.content || "").slice(0, 300)}`;
          const dbKeys = new Set(dbChat.map(key));
          const localKeys = new Set(chatOut.map(key));
          const dbOnly = dbChat.some((m) => !localKeys.has(key(m)));
          if (dbOnly) chatOut = [...dbChat, ...chatOut.filter((m) => !dbKeys.has(key(m)))];
        }
      } catch { /* merge is best-effort */ }
      record.scope_chat = chatOut.slice(-40);
      // Never stomp a doc another writer produced with this tab's empty state.
      if (scopeDocRef.current) record.scope_doc = scopeDocRef.current;

      // Upsert — creates on first save, updates after
      const { error } = await supabase
        .from("smart_assessments")
        .upsert(record, { onConflict: "id" });

      if (error) throw error;

      setSaveStatus("saved");
      isNewRef.current = false;

      // Replace URL if we were on /new so refresh loads the draft
      if (paramId === "new") {
        navigate(`/assess/smart/${assessmentId}`, { replace: true, state: seedData });
      }
    } catch (err) {
      console.error("Auto-save failed:", err);
      setSaveStatus("error");
      dirtyRef.current = true; // Retry on next change
    }
  }, [assessmentId, session, paramId, navigate, seedData]);

  // ── Field updater (marks dirty, triggers autosave timer) ───────────────
  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    dirtyRef.current = true;
    setSaveStatus("idle");

    // Reset debounce timer
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      saveToSupabase();
    }, AUTOSAVE_DELAY);
  }, [saveToSupabase]);

  // ── Explicit save (Save Draft button) ──────────────────────────────────
  const handleSaveDraft = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    dirtyRef.current = true;
    saveToSupabase();
  }, [saveToSupabase]);

  // Classic-form quote generation (Ready-for-Quote + Multi-option Build) was
  // retired 2026-07-14 — all quoting now flows through Scope Chat → Convert to
  // Quote (handleConvertScope), which already builds multi-option quotes when
  // the scope doc carries options[]. The old suggestOptions/draftOptions and
  // legacy single-option generate handlers were removed with the button.

  // ═══════════════════════════════════════════════════════════════════════
  // Scope Chat — ChatGPT-style intake that builds a live scope document.
  // Streams SSE from /api/scope-chat (thinking + text + tool events), keeps
  // the doc synced into the form (scope_notes / estimated_hours /
  // service_type / client_name), and converts deterministically via
  // quote-brain draftFromScope.
  // ═══════════════════════════════════════════════════════════════════════

  const chatStoreKey = `scope-chat-${assessmentId || "new"}`;

  // Load persisted conversation + doc once per assessment key (refresh-safe).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(chatStoreKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (Array.isArray(saved.messages) && saved.messages.length) {
          setChatMessages(saved.messages.map((m) => ({
            role: m.role === "assistant" ? "assistant" : "user",
            content: String(m.content || ""),
            attachments: Array.isArray(m.attachments) ? m.attachments : [],
            thinking: String(m.thinking || ""),
            streaming: false,
            status: null,
            stopped: !!m.stopped,
            steps: Array.isArray(m.steps) ? m.steps : [],
            note_ids: Array.isArray(m.note_ids) ? m.note_ids : [],
          })));
        }
        if (saved.scope_doc && typeof saved.scope_doc === "object") {
          setScopeDoc(saved.scope_doc);
        }
      }
    } catch { /* corrupted store — start fresh */ }
    chatLoadedRef.current = true;
  }, [chatStoreKey]);

  // Persist conversation + doc (capped at 40 messages). Skipped while a
  // reply is streaming so we don't hammer localStorage per token — the
  // final state lands when streaming flips off.
  useEffect(() => {
    if (!chatLoadedRef.current || chatStreaming) return;
    try {
      const messages = chatMessages.slice(-40).map((m) => ({
        role: m.role,
        content: m.content,
        attachments: (m.attachments || []).map(cleanAttachment),
        thinking: (m.thinking || "").slice(0, 2000),
        stopped: !!m.stopped,
        steps: (m.steps || []).slice(0, 30),
        note_ids: m.note_ids || [],
      }));
      localStorage.setItem(chatStoreKey, JSON.stringify({ messages, scope_doc: scopeDoc }));
    } catch { /* storage full — non-fatal */ }
    // The transcript is part of the assessment row now — nudge the same
    // debounced autosave the form fields use so the chat + rough draft land
    // in Supabase too, not just this browser's localStorage.
    if (chatMessages.length) {
      dirtyRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => saveToSupabase(), AUTOSAVE_DELAY);
    }
  }, [chatMessages, scopeDoc, chatStreaming, chatStoreKey, saveToSupabase]);

  // Unsent clarifications draft (notes card). The save effect depends on the
  // TEXT only and reads the key from a ref: on an assessment switch the reload
  // effect swaps the ref + state first, so the old draft is never written
  // under the new key.
  const addendumKeyRef = useRef(addendumKey);
  useEffect(() => {
    if (addendumKeyRef.current === addendumKey) return;
    addendumKeyRef.current = addendumKey;
    try { setNoteAddendum(localStorage.getItem(addendumKey) || ""); } catch { setNoteAddendum(""); }
  }, [addendumKey]);
  useEffect(() => {
    try {
      if (noteAddendum) localStorage.setItem(addendumKeyRef.current, noteAddendum);
      else localStorage.removeItem(addendumKeyRef.current);
    } catch { /* storage full — non-fatal */ }
  }, [noteAddendum]);

  // Model / thinking pickers persist across sessions (sc-model / sc-think).
  const handleModelPref = useCallback((v) => {
    setModelPref(v);
    try { localStorage.setItem("sc-model", v); } catch { /* */ }
  }, []);
  const handleThinkPref = useCallback((v) => {
    setThinkPref(v);
    try { localStorage.setItem("sc-think", v); } catch { /* */ }
  }, []);

  // Every scope_doc update syncs into the form so autosave, the assessment
  // row, and the legacy flows stay coherent (same dirty path as field edits).
  // Multi-option docs write EVERY option into scope_notes as labelled blocks
  // (same format quote-brain draftFromScope records) — the top-level doc only
  // mirrors the recommended option, and the row must keep the full menu.
  const applyScopeDoc = useCallback((doc, { fromModel = false } = {}) => {
    if (!doc || typeof doc !== "object") return;
    // A 🔍 pick pins master_product_id on a materials line. The AI is told to
    // preserve pins when it re-emits the doc, but a dropped field must never
    // lose the tech's pick — re-carry pins onto lines whose description text
    // is unchanged.
    doc = carryMaterialPins(scopeDocRef.current, doc);
    // Same net for a labour rate the tech typed on an option or a section — and
    // the reply's own "typed" stamps are dropped, so the model can never mark a
    // rate as the tech's. Model replies only: on a hand edit this would put
    // back the rate the tech has just cleared or retyped.
    if (fromModel) doc = carryTypedRates(scopeDocRef.current, doc);
    setScopeDoc(doc);
    const bulletLines = (u) => (Array.isArray(u.scope) ? u.scope : [])
      .map((s) => `- ${String(s ?? "")}`).join("\n");
    const options = Array.isArray(doc.options) ? doc.options : [];
    const scopeText = options.length >= 2
      ? options.map((o, i) => `${String(o.label || `Option ${i + 1}`).toUpperCase()}${o.recommended ? " ★ RECOMMENDED" : ""}\n${bulletLines(o)}`).join("\n\n")
      : bulletLines(doc);
    setForm((prev) => ({
      ...prev,
      scope_notes: scopeText || prev.scope_notes,
      estimated_hours: doc.labour_hours ?? prev.estimated_hours,
      service_type: doc.family || prev.service_type,
      client_name: prev.client_name || doc.client_name || prev.client_name,
    }));
    dirtyRef.current = true;
    setSaveStatus("idle");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => saveToSupabase(), AUTOSAVE_DELAY);
  }, [saveToSupabase]);

  // ── Chat attachments (photos / video / PDF-text manuals) ─────────────────
  // Files the tech drops into the chat so the AI can look at them. They upload
  // to the same private bucket ({assessmentId}/chat/…); videos are sampled into
  // frames (Claude has no native video input). Signed URLs are minted fresh at
  // send time so the API can fetch them.
  const signPath = useCallback(async (path, ttl = 21600) => {
    try {
      const { data, error } = await supabase.storage.from(VIDEO_BUCKET).createSignedUrl(path, ttl);
      if (error || !data) return null;
      return data.signedUrl;
    } catch { return null; }
  }, []);

  const patchAttachment = useCallback((id, patch) => {
    setChatAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }, []);

  // Sample evenly-spaced JPEG frames from a video file and upload them.
  const sampleVideoFrames = useCallback(async (file, groupId) => {
    let objectUrl = null;
    let videoEl = null;
    try {
      objectUrl = URL.createObjectURL(file);
      videoEl = document.createElement("video");
      videoEl.src = objectUrl;
      videoEl.preload = "auto";
      videoEl.muted = true;
      videoEl.playsInline = true;
      await new Promise((resolve, reject) => {
        videoEl.onloadedmetadata = () => resolve();
        videoEl.onerror = () => reject(new Error("Couldn't read the video file"));
        setTimeout(resolve, 8000);
      });
      const duration = Number.isFinite(videoEl.duration) ? videoEl.duration : 0;
      const frames = [];
      for (let i = 0; i < CHAT_VIDEO_FRAMES; i++) {
        const t = duration > 0 ? (duration * (i + 0.5)) / CHAT_VIDEO_FRAMES : i * 2;
        let frame;
        try { frame = await extractFrameAt(videoEl, t); } catch { continue; }
        const fpath = `${assessmentId}/chat/${groupId}/frame-${i}.jpg`;
        try {
          const { error } = await supabase.storage.from(VIDEO_BUCKET).upload(fpath, frame.blob, { contentType: "image/jpeg", upsert: true });
          if (!error) frames.push({ path: fpath });
        } catch { /* skip this frame */ }
      }
      return frames;
    } finally {
      if (objectUrl) { try { URL.revokeObjectURL(objectUrl); } catch { /* */ } }
    }
  }, [assessmentId]);

  const handleAddChatFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setAttachBusy(true);
    for (const file of files) {
      const id = makeVideoId();
      const kind = attachKindFor(file);
      if (kind === "unsupported") {
        setChatAttachments((prev) => [...prev, {
          id, kind, name: file.name || "file", status: "error",
          error: "Can't read this type — try a photo, video, PDF, or text file.",
        }]);
        continue;
      }
      const ext = (file.name && file.name.includes(".") ? file.name.split(".").pop() : (kind === "image" ? "jpg" : kind === "document" ? "pdf" : kind === "video" ? "mp4" : "txt")).toLowerCase();
      setChatAttachments((prev) => [...prev, {
        id, kind, name: file.name || `${kind}.${ext}`, mime: file.type || "",
        size: file.size || 0, status: "uploading",
      }]);
      try {
        const path = `${assessmentId}/chat/${id}.${ext}`;
        if (kind === "text") {
          const text = (await file.text()).slice(0, 16000);
          await supabase.storage.from(VIDEO_BUCKET).upload(path, file, { contentType: file.type || "text/plain", upsert: true });
          patchAttachment(id, { path, text, status: "ready" });
        } else if (kind === "video") {
          try { await supabase.storage.from(VIDEO_BUCKET).upload(path, file, { contentType: file.type || "video/mp4", upsert: true }); } catch { /* the frames are what matter */ }
          const frames = await sampleVideoFrames(file, id);
          if (frames.length) patchAttachment(id, { path, frames, status: "ready" });
          else patchAttachment(id, { status: "error", error: "Couldn't read frames from this video." });
        } else {
          // image | document
          const { error } = await supabase.storage.from(VIDEO_BUCKET)
            .upload(path, file, { contentType: file.type || (kind === "image" ? "image/jpeg" : "application/pdf"), upsert: true });
          if (error) throw error;
          patchAttachment(id, { path, status: "ready" });
        }
      } catch (err) {
        console.warn("[chat-attach] upload failed:", err?.message);
        patchAttachment(id, { status: "error", error: err?.message || "Upload failed" });
      }
    }
    setAttachBusy(false);
  }, [assessmentId, patchAttachment, sampleVideoFrames]);

  const handleRemoveChatAttachment = useCallback((id) => {
    setChatAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  // Convert stored attachments (paths) into wire form with freshly-signed URLs.
  // Videos expand into their sampled frames as individual image blocks.
  const attachmentsToWire = useCallback(async (list) => {
    const out = [];
    for (const a of (list || [])) {
      if (a.kind === "image" && a.path) {
        const url = await signPath(a.path);
        if (url) out.push({ kind: "image", url, name: a.name, mime: a.mime });
      } else if (a.kind === "document" && a.path) {
        const url = await signPath(a.path);
        if (url) out.push({ kind: "document", url, name: a.name, mime: a.mime });
      } else if (a.kind === "text" && a.text) {
        out.push({ kind: "text", name: a.name, text: a.text });
      } else if (a.kind === "video" && Array.isArray(a.frames)) {
        for (let i = 0; i < a.frames.length; i++) {
          const url = await signPath(a.frames[i].path);
          if (url) out.push({ kind: "image", url, name: `${a.name || "video"} — frame ${i + 1}/${a.frames.length}` });
        }
      }
    }
    return out;
  }, [signPath]);

  // ⏹ Stop — abort the stream. Partial text + last known doc are kept, so
  // the tech can type a correction as the next message.
  const handleChatStop = useCallback(() => {
    if (chatAbortRef.current) chatAbortRef.current.abort();
  }, []);

  // `meta.noteIds` — Jobber note ids shipped verbatim in this message (from the
  // notes card's Send); kept on the user turn so a later Send skips them.
  const handleChatSend = useCallback(async (textArg, meta) => {
    const text = String(typeof textArg === "string" ? textArg : chatInput).trim();
    const staged = (chatAttachmentsRef.current || []).filter((a) => a.status === "ready").map(cleanAttachment);
    if ((!text && !staged.length) || chatStreaming) return;
    // Clear the box only when sending ITS contents — the notes card's Send
    // passes its own text and must not wipe a draft the tech typed in here.
    if (typeof textArg !== "string") setChatInput("");
    // Drop the attachments we're sending (and any errored ones); keep uploads
    // still in flight so a mid-upload Send doesn't lose them.
    setChatAttachments((prev) => prev.filter((a) => a.status === "uploading"));
    setChatStreaming(true);

    // Full history, role/content/attachments only — UI-only fields stripped.
    // Attachments are signed to fresh URLs so the API can fetch them.
    const priorTurns = chatMessagesRef.current
      .filter((m) => (m.content || "").trim() || (m.attachments && m.attachments.length));
    const history = [];
    for (const m of priorTurns) {
      history.push({ role: m.role, content: m.content, attachments: await attachmentsToWire(m.attachments) });
    }
    history.push({ role: "user", content: text, attachments: await attachmentsToWire(staged) });

    setChatMessages((prev) => [
      ...prev,
      { role: "user", content: text, attachments: staged, note_ids: Array.isArray(meta?.noteIds) ? meta.noteIds.map(String) : [] },
      { role: "assistant", content: "", thinking: "", streaming: true, status: null, steps: [] },
    ]);

    const patchLast = (patch) => {
      setChatMessages((prev) => {
        if (!prev.length || prev[prev.length - 1].role !== "assistant") return prev;
        const next = prev.slice();
        next[next.length - 1] = { ...next[next.length - 1], ...patch };
        return next;
      });
    };

    const controller = new AbortController();
    chatAbortRef.current = controller;

    let rawText = "";
    let thinkingText = "";
    let gotDone = false;
    // Server-side error events. These used to throw and kill the stream, but
    // the server keeps going after an error and always sends a final "done" —
    // throwing lost the doc and the honest wrap-up. Collect and show instead.
    let streamErrors = [];
    // The tail of every reply carries a fenced ```scope-json block — never render it.
    const displayed = () => rawText.split("```scope-json")[0];

    const finalize = (reply, doc) => {
      gotDone = true;
      const base = String(reply || "").split("```scope-json")[0].trim() || displayed().trim();
      const clean = [base, ...streamErrors.map((e) => "⚠ " + e)].filter(Boolean).join("\n\n");
      patchLast({ content: clean, streaming: false, status: null });
      if (doc) applyScopeDoc(doc, { fromModel: true });
    };

    // Memory chips are driven ONLY by tool_result events off the server — never
    // by what the model wrote. The whole bug was the model saying "noted as a
    // standing rule" with nothing saved; if the chip could be produced by prose,
    // it would inherit exactly that lie.
    let memChips = [];
    // The activity trail — every tool step this turn, kept on the message so
    // the tech can watch the work live and reopen it after (⚙ N steps).
    let steps = [];

    const handleEvent = (ev) => {
      if (!ev || typeof ev !== "object") return;
      if (ev.type === "thinking") {
        thinkingText += ev.text || "";
        patchLast({ thinking: thinkingText });
      } else if (ev.type === "text") {
        rawText += ev.text || "";
        // The reply always ENDS with the (hidden) scope-json block — without
        // this status the chat looks frozen while a big doc streams out.
        patchLast(rawText.includes("```scope-json") && !gotDone
          ? { content: displayed(), status: "📝 updating the scope document…" }
          : { content: displayed() });
      } else if (ev.type === "tool") {
        // pricebook_search must be checked before the generic /search/ web
        // fallback or the book lookup mislabels as a web search.
        const label = MEMORY_TOOL_STATUS[ev.name]
          || (ev.name === "pricebook_search" ? "📖 checking the price book…"
            : ev.name === "web_fetch" ? "🌐 reading a page…"
            : ev.name === "count_devices_on_plan" ? "📐 counting devices on the print…"
            : ev.name === "view_job_photos" ? "🖼 looking at the job photos…"
            : ev.name === "recover_scope_doc" ? "📝 reply was cut off — regenerating the scope document…"
            : /search/i.test(ev.name || "") ? "🔎 searching the web…" : `🔧 ${ev.name || "working"}…`);
        steps = startStep(steps, ev.name, label);
        patchLast({ status: label, steps });
      } else if (ev.type === "tool_call") {
        steps = argStep(steps, ev.name, ev.arg);
        patchLast({ steps });
      } else if (ev.type === "tool_result") {
        if (MEMORY_TOOL_STATUS[ev.name]) {
          const chip = memoryChipFor(ev);
          if (chip) { memChips = [...memChips, chip]; patchLast({ memory: memChips }); }
        }
        steps = finishStep(steps, ev.name, ev.ok !== false, stepNote(ev.name, ev.detail, ev.ok !== false));
        patchLast({ status: null, steps });
      } else if (ev.type === "done") {
        finalize(ev.reply, ev.scope_doc);
      } else if (ev.type === "error") {
        // Don't throw — the server continues past errors (overload mid-turn,
        // hop caps) and still sends "done" with whatever it salvaged. Killing
        // the stream here used to discard that doc and honest wrap-up.
        streamErrors.push(String(ev.error || "Scope chat failed"));
        patchLast({ status: null });
      }
      // "meta" and "stop" need no UI handling.
    };

    try {
      // apiFetch (not fetch) — the server decides whether to offer the memory
      // tools by validating this Bearer token against Supabase. A bare fetch
      // sends no token, so the chat would read as anonymous and silently lose
      // the ability to remember anything.
      const res = await apiFetch("/api/scope-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: history,
          scope_doc: scopeDocRef.current || null,
          // Joinability: the server stamps this onto the scope_chat event so
          // chat effort can be tied to the assessment and downstream quote.
          assessment_id: assessmentId || null,
          family: formRef.current.service_type || "other",
          // Every linked note; once the tech has ticked any, each carries
          // selected:true/false so the server grounds on the ticked ones only
          // and states how many were left out. No ticks → plain list (legacy).
          jobber_notes: flagSelectedNotes(jobberNotesRef.current || [], formRef.current.jobber_note_ids),
          // Archived note photos: ids + captions only — the server signs fresh
          // URLs when (and only when) the AI decides to look at them.
          // …minus the photos that belong to UNTICKED notes (see filterMediaBySelection).
          jobber_media: filterMediaBySelection(jobberMediaRef.current || [], jobberNotesRef.current || [], formRef.current.jobber_note_ids).filter((m) => m.isImage).map((m) => ({
            id: m.id,
            name: m.fileName || null,
            note: m.noteMessage || null,
            author: m.noteAuthor || null,
            label: m.jobNumber ? `Job #${m.jobNumber}` : "Request",
            takenAt: m.takenAt || null,
          })),
          model_pref: modelPref,
          thinking: thinkPref,
          stream: true,
        }),
        signal: controller.signal,
      });

      const ctype = (res.headers.get("content-type") || "").toLowerCase();

      // Fallback: server answered plain JSON (streaming unsupported).
      if (ctype.includes("application/json")) {
        const j = await res.json();
        if (!res.ok || j.ok === false) throw new Error(j.error || `HTTP ${res.status}`);
        finalize(j.reply, j.scope_doc);
        return;
      }
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      // SSE: events are "data: {json}" lines separated by blank lines.
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf = (buf + decoder.decode(value, { stream: true })).replace(/\r\n/g, "\n");
        let sep;
        while ((sep = buf.indexOf("\n\n")) !== -1) {
          const rawEvent = buf.slice(0, sep);
          buf = buf.slice(sep + 2);
          for (const line of rawEvent.split("\n")) {
            if (!line.startsWith("data:")) continue;
            const payload = line.slice(5).trim();
            if (!payload) continue;
            let ev = null;
            try { ev = JSON.parse(payload); } catch { continue; }
            handleEvent(ev);
          }
        }
      }
      if (!gotDone) {
        // Stream closed without a done event — the server died mid-reply
        // (Vercel duration kill) or the connection dropped. This is exactly
        // how the Whipple turns "trailed off": the old code kept the partial
        // text and said nothing, so the tech had no idea the doc never came.
        // Salvage a complete doc if one made it through, and say plainly what
        // happened either way.
        let salvaged = false;
        const parts = rawText.split("```scope-json");
        for (let i = parts.length - 1; i >= 1 && !salvaged; i--) {
          const end = parts[i].indexOf("```");
          const raw = (end >= 0 ? parts[i].slice(0, end) : parts[i]).trim();
          if (!raw) continue;
          try { applyScopeDoc(JSON.parse(raw), { fromModel: true }); salvaged = true; } catch { /* partial JSON */ }
        }
        const note = salvaged
          ? "⚠ The connection dropped mid-reply, but the scope document made it through — the document panel is up to date."
          : "⚠ The connection dropped before the scope document arrived — the document panel was NOT updated. Say \"re-emit the doc\" to regenerate it.";
        patchLast({
          content: [displayed().trim(), ...streamErrors.map((e) => "⚠ " + e), note].filter(Boolean).join("\n\n"),
          streaming: false, status: null,
        });
      }
    } catch (err) {
      if (err && err.name === "AbortError") {
        // Tech hit ⏹ Stop — keep partial text, keep the last known doc.
        patchLast({
          content: (displayed().trim() + " …(stopped)").trim(),
          streaming: false, status: null, stopped: true,
        });
      } else {
        const partial = displayed().trim();
        patchLast({
          content: (partial ? partial + "\n" : "") + "⚠ " + (err?.message || "Scope chat failed"),
          streaming: false, status: null,
        });
      }
    } finally {
      chatAbortRef.current = null;
      setChatStreaming(false);
    }
  }, [chatInput, chatStreaming, modelPref, thinkPref, applyScopeDoc, attachmentsToWire]);

  // First-run chip — sends the typed scope (+ tech notes) as the first message,
  // framed so the bubble shows EXACTLY what the AI was given, in full, and the
  // AI must confirm its sources and flag anything it drops. The chip used to
  // fire scope_notes bare: when that field held a stomped summary or bare doc
  // title, the tech had no way to see the AI was scoping off a fragment.
  const handleUseTypedScope = useCallback(() => {
    const scope = (formRef.current.scope_notes || "").trim();
    const tech = (formRef.current.tech_notes || "").trim();
    if (!scope && !tech) return;
    const wc = (s) => (s ? s.split(/\s+/).filter(Boolean).length : 0);
    const parts = [
      "Build the scope document from my complete typed site-assessment notes below — use EVERY detail, along with the linked Jobber notes and photos. Start your reply by confirming what you're working from, and if anything below does NOT make it into the scope doc, call it out explicitly at the end.",
    ];
    if (scope) parts.push(`—— MY TYPED SCOPE NOTES (${wc(scope)} words, complete) ——\n${scope}`);
    if (tech) parts.push(`—— TECH NOTES — on-site facts (${wc(tech)} words, complete) ——\n${tech}`);
    handleChatSend(parts.join("\n\n"));
  }, [handleChatSend]);

  // ── Jobber Notes card → Scope Chat ───────────────────────────────────────
  // Tick / untick one linked note. Any tick puts the card in selection mode:
  // only ticked notes ground the chat (every turn) and quote-brain. Unticking
  // the last one leaves [] — an explicit "NONE of these describe the job" —
  // never null (null = never chose, which means every note rides as context).
  const handleToggleNote = useCallback((id) => {
    if (!id) return;
    const key = String(id);
    const cur = Array.isArray(formRef.current.jobber_note_ids) ? formRef.current.jobber_note_ids.map(String) : [];
    updateField("jobber_note_ids", cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key]);
  }, [updateField]);
  const handleSelectAllNotes = useCallback((on) => {
    updateField("jobber_note_ids", on ? (jobberNotesRef.current || []).map((n) => n.id).filter(Boolean).map(String) : []);
  }, [updateField]);

  // Send the TICKED notes verbatim (oldest → newest, line breaks kept) plus
  // the tech's clarifications as ONE chat message. In the message the notes
  // ARE the scope — nothing the system-prompt context path could cap or skim —
  // and the reply must confirm what it read and call out anything left out.
  // Clarifications outrank the notes where they disagree (they're newer).
  // Notes already shipped in an earlier turn (user-turn note_ids) are NOT
  // resent: ticks are cumulative, Send ships only what's new — so a long thread
  // goes up in batches and a later tick just ADDS to the doc.
  const handleUseJobberNotes = useCallback(() => {
    const f = formRef.current;
    const ids = Array.isArray(f.jobber_note_ids) ? f.jobber_note_ids.map(String) : null;
    const sel = new Set(ids || []);
    const all = jobberNotesRef.current || [];
    const sent = sentNoteIdsOf(chatMessagesRef.current);
    const ticked = all.filter((n) => n.id && sel.has(String(n.id)));
    const picked = ticked
      .filter((n) => !sent.has(String(n.id)))
      .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    const already = ticked.length - picked.length;
    const firstTurn = chatMessagesRef.current.length === 0;
    const explicitNone = ids !== null && ids.length === 0 && all.length > 0;
    const addendum = String(noteAddendum || "").trim();
    // Typed assessment notes ride along only before the chat starts (same rule
    // as the "Use my typed assessment notes" chip — see typedMeta).
    const typed = noteIncludeTyped && firstTurn && !scopeDocRef.current
      ? `${f.scope_notes || ""}\n${f.tech_notes || ""}`.trim()
      : "";
    if (!picked.length && !addendum && !typed) return;
    const wc = (s) => (s ? String(s).split(/\s+/).filter(Boolean).length : 0);
    const when = (iso) => {
      if (!iso) return "";
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
    };
    const from = [
      picked.length ? `the ${picked.length} Jobber note${picked.length === 1 ? "" : "s"} I ticked` : "",
      addendum ? "my clarifications" : "",
      typed ? "my typed assessment notes" : "",
    ].filter(Boolean).join(" + ");
    const lead = firstTurn
      ? `Build the scope document from ${from} below — use EVERY detail.`
      : `Add ${from} below to the scope document — use EVERY detail, keep everything already scoped, and reconcile.`;
    const notesLine = explicitNone
      ? ` Do NOT use any of the ${all.length} linked Jobber notes on this request — none of them describe this job.`
      : picked.length && ticked.length < all.length
        ? ` I ticked ${ticked.length} of the ${all.length} notes on this request/job; the unticked ones are unrelated calls — leave them out.`
        : "";
    const alreadyLine = already > 0 ? ` (${already} other ticked note${already === 1 ? "" : "s"} went up earlier in this chat — they still stand.)` : "";
    const parts = [
      lead + notesLine + alreadyLine +
      (addendum ? " Where my clarifications disagree with a note, my clarifications win." : "") +
      " Start your reply by confirming what you're working from, and if anything below does NOT make it into the scope doc, call it out explicitly at the end.",
    ];
    if (addendum) parts.push(`—— MY CLARIFICATIONS / ADDITIONS (${wc(addendum)} words) ——\n${addendum}`);
    if (typed) parts.push(`—— MY TYPED ASSESSMENT NOTES (${wc(typed)} words, complete) ——\n${typed}`);
    if (picked.length) {
      const body = picked.map((n) => {
        const tag = [n.sourceLabel || (n.source === "job" ? "Job" : "Request"), n.createdBy || "team", when(n.createdAt)].filter(Boolean).join(" · ");
        return `[${tag}]\n${String(n.message || "").trim()}`;
      }).join("\n\n");
      parts.push(`—— SELECTED JOBBER NOTES (${picked.length}, oldest → newest, verbatim, ${wc(body)} words) ——\n${body}`);
    }
    handleChatSend(parts.join("\n\n"), { noteIds: picked.map((n) => String(n.id)) });
    setNoteAddendum("");
    // Bring the chat into view so the tech watches the reply build.
    try { document.querySelector(".sa-chat-card")?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch { /* */ }
  }, [handleChatSend, noteAddendum, noteIncludeTyped]);

  // ⚡ Convert to Quote — deterministic pricing via quote-brain draftFromScope.
  // (The "Start from a template?" chooser that used to front this was retired
  // 2026-09-06 — unused/confusing. quote-brain still accepts template_id on
  // draftFromScope if the picker ever comes back.)
  const handleConvertScope = useCallback(async () => {
    const doc = scopeDocRef.current;
    if (convertingRef.current || converting || !doc || !Array.isArray(doc.scope) || !doc.scope.length) return;
    if (photoBusy) { setConvertError("Photos are still uploading — give it a second, then hit Convert."); return; }
    convertingRef.current = true;
    setConverting(true);
    setConvertError(null);
    try {
      // Flush pending edits so the assessment row exists and is current.
      if (timerRef.current) clearTimeout(timerRef.current);
      dirtyRef.current = true;
      await saveToSupabase();

      // ── Push Scope Notes + not-yet-pushed photos to the linked Jobber
      // request as ONE note, BEFORE building the quote. Convert is the single
      // push path (the standalone photo button was removed), so notes and pics
      // always land together and can never double-post: photos carry a
      // persisted `pushed_at`, and the notes ride the first push only.
      const f = formRef.current;
      if (f.jobber_request_id) {
        const unpushed = (f.photos || []).filter((p) => p.status === "uploaded" && !p.pushed_at);
        const notesArr = (Array.isArray(doc.site_notes) ? doc.site_notes : [])
          .map((s) => String(s?.text ?? s ?? "").trim())
          .filter(Boolean);
        // If any photo already went up (this session or a prior one) assume the
        // notes did too, so a re-convert never re-posts the same note text.
        const notesAlreadyUp = notePushedRef.current || (f.photos || []).some((p) => p.pushed_at);
        const includeNotes = notesArr.length > 0 && !notesAlreadyUp;
        if (unpushed.length || includeNotes) {
          const head = `Site assessment${f.client_name ? " — " + f.client_name : ""}${f.address ? ", " + f.address : ""}`;
          const parts = [];
          if (includeNotes) parts.push("📋 Scope notes\n" + notesArr.map((n) => "• " + n).join("\n"));
          if (unpushed.length) parts.push(`📷 ${unpushed.length} site photo${unpushed.length === 1 ? "" : "s"} attached`);
          const pr = await apiFetch("/api/jobber", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "pushRequestPhotos",
              requestId: f.jobber_request_id,
              photos: unpushed.map((p) => ({ path: p.path, name: p.name })),
              message: head + "\n\n" + parts.join("\n\n"),
            }),
          });
          const pj = await pr.json().catch(() => ({}));
          if (!pr.ok || pj.ok === false) {
            throw new Error(`Couldn't push notes/photos to Jobber: ${pj.error || pj.details || pr.statusText}. Nothing was created — tap Convert to retry.`);
          }
          // Mark the pushed photos and persist immediately — we're about to
          // navigate away, so don't rely on the debounced autosave firing.
          const nowIso = new Date().toISOString();
          const ids = new Set(unpushed.map((p) => p.id));
          if (ids.size) {
            const nextPhotos = (f.photos || []).map((p) => (ids.has(p.id) ? { ...p, pushed_at: nowIso } : p));
            setForm((prev) => ({ ...prev, photos: (prev.photos || []).map((p) => (ids.has(p.id) ? { ...p, pushed_at: nowIso } : p)) }));
            try { await supabase.from("smart_assessments").update({ photos: nextPhotos, updated_at: nowIso }).eq("id", assessmentId); } catch { /* non-fatal — pushed_at is already in UI state */ }
          }
          if (includeNotes) notePushedRef.current = true;
        }
      }

      const res = await apiFetch("/api/quote-brain", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "draftFromScope", assessment_id: assessmentId || null, scope_doc: doc }),
      });
      const j = await res.json();
      if (!res.ok || j.ok === false || !j.quote_id) throw new Error(j.error || "Convert failed");
      navigate(`/quote/${j.quote_id}`);
    } catch (e) {
      setConvertError(e.message);
      convertingRef.current = false;
      setConverting(false);
    }
  }, [converting, photoBusy, assessmentId, navigate, saveToSupabase]);

  // Inline doc edits — ONE funnel: the mutator produces the next doc and it
  // flows through applyScopeDoc, so form-sync (scope_notes / estimated_hours /
  // service_type), localStorage persistence, and dirty/autosave all behave
  // exactly like chat-produced docs. The server already receives scope_doc
  // each turn, so hand edits ride along with the next message.
  const editScopeDoc = useCallback((mutator) => {
    const cur = scopeDocRef.current;
    if (!cur || typeof mutator !== "function") return;
    const next = mutator(cur);
    if (next && typeof next === "object") applyScopeDoc(next);
  }, [applyScopeDoc]);

  // ── Back button ────────────────────────────────────────────────────────
  const handleBack = useCallback(() => {
    // Save any pending changes before leaving
    if (dirtyRef.current) {
      if (timerRef.current) clearTimeout(timerRef.current);
      saveToSupabase();
    }
    navigate("/");
  }, [navigate, saveToSupabase]);

  // ── Cleanup timer + media recorder on unmount ──────────────────────────
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(t => t.stop());
      }
      // Save any pending changes on unmount
      if (dirtyRef.current) saveToSupabase();
    };
  }, [saveToSupabase]);

  // ── Helper: get best audio MIME type ───────────────────────────────────
  const getAudioMimeType = useCallback(() => {
    if (typeof MediaRecorder === "undefined") return null;
    if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) return "audio/webm;codecs=opus";
    if (MediaRecorder.isTypeSupported("audio/webm")) return "audio/webm";
    if (MediaRecorder.isTypeSupported("audio/mp4")) return "audio/mp4";
    if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) return "audio/ogg;codecs=opus";
    return null;
  }, []);

  // ── Helper: blob to base64 ────────────────────────────────────────────
  const blobToBase64 = useCallback((blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, []);

  // Route a finished voice transcript to the right target: the scope chat
  // input (default) or the legacy scope_notes textarea in the full form.
  const appendTranscript = useCallback((text) => {
    const t = String(text || "").trim();
    if (!t) return;
    if (voiceTargetRef.current === "scope") {
      setForm((prev) => {
        const sep = prev.scope_notes && !prev.scope_notes.endsWith(" ") ? " " : "";
        return { ...prev, scope_notes: prev.scope_notes + sep + t + " " };
      });
      dirtyRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => saveToSupabase(), AUTOSAVE_DELAY);
    } else if (voiceTargetRef.current === "notes") {
      // Dictating clarifications under the Jobber notes.
      setNoteAddendum((prev) => {
        const sep = prev && !prev.endsWith(" ") ? " " : "";
        return prev + sep + t + " ";
      });
    } else {
      setChatInput((prev) => {
        const sep = prev && !prev.endsWith(" ") ? " " : "";
        return prev + sep + t + " ";
      });
    }
  }, [saveToSupabase]);

  // ── Voice input (MediaRecorder + Whisper primary, Web Speech fallback) ─
  // `target` = "chat" (scope chat input, default), "scope" (legacy textarea) or
  // "notes" (clarifications under the Jobber notes). The target is fixed when
  // recording STARTS — tapping a different mic to stop still lands the
  // transcript where the tech began dictating.
  const toggleVoiceInput = useCallback((target) => {
    if (!isListening && (target === "chat" || target === "scope" || target === "notes")) voiceTargetRef.current = target;
    const hasMediaRecorder = typeof MediaRecorder !== "undefined";
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    // ── Stop recording ──
    if (isListening) {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      } else if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      return;
    }

    // ── Primary: MediaRecorder + Whisper ──
    if (hasMediaRecorder && whisperAvailable !== false) {
      navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        audioStreamRef.current = stream;
        audioChunksRef.current = [];

        const mimeType = getAudioMimeType();
        const options = mimeType ? { mimeType } : {};
        const recorder = new MediaRecorder(stream, options);
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
        };

        recorder.onstop = async () => {
          stream.getTracks().forEach(t => t.stop());
          audioStreamRef.current = null;
          if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);

          const chunks = audioChunksRef.current;
          if (chunks.length === 0) {
            setIsListening(false);
            return;
          }

          const usedMime = recorder.mimeType || mimeType || "audio/webm";
          const audioBlob = new Blob(chunks, { type: usedMime });

          setIsTranscribing(true);
          try {
            const audioBase64 = await blobToBase64(audioBlob);
            const res = await apiFetch("/api/claude", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                action: "transcribeAudio",
                audioBase64,
                mimeType: usedMime,
              }),
            });
            const data = await res.json();

            if (data.whisperAvailable === false) {
              setWhisperAvailable(false);
              console.warn("Whisper unavailable:", data.error);
            } else if (data.transcript && data.transcript.trim()) {
              setWhisperAvailable(true);
              appendTranscript(data.transcript);
            }
          } catch (err) {
            console.error("Transcription error:", err);
          } finally {
            setIsTranscribing(false);
            setIsListening(false);
          }
        };

        recorder.start(1000);
        setIsListening(true);

        // 5-min limit
        let seconds = 0;
        recordingTimerRef.current = setInterval(() => {
          seconds++;
          if (seconds >= 300) {
            recorder.stop();
            clearInterval(recordingTimerRef.current);
          }
        }, 1000);
      }).catch((err) => {
        if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
          alert("Microphone access denied. Please allow mic access in your browser settings.");
        } else {
          alert("Could not access microphone. Please check your device settings.");
        }
      });
      return;
    }

    // ── Fallback: Web Speech API ──
    if (!SpeechRecognition) {
      alert("Voice input is not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-CA";
    recognitionRef.current = recognition;

    let finalTranscript = "";

    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript + " ";
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      if (finalTranscript) {
        appendTranscript(finalTranscript);
        finalTranscript = "";
      }
    };

    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognition.start();
    setIsListening(true);
  }, [isListening, whisperAvailable, appendTranscript, getAudioMimeType, blobToBase64]);

  // ── Site photos ─────────────────────────────────────────────────────────
  // Photos live in the same private Storage bucket as walkthrough videos
  // ({assessmentId}/photos/…) with a lightweight index in the row's `photos`
  // jsonb column — mutations ride the normal dirty/autosave path.
  // (photoBusy state is declared up top so Convert to Quote can gate on it.)

  const updatePhotos = useCallback((updater) => {
    setForm((prev) => ({
      ...prev,
      photos: typeof updater === "function" ? updater(prev.photos || []) : updater,
    }));
    dirtyRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => saveToSupabase(), AUTOSAVE_DELAY);
  }, [saveToSupabase]);

  const handleAddPhotos = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f && f.type && f.type.startsWith("image/"));
    if (!files.length) return;
    setPhotoBusy(true);
    for (const file of files) {
      const id = makeVideoId();
      const ext = (file.name && file.name.includes(".") ? file.name.split(".").pop() : "jpg").toLowerCase();
      const path = `${assessmentId}/photos/${id}.${ext}`;
      const entry = {
        id, path,
        name: file.name || `photo.${ext}`,
        size: file.size || 0,
        status: "uploading",
        created_at: new Date().toISOString(),
        pushed_at: null,
      };
      updatePhotos((prev) => [...prev, entry]);
      try {
        const { error } = await supabase.storage
          .from(VIDEO_BUCKET)
          .upload(path, file, { contentType: file.type || "image/jpeg", upsert: true });
        if (error) throw error;
        updatePhotos((prev) => prev.map((p) => (p.id === id ? { ...p, status: "uploaded" } : p)));
      } catch (err) {
        console.warn("[photo] upload failed:", err?.message);
        updatePhotos((prev) => prev.map((p) => (p.id === id ? { ...p, status: "error" } : p)));
      }
    }
    setPhotoBusy(false);
  }, [assessmentId, updatePhotos]);

  const handleRemovePhoto = useCallback(async (photo) => {
    if (!window.confirm("Remove this photo?")) return;
    try { await supabase.storage.from(VIDEO_BUCKET).remove([photo.path]); } catch { /* ignore */ }
    updatePhotos((prev) => prev.filter((p) => p.id !== photo.id));
  }, [updatePhotos]);

  // NOTE: photos (and Scope Notes) reach the Jobber request via Convert to Quote
  // — see handleConvertScope. The old standalone "Push photos" button was removed
  // so the two can't double-post; a single note carries notes + pics together.

  // ── Site-walkthrough video ─────────────────────────────────────────────
  // Video files are far too large for the /api JSON body limit, so they upload
  // straight from the browser to Supabase Storage. The `videos` column on the
  // assessment is just a lightweight index; the heavy file lives in Storage.
  // Offline or on a failed upload, the raw blob is stashed in IndexedDB and
  // re-tried whenever the browser comes back online.

  // Keep a ref in sync so async upload/drain callbacks read the latest list.
  useEffect(() => { videosRef.current = videos; }, [videos]);

  // Persist the videos index. Wrapped defensively: if the `videos` column
  // hasn't been migrated yet this just warns — it never breaks normal saves.
  const saveVideosToDb = useCallback(async (list) => {
    try {
      const { error } = await supabase
        .from("smart_assessments")
        .update({ videos: list })
        .eq("id", assessmentId);
      if (error) throw error;
    } catch (err) {
      console.warn("[video] persist failed — run the 20260521_assessment_videos migration?", err?.message);
    }
  }, [assessmentId]);

  // Update local state + DB together.
  const updateVideos = useCallback((updater) => {
    setVideos((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      saveVideosToDb(next);
      return next;
    });
  }, [saveVideosToDb]);

  // Upload a single blob to Storage. Returns true on success.
  const uploadToStorage = useCallback(async (path, blob, mime) => {
    const { error } = await supabase.storage
      .from(VIDEO_BUCKET)
      .upload(path, blob, { contentType: mime, upsert: true });
    if (error) throw error;
    return true;
  }, []);

  // Add a video (from native camera capture or file picker).
  const handleAddVideo = useCallback(async (file) => {
    if (!file) return;
    const id = makeVideoId();
    const mime = file.type || "video/mp4";
    const ext = videoExtFromMime(mime, file.name);
    const path = `${assessmentId}/${id}.${ext}`;
    const duration = await getVideoDuration(file);
    const online = typeof navigator === "undefined" || navigator.onLine !== false;

    const entry = {
      id, path,
      name: file.name || `recording.${ext}`,
      size: file.size || 0,
      mime, duration,
      status: online ? "uploading" : "queued",
      created_at: new Date().toISOString(),
      created_by: session?.user?.email || null,
      analysis: null,
    };

    videoBlobCacheRef.current[id] = file; // keep the blob so analysis skips a re-download
    updateVideos((prev) => [...prev, entry]);

    // Make sure the assessment row exists so the videos update lands on a row.
    dirtyRef.current = true;
    await saveToSupabase();

    if (!online) {
      try { await queueVideo({ id, assessmentId, video: entry, blob: file }); } catch {}
      return;
    }

    try {
      await uploadToStorage(path, file, mime);
      updateVideos((prev) => prev.map((v) => (v.id === id ? { ...v, status: "uploaded" } : v)));
    } catch (err) {
      console.warn("[video] upload failed, queueing for retry:", err?.message);
      try { await queueVideo({ id, assessmentId, video: { ...entry, status: "queued" }, blob: file }); } catch {}
      updateVideos((prev) => prev.map((v) => (v.id === id ? { ...v, status: "queued" } : v)));
    }
  }, [assessmentId, session, saveToSupabase, updateVideos, uploadToStorage]);

  // Retry any queued (offline) uploads. Safe to call repeatedly.
  const drainQueue = useCallback(async () => {
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    let queued = [];
    try { queued = await listQueued(assessmentId); } catch { return; }
    if (!queued.length) return;
    const done = [];
    for (const rec of queued) {
      try {
        await uploadToStorage(rec.video.path, rec.blob, rec.video.mime);
        await removeQueued(rec.id);
        done.push(rec.id);
      } catch { /* leave queued; retry on next online event */ }
    }
    if (done.length) {
      updateVideos((prev) => prev.map((v) => (done.includes(v.id) ? { ...v, status: "uploaded" } : v)));
    }
  }, [assessmentId, uploadToStorage, updateVideos]);

  // Drain on mount + whenever the browser regains connectivity.
  useEffect(() => {
    if (paramId === "new") return;
    drainQueue();
    const onOnline = () => drainQueue();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [paramId, drainQueue]);

  // Play a saved video via a short-lived signed URL (bucket is private).
  const handlePlayVideo = useCallback(async (video) => {
    if (video.status !== "uploaded") {
      alert(video.status === "queued"
        ? "This video hasn't uploaded yet — it'll upload automatically when you're back online."
        : "This video is still uploading.");
      return;
    }
    setPlayLoading(true);
    try {
      const { data, error } = await supabase.storage
        .from(VIDEO_BUCKET)
        .createSignedUrl(video.path, 3600);
      if (error) throw error;
      setPlayingUrl(data.signedUrl);
    } catch (err) {
      alert("Could not load video: " + (err?.message || "unknown error"));
    } finally {
      setPlayLoading(false);
    }
  }, []);

  // Remove a video (Storage object + queued blob + index entry).
  const handleRemoveVideo = useCallback(async (video) => {
    if (!window.confirm("Remove this video? This can't be undone.")) return;
    try { await supabase.storage.from(VIDEO_BUCKET).remove([video.path]); } catch { /* ignore */ }
    try { await removeQueued(video.id); } catch { /* ignore */ }
    delete videoBlobCacheRef.current[video.id];
    updateVideos((prev) => prev.filter((v) => v.id !== video.id));
  }, [updateVideos]);

  // ── Video AI analysis (Phase 2) ────────────────────────────────────────
  const setVideoStatus = useCallback((id, status, error) => {
    setAnalysisState((prev) => ({ ...prev, [id]: { status, error: error || null } }));
  }, []);

  const getVideoBlob = useCallback(async (video) => {
    const cached = videoBlobCacheRef.current[video.id];
    if (cached) return cached;
    const { data, error } = await supabase.storage.from(VIDEO_BUCKET).download(video.path);
    if (error || !data) throw new Error("Couldn't download the video from storage");
    videoBlobCacheRef.current[video.id] = data;
    return data;
  }, []);

  // Orchestrates: blob → audio → transcript → sections → frames → captions.
  // Every external step is best-effort; if narration can't be extracted we fall
  // back to evenly-spaced screenshots so you always get a captioned photo set.
  const handleAnalyzeVideo = useCallback(async (video) => {
    if (video.status !== "uploaded") { alert("This video needs to finish uploading before it can be analyzed."); return; }
    const id = video.id;
    const AUDIO_SPEED = 3, MAX_FRAMES = 24, CAPTION_BATCH = 8;
    let objectUrl = null;
    let videoEl = null;
    try {
      setVideoStatus(id, "downloading");
      const blob = await getVideoBlob(video);
      objectUrl = URL.createObjectURL(blob);
      videoEl = document.createElement("video");
      videoEl.src = objectUrl;
      videoEl.preload = "auto";
      videoEl.playsInline = true;
      await new Promise((resolve, reject) => {
        videoEl.onloadedmetadata = () => resolve();
        videoEl.onerror = () => reject(new Error("Couldn't read the video file"));
        setTimeout(resolve, 8000);
      });
      const duration = Number.isFinite(videoEl.duration) ? videoEl.duration : (video.duration || 0);

      // 1. Transcribe narration (best-effort)
      setVideoStatus(id, "transcribing");
      let segments = [];
      try {
        const audioBlob = await extractAudioFromVideo(videoEl, AUDIO_SPEED);
        if (audioBlob && audioBlob.size > 0) {
          const audioBase64 = await blobToB64(audioBlob);
          const r = await apiFetch("/api/claude", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "transcribeAudioTimestamps", audioBase64, mimeType: audioBlob.type }),
          });
          const j = await r.json();
          segments = (j.segments || []).map((s) => ({ start: (s.start || 0) * AUDIO_SPEED, end: (s.end || 0) * AUDIO_SPEED, text: s.text }));
        }
      } catch (e) { console.warn("[video] transcription failed, continuing without narration:", e?.message); }

      // 2. Section the walkthrough (smart if we have narration, else intervals)
      setVideoStatus(id, "sectioning");
      let sections = [];
      if (segments.length) {
        const r = await apiFetch("/api/claude", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "analyzeVideoSections", transcript: segments, scope_notes: formRef.current.scope_notes || "", duration }),
        });
        const j = await r.json();
        sections = Array.isArray(j.sections) ? j.sections : [];
      }
      if (!sections.length) {
        const step = duration > 0 ? Math.max(20, duration / 8) : 30;
        const total = duration || step * 4;
        for (let t = 0; t < total; t += step) {
          sections.push({ title: `Clip @ ${formatDuration(t)}`, start: t, end: Math.min(t + step, total), summary: "", keywords: [], capture_times: [Math.min(t + step / 2, total - 0.1)] });
        }
      }

      // 3. Extract + upload frames at each stop's capture_times
      setVideoStatus(id, "capturing");
      const captionQueue = [];
      let frameCount = 0;
      for (const sec of sections) {
        sec.frames = [];
        let times = (Array.isArray(sec.capture_times) && sec.capture_times.length)
          ? sec.capture_times
          : [(Number(sec.start) + Number(sec.end)) / 2];
        times = times.slice(0, 2);
        for (let t of times) {
          if (frameCount >= MAX_FRAMES) break;
          t = Math.max(0, Math.min(Number(t) || 0, (duration || 1) - 0.05));
          let frame;
          try { frame = await extractFrameAt(videoEl, t); } catch { continue; }
          const fid = makeVideoId();
          const fpath = `${assessmentId}/frames/${id}/${fid}.jpg`;
          try {
            await supabase.storage.from(VIDEO_BUCKET).upload(fpath, frame.blob, { contentType: "image/jpeg", upsert: true });
          } catch (e) { console.warn("[video] frame upload failed:", e?.message); continue; }
          const frameRef = { id: fid, path: fpath, caption: "", t };
          sec.frames.push(frameRef);
          captionQueue.push({ id: fid, base64: frame.base64, frameRef });
          frameCount++;
        }
      }

      // 4. Caption frames with Claude vision (batched)
      setVideoStatus(id, "captioning");
      const ctxText = (formRef.current.scope_notes || "").slice(0, 600);
      for (let i = 0; i < captionQueue.length; i += CAPTION_BATCH) {
        const batch = captionQueue.slice(i, i + CAPTION_BATCH);
        try {
          const r = await apiFetch("/api/claude", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "captionVideoFrames", frames: batch.map((b) => ({ id: b.id, base64: b.base64, mime: "image/jpeg" })), context: ctxText }),
          });
          const j = await r.json();
          const caps = j.captions || {};
          batch.forEach((b) => { b.frameRef.caption = caps[b.id] || ""; });
        } catch (e) { console.warn("[video] captioning failed:", e?.message); }
      }

      // 5. Persist the analysis onto the video entry
      const analysis = {
        status: "done",
        generated_at: new Date().toISOString(),
        had_transcript: segments.length > 0,
        transcript_text: segments.map((s) => s.text).join(" ").slice(0, 8000),
        sections: sections.map((s) => ({
          title: s.title || "",
          start: Number(s.start) || 0,
          end: Number(s.end) || 0,
          summary: s.summary || "",
          keywords: Array.isArray(s.keywords) ? s.keywords : [],
          frames: (s.frames || []).map((f) => ({ path: f.path, caption: f.caption || "", t: f.t })),
        })).filter((s) => s.frames.length),
      };
      updateVideos((prev) => prev.map((v) => (v.id === id ? { ...v, analysis } : v)));
      setVideoStatus(id, "done");
    } catch (err) {
      console.error("[video] analysis failed:", err);
      setVideoStatus(id, "error", err?.message || "Analysis failed");
    } finally {
      if (videoEl) { try { videoEl.pause(); } catch { /* */ } videoEl.removeAttribute("src"); try { videoEl.load(); } catch { /* */ } }
      if (objectUrl) { try { URL.revokeObjectURL(objectUrl); } catch { /* */ } }
    }
  }, [assessmentId, getVideoBlob, setVideoStatus, updateVideos]);

  // ── Workflow state display ─────────────────────────────────────────────
  const workflowInfo = WORKFLOW_LABELS[form.workflow_state] || WORKFLOW_LABELS.new;

  // ── Loading state ──────────────────────────────────────────────────────
  if (!loaded) {
    return (
      <div style={{ minHeight: "100vh", background: "#0d0f12", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 36, height: 36, border: "3px solid rgba(165,206,58,0.2)", borderTopColor: "#a5ce3a", borderRadius: "50%", animation: "sa-spin 0.8s linear infinite" }} />
        <style>{`@keyframes sa-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <>
      <style>{SMART_ASSESSMENT_CSS}</style>
      <div className="sa-wrap">

        {/* ── Top Bar ──────────────────────────────────────────────── */}
        <div className="sa-topbar">
          <button className="sa-back-btn" onClick={handleBack}>&#8249; Back</button>
          <div className="sa-topbar-center">
            <div className="sa-topbar-title">Smart Assessment</div>
            <div className="sa-topbar-client">{form.client_name || "New Client"}</div>
          </div>
          <button
            onClick={() => navigate("/quotes?view=backends&sub=author", { state: { clientName: form.client_name, jobberRequestId: form.jobber_request_id, assessmentId } })}
            title="Open Backend Builder for this client"
            style={{ background: "rgba(165,206,58,.12)", border: "1px solid rgba(165,206,58,.4)", borderRadius: 8, color: "#a5ce3a", fontWeight: 700, fontSize: 12, padding: "6px 12px", cursor: "pointer", whiteSpace: "nowrap", marginRight: 8 }}
          >
            Backend Builder
          </button>
          <div className="sa-save-indicator">
            {saveStatus === "saving" && <span className="sa-save-status saving">Saving...</span>}
            {saveStatus === "saved" && <span className="sa-save-status saved">Saved &#10003;</span>}
            {saveStatus === "error" && <span className="sa-save-status error">Error</span>}
          </div>
        </div>

        {/* Pipeline flow-through — click straight to the quote / takeoff / job
            this assessment produced. Skipped on /new: nothing exists yet. */}
        {paramId !== "new" && (
          <div className="sa-section">
            <FlowStrip anchor={{ assessment: assessmentId }} />
          </div>
        )}


        {/* ── Job Basics — compact expandable header ─────────────────── */}
        <div className="sa-section">
          <div className="sa-basics">
            <button className="sa-basics-head" onClick={() => setBasicsOpen((o) => !o)} type="button">
              <div className="sa-basics-summary">
                <span className="sa-basics-name">{form.client_name || "No client yet"}</span>
                <span className="sa-basics-sub">
                  {[
                    SERVICE_TYPES.find((s) => s.key === form.service_type)?.label,
                    form.address,
                  ].filter(Boolean).join(" · ") || "Tap to add job basics"}
                </span>
              </div>
              <div className="sa-workflow-badge" style={{ background: workflowInfo.color + "20", color: workflowInfo.color, borderColor: workflowInfo.color }}>
                {workflowInfo.label}
              </div>
              <span className={`sa-chevron${basicsOpen ? " open" : ""}`}>&#9660;</span>
            </button>
            {basicsOpen && (
              <div className="sa-basics-body">
                <div className="sa-field">
                  <label className="sa-label">Service Type</label>
                  <select
                    className="sa-select"
                    value={form.service_type}
                    onChange={(e) => updateField("service_type", e.target.value)}
                  >
                    <option value="">Select service type...</option>
                    {SERVICE_TYPES.map((st) => (
                      <option key={st.key} value={st.key}>{st.label}</option>
                    ))}
                  </select>
                </div>
                <div className="sa-field-row">
                  <div className="sa-field">
                    <label className="sa-label">Client Name</label>
                    <input
                      className="sa-input"
                      placeholder="Client name"
                      value={form.client_name}
                      onChange={(e) => updateField("client_name", e.target.value)}
                    />
                  </div>
                  <div className="sa-field">
                    <label className="sa-label">Phone</label>
                    <input
                      className="sa-input"
                      type="tel"
                      placeholder="250-555-0000"
                      value={form.phone}
                      onChange={(e) => updateField("phone", e.target.value)}
                    />
                  </div>
                </div>
                <div className="sa-field">
                  <label className="sa-label">Email</label>
                  <input
                    className="sa-input"
                    type="email"
                    placeholder="client@email.com"
                    value={form.email}
                    onChange={(e) => updateField("email", e.target.value)}
                  />
                </div>
                <div className="sa-field" style={{ marginBottom: 0 }}>
                  <label className="sa-label">Address</label>
                  <input
                    className="sa-input"
                    placeholder="123 Main St, Salmon Arm BC"
                    value={form.address}
                    onChange={(e) => updateField("address", e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Jobber Notes — team-typed context pulled from the request/job ── */}
        {form.jobber_request_id && (
          <div className="sa-section">
            <JobberNotesCard
              notes={jobberNotes}
              media={jobberMedia}
              mediaNote={jobberMediaNote}
              loading={jobberNotesLoading}
              open={jobberNotesOpen}
              onToggle={() => setJobberNotesOpen((o) => !o)}
              selectedIds={selectedNoteIds}
              selection={form.jobber_note_ids}
              sentNoteIds={sentNoteIds}
              typedChars={typedMeta.chars}
              onToggleNote={handleToggleNote}
              onSelectAll={handleSelectAllNotes}
              addendum={noteAddendum}
              onAddendumChange={setNoteAddendum}
              onSend={handleUseJobberNotes}
              streaming={chatStreaming}
              onMic={() => toggleVoiceInput("notes")}
              isListening={isListening}
              isTranscribing={isTranscribing}
              showIncludeTyped={typedMeta.show}
              includeTyped={noteIncludeTyped}
              onIncludeTypedChange={setNoteIncludeTyped}
              typedWords={typedMeta.words}
            />
          </div>
        )}

        {/* ── 1. Scope Chat — the primary input ──────────────────────── */}
        <div className="sa-section">
          <ScopeChatCard
            messages={chatMessages}
            input={chatInput}
            onInputChange={setChatInput}
            onSend={handleChatSend}
            onStop={handleChatStop}
            streaming={chatStreaming}
            isListening={isListening}
            isTranscribing={isTranscribing}
            onMic={() => toggleVoiceInput("chat")}
            modelPref={modelPref}
            thinkPref={thinkPref}
            onModelChange={handleModelPref}
            onThinkChange={handleThinkPref}
            showUseTyped={typedMeta.show}
            typedWords={typedMeta.words}
            onUseTyped={handleUseTypedScope}
            attachments={chatAttachments}
            attachBusy={attachBusy}
            onAddFiles={handleAddChatFiles}
            onRemoveAttachment={handleRemoveChatAttachment}
          />
        </div>

        {/* ── 2. Scope Notes — point-form, chat-fed, hand-editable ────── */}
        <div className="sa-section">
          <ScopeNotesCard doc={scopeDoc} onEdit={editScopeDoc} disabled={chatStreaming} />
        </div>

        {/* ── 3. Scope Document — the live rough draft ────────────────── */}
        <div className="sa-section">
          <ScopeDocCard
            doc={scopeDoc}
            onEdit={editScopeDoc}
            disabled={chatStreaming}
            onConvert={handleConvertScope}
            converting={converting}
            error={convertError}
            family={form.service_type || "other"}
            assessmentId={assessmentId}
          />
        </div>

        {/* ── 4. Site media — photos ride Convert to Quote to the Jobber request ─ */}
        <div className="sa-section sa-media-section">
          <div className="sa-doc-eyebrow">Site Photos &amp; Video</div>

          {form.photos.length > 0 && (
            <div className="sa-photo-grid">
              {form.photos.map((p) => (
                <PhotoThumb key={p.id} photo={p} onRemove={handleRemovePhoto} disabled={converting} />
              ))}
            </div>
          )}

          {(() => {
            const unpushed = (form.photos || []).filter((p) => p.status === "uploaded" && !p.pushed_at);
            if (!unpushed.length || !form.jobber_request_id) return null;
            return (
              <div className="sa-media-hint">
                ⚡ {unpushed.length} photo{unpushed.length === 1 ? "" : "s"} + your Scope Notes push to the Jobber request when you hit <strong>Convert to Quote</strong>.
              </div>
            );
          })()}

          <div className="sa-hero-actions sa-media-actions">
            <button
              className="sa-voice-btn"
              onClick={() => photoCameraRef.current?.click()}
              type="button"
              disabled={photoBusy}
            >
              <span className="sa-voice-icon">📷</span>
              <span>{photoBusy ? "Uploading…" : "Take Photo"}</span>
            </button>

            <button
              className="sa-voice-btn"
              onClick={() => photoPickRef.current?.click()}
              type="button"
              disabled={photoBusy}
            >
              <span className="sa-voice-icon">🖼</span>
              <span>Add Photos</span>
            </button>
          </div>

          {/* Take Photo opens the phone's native camera; Add Photos picks from the gallery. */}
          <input
            ref={photoCameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={(e) => { handleAddPhotos(e.target.files); e.target.value = ""; }}
          />
          <input
            ref={photoPickRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => { handleAddPhotos(e.target.files); e.target.value = ""; }}
          />

          <div className="sa-hero-actions sa-media-actions">
            <button
              className="sa-voice-btn"
              onClick={() => recordInputRef.current?.click()}
              type="button"
            >
              <span className="sa-voice-icon">🎬</span>
              <span>Record Video</span>
            </button>

            <button
              className="sa-voice-btn"
              onClick={() => uploadInputRef.current?.click()}
              type="button"
            >
              <span className="sa-voice-icon">⬆️</span>
              <span>Upload Video</span>
            </button>
          </div>

          {/* Record opens the phone's native camera; Upload picks an existing file. */}
          <input
            ref={recordInputRef}
            type="file"
            accept="video/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={(e) => { handleAddVideo(e.target.files?.[0]); e.target.value = ""; }}
          />
          <input
            ref={uploadInputRef}
            type="file"
            accept="video/*"
            style={{ display: "none" }}
            onChange={(e) => { handleAddVideo(e.target.files?.[0]); e.target.value = ""; }}
          />

          {videos.length > 0 && (
            <div className="sa-video-list">
              {videos.map((v) => {
                const aState = analysisState[v.id];
                const analyzing = !!aState && aState.status && aState.status !== "done" && aState.status !== "error";
                const analysis = v.analysis;
                return (
                <div key={v.id} className="sa-video-card">
                  <div className="sa-video-item">
                    <span className="sa-video-thumb">🎬</span>
                    <div className="sa-video-meta">
                      <div className="sa-video-name">{v.name}</div>
                      <div className="sa-video-sub">
                        {formatDuration(v.duration)} · {formatVideoSize(v.size)} ·{" "}
                        <span className={`sa-video-status sa-vs-${v.status}`}>
                          {VIDEO_STATUS_LABELS[v.status] || v.status}
                        </span>
                      </div>
                    </div>
                    <button
                      className="sa-video-act"
                      onClick={() => handlePlayVideo(v)}
                      type="button"
                      title="Play"
                      disabled={playLoading || v.status !== "uploaded"}
                    >▶</button>
                    {v.status === "uploaded" && (
                      <button
                        className="sa-video-act sa-video-analyze"
                        onClick={() => handleAnalyzeVideo(v)}
                        type="button"
                        title={analysis ? "Re-analyze with AI" : "Analyze with AI"}
                        disabled={analyzing}
                      >{analyzing ? "⏳" : "✨"}</button>
                    )}
                    <button
                      className="sa-video-act sa-video-del"
                      onClick={() => handleRemoveVideo(v)}
                      type="button"
                      title="Remove"
                    >✕</button>
                  </div>

                  {analyzing && (
                    <div className="sa-analysis-status">{ANALYSIS_LABELS[aState.status] || "Working…"}</div>
                  )}
                  {aState && aState.status === "error" && (
                    <div className="sa-analysis-err">Analysis failed: {aState.error}</div>
                  )}

                  {analysis && Array.isArray(analysis.sections) && analysis.sections.length > 0 && (
                    <div className="sa-analysis">
                      <div className="sa-analysis-head">
                        🤖 AI walkthrough · {analysis.sections.length} stop(s)
                        {analysis.had_transcript ? "" : " · no narration detected"}
                      </div>
                      {analysis.sections.map((sec, si) => (
                        <div key={si} className="sa-stop">
                          <div className="sa-stop-title">
                            {sec.title || `Stop ${si + 1}`}
                            <span className="sa-stop-time">{formatDuration(sec.start)}–{formatDuration(sec.end)}</span>
                          </div>
                          {sec.summary && <div className="sa-stop-summary">{sec.summary}</div>}
                          {Array.isArray(sec.keywords) && sec.keywords.length > 0 && (
                            <div className="sa-stop-tags">
                              {sec.keywords.map((k, ki) => <span key={ki} className="sa-tag">{k}</span>)}
                            </div>
                          )}
                          <div className="sa-frames">
                            {sec.frames.map((f, fi) => (
                              <FrameThumb key={fi} path={f.path} caption={f.caption} />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {playingUrl && (
            <div className="sa-video-player">
              <video
                src={playingUrl}
                controls
                autoPlay
                playsInline
                style={{ width: "100%", borderRadius: 10, background: "#000" }}
              />
              <button className="sa-video-close" onClick={() => setPlayingUrl(null)} type="button">
                Close
              </button>
            </div>
          )}

        </div>



        {/* ── Actions ─────────────────────────────────────────────────── */}
        <div className="sa-section sa-actions-section">
          <button className="sa-action-btn sa-save-draft" onClick={handleSaveDraft}>
            Save Draft
          </button>
          {/* Quoting now flows through Scope Chat \u2192 "\u26A1 Convert to Quote" (which
              builds multi-option quotes on its own). The old "Multi-option Build"
              button was retired 2026-07-14. */}
          {whisperAvailable === false && (
            <div style={{ fontSize: 11, color: "#f59e0b", textAlign: "center", marginTop: 4 }}>
              Whisper unavailable \u2014 using browser speech (may auto-stop on pauses)
            </div>
          )}
        </div>

      </div>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// JobberNotesCard — read-only view of the team's notes on the linked Jobber
// request + its job(s). Pulled in so the tech sees them in ElecBid and the
// scope chat / quote builder use them as context (no copy/paste).
// ═══════════════════════════════════════════════════════════════════════════
function JobberNotesCard({
  notes, media, mediaNote, loading, open, onToggle,
  // Selection + send-to-chat (see handleUseJobberNotes)
  selectedIds, selection, sentNoteIds, onToggleNote, onSelectAll,
  addendum, onAddendumChange, onSend, streaming,
  onMic, isListening, isTranscribing,
  showIncludeTyped, includeTyped, onIncludeTypedChange, typedWords, typedChars,
}) {
  const list = Array.isArray(notes) ? notes : [];
  const sel = selectedIds instanceof Set ? selectedIds : new Set();
  const sent = sentNoteIds instanceof Set ? sentNoteIds : new Set();
  // selection: null = never chose (all notes ride as context), [] = ticked NONE
  const neverChose = !Array.isArray(selection);
  const explicitNone = Array.isArray(selection) && selection.length === 0 && list.length > 0;
  const picked = list.filter((n) => n.id && sel.has(String(n.id)));
  const fresh = picked.filter((n) => !sent.has(String(n.id)));   // ticked, not yet sent
  const already = picked.length - fresh.length;
  const addText = String(addendum || "").trim();
  const withTyped = !!(showIncludeTyped && includeTyped);
  const canSend = !streaming && (fresh.length > 0 || !!addText || withTyped);
  // Size of what Send puts in ONE message, measured like the handler builds it:
  // fresh note bodies + a tag line each, clarifications, the typed notes when
  // they ride along, and ~600 chars of preamble/headers. Past ~24k the server
  // tells the AI it can't see the rest, so warn before that.
  const sendChars = fresh.reduce((s, n) => s + String(n.message || "").length + 60, 0)
    + addText.length + (withTyped ? (typedChars || 0) : 0) + 600;
  const sendBits = [
    fresh.length ? `${fresh.length} note${fresh.length === 1 ? "" : "s"}` : "",
    addText ? "clarifications" : "",
    withTyped ? "typed scope" : "",
  ].filter(Boolean);
  const sendLabel = sendBits.length
    ? `Send ${sendBits.join(" + ")} to Scope Chat ↵`
    : already > 0
      ? `All ${already} ticked note${already === 1 ? "" : "s"} already sent — type a clarification to send`
      : "Tick a note or type a clarification to send";
  const linkBtn = { background: "none", border: "none", padding: 0, color: "#a5ce3a", fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: "'Roboto', sans-serif" };
  // Viewable photos/videos for the strip + lightbox (docs are rare on notes —
  // skipped here; they still reach the AI-visible archive via job_media).
  const pics = (Array.isArray(media) ? media : []).filter((m) => m.isImage || m.isVideo);
  const [showAllPics, setShowAllPics] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const PICS_FIRST = 8;
  const shownPics = showAllPics ? pics : pics.slice(0, PICS_FIRST);
  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  const tile = { width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: "1px solid #2a3140", display: "block", cursor: "pointer", background: "#1c2028" };
  return (
    <div className="sa-notes-card">
      <button
        type="button"
        onClick={onToggle}
        style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left" }}
      >
        <span className="sa-doc-eyebrow" style={{ margin: 0 }}>📋 Jobber Notes · from the request &amp; job</span>
        {!!list.length && (
          <span style={{ fontSize: 11, fontWeight: 700, color: "#a5ce3a", background: "rgba(165,206,58,.12)", border: "1px solid rgba(165,206,58,.35)", borderRadius: 999, padding: "1px 8px" }}>{list.length}</span>
        )}
        {picked.length > 0 && (
          <span style={{ fontSize: 11, fontWeight: 700, color: "#0d0f12", background: "#a5ce3a", borderRadius: 999, padding: "1px 8px" }}>✓ {picked.length} ticked</span>
        )}
        {!!pics.length && (
          <span style={{ fontSize: 11, fontWeight: 700, color: "#7dd3fc", background: "rgba(125,211,252,.10)", border: "1px solid rgba(125,211,252,.3)", borderRadius: 999, padding: "1px 8px" }}>📷 {pics.length}</span>
        )}
        <span style={{ marginLeft: "auto", fontSize: 12, opacity: 0.6 }}>{open ? "▾" : "▸"}</span>
      </button>
      <div className="sa-doc-subcaption">
        Typed by the team in Jobber — pulled in automatically. Tick the notes that describe THIS job (skip the junk calls), add any clarifications, and hit Send — the scope chat quotes from exactly what you send, word for word (and can look at the photos).
      </div>
      {open && (
        <>
          {loading && <div className="sa-notes-empty">Loading notes from Jobber…</div>}
          {!loading && !list.length && !pics.length && (
            <div className="sa-notes-empty">No notes on the Jobber request or job yet.</div>
          )}
          {!loading && !!pics.length && (
            <div style={{ margin: "8px 0 4px" }}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {shownPics.map((m, i) => (
                  <div key={m.id} onClick={() => setLightbox(i)} title={m.fileName || m.noteMessage || "photo"} style={{ position: "relative" }}>
                    {m.isVideo ? (
                      <div style={{ ...tile, display: "flex", alignItems: "center", justifyContent: "center", color: "#e8eaf0", fontSize: 20 }}>▶</div>
                    ) : (
                      <img src={m.thumbUrl || m.url} alt={m.fileName || "photo"} loading="lazy" style={tile} />
                    )}
                    {m.jobNumber && (
                      <span style={{ position: "absolute", left: 3, bottom: 3, background: "rgba(6,8,10,.75)", color: "#a5ce3a", fontSize: 9, fontWeight: 700, borderRadius: 5, padding: "0px 4px" }}>#{m.jobNumber}</span>
                    )}
                  </div>
                ))}
                {!showAllPics && pics.length > PICS_FIRST && (
                  <button onClick={() => setShowAllPics(true)}
                    style={{ ...tile, border: "1px dashed #2a3140", color: "#8892a4", fontSize: 11, fontWeight: 700 }}>
                    +{pics.length - PICS_FIRST}
                  </button>
                )}
              </div>
              {mediaNote && <div style={{ fontSize: 11, color: "#8892a4", marginTop: 4 }}>{mediaNote}</div>}
            </div>
          )}
          {!loading && !!list.length && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "8px 0 6px", fontSize: 11.5, color: "#8892a4", flexWrap: "wrap" }}>
              <span>
                {picked.length
                  ? `${picked.length} of ${list.length} ticked${already ? ` · ${already} already sent` : ""}`
                  : explicitNone
                    ? `None ticked — the AI is told to ignore all ${list.length} notes`
                    : `Nothing ticked yet — until you tick some, all ${list.length} ride along as background context`}
              </span>
              <button type="button" style={linkBtn} onClick={() => onSelectAll && onSelectAll(true)}>Select all</button>
              {!explicitNone && <button type="button" style={linkBtn} onClick={() => onSelectAll && onSelectAll(false)}>{neverChose ? "Use none" : "Untick all"}</button>}
            </div>
          )}
          {!loading && list.map((n, i) => {
            const on = !!n.id && sel.has(String(n.id));
            return (
              <label
                key={n.id || i}
                className="sa-doc-row"
                style={{ alignItems: "flex-start", gap: 8, cursor: n.id ? "pointer" : "default", padding: "6px 8px", borderRadius: 8, marginBottom: 4, background: on ? "rgba(165,206,58,.07)" : "transparent", border: `1px solid ${on ? "rgba(165,206,58,.35)" : "transparent"}` }}
              >
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!n.id}
                  onChange={() => onToggleNote && onToggleNote(n.id)}
                  title={on ? "Untick — leave this note out" : "Tick — quote from this note"}
                  style={{ marginTop: 3, width: 15, height: 15, flexShrink: 0, accentColor: "#a5ce3a", cursor: n.id ? "pointer" : "default" }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.7, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ color: "#a5ce3a" }}>{n.sourceLabel || (n.source === "job" ? "Job" : "Request")}</span>
                    <span>· {n.createdBy || "team"}</span>
                    {fmtDate(n.createdAt) && <span>· {fmtDate(n.createdAt)}</span>}
                    {!!n.id && sent.has(String(n.id)) && <span style={{ color: "#a5ce3a" }} title="Already sent to the scope chat — won't be resent">· ✓ sent</span>}
                  </div>
                  <div style={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.45 }}>{n.message}</div>
                </div>
              </label>
            );
          })}
          {!loading && (!!list.length || showIncludeTyped) && (
            <div style={{ marginTop: 10, borderTop: "1px solid #2a3140", paddingTop: 10 }}>
              <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
                <textarea
                  className="sa-textarea"
                  rows={2}
                  value={addendum || ""}
                  placeholder="Clarifications or anything on top of the Jobber notes… (optional — where these disagree with a note, yours win)"
                  style={{ fontSize: 13, minHeight: 44 }}
                  onChange={(e) => onAddendumChange && onAddendumChange(e.target.value)}
                  onKeyDown={(e) => {
                    // Ctrl/Cmd+Enter sends; plain Enter is a newline here (notes are multi-line).
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && canSend) { e.preventDefault(); onSend && onSend(); }
                  }}
                />
                <button
                  type="button"
                  className={`sa-chat-mic${isListening ? " listening" : ""}`}
                  onClick={onMic}
                  disabled={isTranscribing}
                  title={isTranscribing ? "Transcribing…" : isListening ? "Tap to stop" : "Dictate clarifications"}
                >{isTranscribing ? "⏳" : isListening ? "🔴" : "🎤"}</button>
              </div>
              {showIncludeTyped && (
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#8892a4", marginTop: 6, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!includeTyped} onChange={(e) => onIncludeTypedChange && onIncludeTypedChange(e.target.checked)} style={{ accentColor: "#a5ce3a" }} />
                  Also send my typed assessment notes ({typedWords || 0} words)
                </label>
              )}
              {sendChars > 23000 && (
                <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 6 }}>
                  ⚠ That&apos;s ~{Math.round(sendChars / 1000)}k characters in one message. Past ~24k the AI is told it can&apos;t see the rest. Tick fewer notes and Send, then tick the rest and Send again — notes already sent stay ticked and aren&apos;t resent.
                </div>
              )}
              <button
                type="button"
                className="sa-chat-send"
                style={{ marginTop: 8, height: 38, width: "100%" }}
                onClick={() => onSend && onSend()}
                disabled={!canSend}
                title="Sends the ticked notes verbatim (oldest → newest) plus your clarifications as one scope-chat message"
              >{streaming ? "Scope chat is replying…" : sendLabel}</button>
            </div>
          )}
          {lightbox != null && (
            <Lightbox
              items={pics}
              index={lightbox}
              showContext
              onClose={() => setLightbox(null)}
              onStep={(d) => setLightbox((i) => (i + d + pics.length) % pics.length)}
            />
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ScopeChatCard — ChatGPT-style scope intake chat (streams /api/scope-chat).
//   • Bubbles mirror QuoteDraftReview's RefineChat (user right/lime, AI left)
//   • Live 💭 thinking block → collapses to a one-line toggle once the reply starts
//   • Send ⇄ ⏹ Stop swap while streaming; Ctrl/Cmd+Enter sends; mic feeds input
// ═══════════════════════════════════════════════════════════════════════════
function ScopeChatCard({
  messages, input, onInputChange, onSend, onStop, streaming,
  isListening, isTranscribing, onMic,
  modelPref, thinkPref, onModelChange, onThinkChange,
  showUseTyped, onUseTyped, typedWords,
  attachments, attachBusy, onAddFiles, onRemoveAttachment,
}) {
  const listRef = useRef(null);
  const fileInputRef = useRef(null);
  const [openThinking, setOpenThinking] = useState({}); // msg index → expanded
  const staged = attachments || [];
  const readyCount = staged.filter((a) => a.status === "ready").length;
  const canSend = !streaming && (!!input.trim() || readyCount > 0);

  // Keep the newest message in view while a reply streams in.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  return (
    <div className="sa-chat-card">
      <div className="sa-chat-head">
        <div className="sa-chat-title">Scope Chat</div>
        <div style={{ display: "flex", gap: 6 }}>
          <select
            className="sa-chat-select"
            value={modelPref}
            onChange={(e) => onModelChange(e.target.value)}
            title="Model"
          >
            <option value="fast">⚡ Fast</option>
            <option value="standard">🧠 Smart</option>
            <option value="max">🚀 Max</option>
          </select>
          <select
            className="sa-chat-select"
            value={thinkPref}
            onChange={(e) => onThinkChange(e.target.value)}
            title="Thinking effort"
          >
            <option value="off">Thinking: Off</option>
            <option value="standard">Thinking: Standard</option>
            <option value="deep">Thinking: Deep</option>
          </select>
        </div>
      </div>

      <div className="sa-chat-list" ref={listRef}>
        {messages.length === 0 && (
          <div className="sa-chat-empty">
            <div>Describe the job — I'll ask what's missing and build the scope document live.</div>
            <button
              type="button"
              className="sa-chat-hint"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              title="Attach photos, video, PDF or text manuals"
            >📎 Attach a photo, video or manual — I can read nameplates, ID parts &amp; colours, and pull specs</button>
            {showUseTyped && (
              <button
                type="button"
                className="sa-chat-chip"
                onClick={onUseTyped}
                title="Sends the Scope Notes + Tech Notes fields typed on THIS assessment, in full — the AI confirms what it's working from. Jobber notes are separate: tick and send those from the Jobber Notes card above."
              >
                Use my typed assessment notes — {typedWords || 0} words ↵
              </button>
            )}
          </div>
        )}
        {messages.map((m, i) => {
          if (m.role === "user") {
            const atts = Array.isArray(m.attachments) ? m.attachments : [];
            return (
              <div key={i} className="sa-chat-msg-user">
                {atts.length > 0 && (
                  <div className="sa-chat-msg-atts">
                    {atts.map((a) => <ChatAttachmentThumb key={a.id} att={a} />)}
                  </div>
                )}
                {m.content && <div>{m.content}</div>}
              </div>
            );
          }
          const hasReply = !!(m.content && m.content.trim());
          const liveThinking = m.streaming && !hasReply && m.thinking;
          const doneThinking = m.thinking && (hasReply || !m.streaming);
          return (
            <div key={i} className="sa-chat-msg-ai">
              {liveThinking && (
                <div
                  className="sa-chat-thinking"
                  ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}
                >💭 {m.thinking}</div>
              )}
              {doneThinking && (
                <>
                  <button
                    type="button"
                    className="sa-chat-think-toggle"
                    onClick={() => setOpenThinking((o) => ({ ...o, [i]: !o[i] }))}
                  >💭 thought for a bit {openThinking[i] ? "▾" : "▸"}</button>
                  {openThinking[i] && <div className="sa-chat-thinking">{m.thinking}</div>}
                </>
              )}
              <ActivityTrail steps={m.steps} streaming={m.streaming} />
              {hasReply
                ? <RichText text={m.content} accent="#a5ce3a" />
                : (m.streaming && !m.thinking ? <div className="sa-chat-status">…</div> : null)}
              {(m.memory || []).map((chip, ci) => (
                <div key={ci} className={`sa-chat-mem sa-chat-mem-${chip.tone}`}>{chip.text}</div>
              ))}
              {m.status && <div className="sa-chat-status">{m.status}</div>}
            </div>
          );
        })}
      </div>

      {(staged.length > 0 || attachBusy) && (
        <div className="sa-chat-tray">
          {staged.map((a) => (
            <div key={a.id} className={`sa-chat-tray-chip${a.status === "error" ? " err" : ""}`} title={a.error || a.name}>
              <span className="sa-chat-tray-ico">
                {a.status === "uploading" ? "⏳" : a.kind === "image" ? "🖼" : a.kind === "video" ? "🎬" : a.kind === "document" ? "📄" : a.kind === "text" ? "📝" : "📎"}
              </span>
              <span className="sa-chat-tray-name">{a.status === "error" ? (a.error || "Failed") : a.name}</span>
              <button type="button" className="sa-chat-tray-x" title="Remove" onClick={() => onRemoveAttachment(a.id)}>✕</button>
            </div>
          ))}
        </div>
      )}

      <div className="sa-chat-inputrow">
        <input
          ref={fileInputRef}
          type="file"
          accept={CHAT_ATTACH_ACCEPT}
          multiple
          style={{ display: "none" }}
          onChange={(e) => { onAddFiles(e.target.files); e.target.value = ""; }}
        />
        <button
          type="button"
          className="sa-chat-attach"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          disabled={streaming}
          title="Attach photos, video, PDF or text manuals"
        >📎</button>
        <textarea
          className="sa-textarea sa-chat-input"
          rows={2}
          value={input}
          placeholder="Describe the job or ask about an attachment… (Enter to send, Shift+Enter for a new line)"
          onChange={(e) => {
            onInputChange(e.target.value);
            e.target.style.height = "auto";
            e.target.style.height = Math.min(e.target.scrollHeight, 140) + "px";
          }}
          onKeyDown={(e) => {
            // Enter sends (Ctrl/Cmd+Enter still works); Shift+Enter = newline.
            // While streaming or with nothing to send, Enter does nothing
            // (preventDefault keeps it from inserting blank lines).
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (canSend) onSend();
            }
          }}
          onPaste={(e) => {
            // Paste a screenshot / image straight into the chat.
            const files = Array.from(e.clipboardData?.files || []);
            if (files.length) { e.preventDefault(); onAddFiles(files); }
          }}
        />
        <button
          type="button"
          className={`sa-chat-mic${isListening ? " listening" : ""}`}
          onClick={onMic}
          disabled={isTranscribing}
          title={isTranscribing ? "Transcribing…" : isListening ? "Tap to stop" : "Voice input"}
        >{isTranscribing ? "⏳" : isListening ? "🔴" : "🎤"}</button>
        {streaming ? (
          <button type="button" className="sa-chat-send stop" onClick={onStop}>⏹ Stop</button>
        ) : (
          <button type="button" className="sa-chat-send" onClick={() => onSend()} disabled={!canSend}>
            Send
          </button>
        )}
      </div>
    </div>
  );
}

// Thumbnail/chip for a sent chat attachment. Images fetch a signed URL for a
// live preview; PDFs / text / video show a labelled chip.
function ChatAttachmentThumb({ att }) {
  const [url, setUrl] = useState(null);
  const previewPath = att.kind === "image"
    ? att.path
    : (att.kind === "video" && Array.isArray(att.frames) && att.frames[0] ? att.frames[0].path : null);
  useEffect(() => {
    if (!previewPath) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.storage.from(VIDEO_BUCKET).createSignedUrl(previewPath, 3600);
        if (!cancelled && !error) setUrl(data.signedUrl);
      } catch { /* leave chip */ }
    })();
    return () => { cancelled = true; };
  }, [previewPath]);

  if (previewPath) {
    return (
      <span className="sa-chat-att" title={att.name}>
        {url ? <img className="sa-chat-att-img" src={url} alt={att.name || "attachment"} loading="lazy" /> : <span className="sa-chat-att-ph">…</span>}
        {att.kind === "video" && <span className="sa-chat-att-badge">🎬</span>}
      </span>
    );
  }
  const ico = att.kind === "document" ? "📄" : att.kind === "text" ? "📝" : "📎";
  return <span className="sa-chat-att sa-chat-att-file" title={att.name}>{ico} <span className="sa-chat-att-fname">{att.name || "file"}</span></span>;
}


// ═══════════════════════════════════════════════════════════════════════════
// ScopeNotesCard — point-form scope/site notes, sitting between the chat and
// the scope document. Auto-generated from the chat (doc.site_notes — the AI
// adds/edits/removes lines as the tech talks) and hand-editable here: manual
// edits ride along with the next chat message (the full scope_doc is sent
// every turn), so the AI reconciles the scope document from them.
// "VERIFY:" entries get an amber accent so field-confirm items stand out.
// ═══════════════════════════════════════════════════════════════════════════
function ScopeNotesCard({ doc, onEdit, disabled }) {
  const bodyRef = useRef(null);
  const list = doc && Array.isArray(doc.site_notes) ? doc.site_notes : [];

  // Re-fit note textareas whenever the doc changes (chat updates or typing).
  useEffect(() => {
    const root = bodyRef.current;
    if (!root) return;
    root.querySelectorAll("textarea").forEach((el) => {
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 120) + "px";
    });
  }, [doc]);

  const setItem = (i, value) => onEdit((d) => {
    const l = Array.isArray(d.site_notes) ? d.site_notes.slice() : [];
    l[i] = value;
    return { ...d, site_notes: l };
  });
  const removeItem = (i) => onEdit((d) => ({
    ...d,
    site_notes: (Array.isArray(d.site_notes) ? d.site_notes : []).filter((_, j) => j !== i),
  }));
  const addItem = () => onEdit((d) => ({
    ...d,
    site_notes: [...(Array.isArray(d.site_notes) ? d.site_notes : []), ""],
  }));

  return (
    <div className="sa-notes-card" ref={bodyRef}>
      <div className="sa-doc-eyebrow">Scope Notes · built from the chat</div>
      <div className="sa-doc-subcaption">
        Point-form site facts — internal, not client-facing. Edit freely; the scope document below re-syncs from your edits with your next message.
      </div>
      {!doc && (
        <div className="sa-notes-empty">Notes build here automatically as you talk the job out above.</div>
      )}
      {doc && !list.length && (
        <div className="sa-notes-empty">No notes yet — keep talking, or add one below.</div>
      )}
      {list.map((s, i) => {
        const isVerify = /^\s*verify:/i.test(String(s || ""));
        return (
          <div key={i} className={`sa-doc-row${isVerify ? " sa-doc-row-verify" : ""}`}>
            <span className="sa-doc-bullet">•</span>
            <textarea
              className={`sa-doc-edit-line${isVerify ? " sa-doc-verify" : ""}`}
              rows={1}
              value={s}
              disabled={disabled}
              onChange={(e) => setItem(i, e.target.value)}
              onBlur={(e) => { if (!e.target.value.trim()) removeItem(i); }}
            />
            <button
              type="button"
              className="sa-doc-del"
              disabled={disabled}
              title="Remove note"
              onClick={() => removeItem(i)}
            >✕</button>
          </div>
        );
      })}
      {doc && (
        <button
          type="button"
          className="sa-doc-add"
          disabled={disabled}
          onClick={addItem}
        >+ note</button>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// ScopeDocCard — the live scope document, now two-way editable ("a document
// we both work on"). Structured inline editing (no contenteditable):
//   • Title input (uppercase look) · scope/notes/exclusions bullet rows with
//     ✕ delete + "+ line" adders (empty rows removed on blur)
//   • Labour hours (step 0.5) + rate number inputs
//   • Materials rows: qty / unit-select / description inputs (est_unit_cost —
//     and legacy est_cost — preserved, never rendered) + "+ material"
//   • Every edit funnels through onEdit(mutator) → applyScopeDoc upstream
//   • Inputs disable while a reply streams (the done-doc would stomp typing)
//   • "⚡ Convert to Quote →" uses the current (possibly hand-edited) doc
// ═══════════════════════════════════════════════════════════════════════════
// The option fields a scope-doc option carries; everything else (title,
// family, client_name, site_notes, include_warranty) is doc-level.
const SCOPE_OPT_FIELDS = ["scope", "materials", "labour_hours", "labour_rate", RATE_BY_TECH, "notes", "exclusions"];
// Present an option as if its fields sat at the doc top level, so every
// field editor below works unchanged whichever option is active.
function scopeOptionView(d, o) {
  // The rate box shows what Convert will price this option / section at: its
  // own rate only when the tech typed it (see lib/scopeTypedRate), else the
  // job's. Any other rate on the entry is a copy the card left behind or a
  // number the model wrote, and Convert ignores both. The stamp is the entry's
  // own, never inherited from the top level.
  const typed = isTypedRate(o);
  return {
    ...d,
    scope: Array.isArray(o.scope) ? o.scope : [],
    materials: Array.isArray(o.materials) ? o.materials : [],
    labour_hours: o.labour_hours ?? null,
    labour_rate: typed ? o.labour_rate : (d.labour_rate ?? o.labour_rate),
    [RATE_BY_TECH]: typed ? true : undefined,
    notes: Array.isArray(o.notes) ? o.notes : [],
    exclusions: Array.isArray(o.exclusions) ? o.exclusions : [],
  };
}

// ── Price-book picker for the Smart Assessment materials card (Phase 3) ─────
// Same quoteSearch the quote editor's 🔍 uses; picking a supplier price hands
// {product, price} back so the card can rewrite the line in book wording and
// pin master_product_id. Kept SA-local and inline-styled (the editor's modal
// is module-private to QuoteDraftReview).
const SA_PICK = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 300, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "8vh 16px 16px" },
  panel: { background: "#1c2128", border: "1px solid #333a45", borderRadius: 12, width: "100%", maxWidth: 620, maxHeight: "78vh", overflowY: "auto", color: "#e6e9ee" },
  head: { padding: "12px 14px", borderBottom: "1px solid #333a45", position: "sticky", top: 0, background: "#1c2128", zIndex: 1, display: "flex", gap: 8 },
  input: { flex: 1, background: "#12151a", color: "#e6e9ee", border: "1px solid #333a45", borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "inherit" },
  x: { background: "none", border: "1px solid #333a45", color: "#98a2b2", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontFamily: "inherit" },
  card: { background: "#161a20", border: "1px solid #333a45", borderRadius: 8, padding: 10, marginBottom: 8 },
  priceBtn: { background: "none", border: "1px solid #333a45", color: "#e6e9ee", borderRadius: 8, padding: "5px 9px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", gap: 6, alignItems: "center" },
  muted: { color: "#98a2b2", fontSize: 12, padding: 10, textAlign: "center" },
};
function SAPickModal({ initialTerm, onPick, onClose }) {
  const [term, setTerm] = useState(initialTerm || "");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [searchError, setSearchError] = useState("");
  useEffect(() => {
    const t = String(term || "").trim();
    if (t.length < 3) { setResults(null); setSearchError(""); return; }
    const timer = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await apiFetch("/api/pricebook", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "quoteSearch", term: t, limit: 20 }),
        });
        // A failed request must NEVER render as "nothing found" — an expired
        // session's 401 looks exactly like an empty price book.
        if (r.status === 401 || r.status === 403) { setSearchError("Session expired — refresh to sign back in."); setResults(null); return; }
        const j = await r.json();
        if (!r.ok || j.ok === false) { setSearchError(j.error || `Search failed (${r.status}).`); setResults(null); return; }
        setSearchError("");
        setResults(Array.isArray(j.results) ? j.results : []);
      } catch { setSearchError("Couldn't reach the price book — check your connection."); setResults(null); }
      finally { setBusy(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [term]);
  const money = (n) => `$${Number(n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div onClick={onClose} style={SA_PICK.overlay}>
      <div onClick={(e) => e.stopPropagation()} style={SA_PICK.panel}>
        <div style={SA_PICK.head}>
          <input autoFocus style={SA_PICK.input} placeholder="Search the price book… e.g. mast kit, 200A meter socket"
            value={term} onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") onClose(); }} />
          <button type="button" style={SA_PICK.x} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: 12 }}>
          {searchError && <div style={{ ...SA_PICK.muted, color: "#fca5a5" }}>{searchError}</div>}
          {results === null && !searchError && <div style={SA_PICK.muted}>Type at least 3 characters to search.{busy ? " Searching…" : ""}</div>}
          {results !== null && results.length === 0 && !busy && (
            <div style={SA_PICK.muted}>Nothing in the book for “{term}”. Keep the line as written — the matcher gets another shot at Convert.</div>
          )}
          {(results || []).map((p, i) => (
            <div key={p.master_product_id || i} style={SA_PICK.card}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                {p.display_description || p.description}
                {p.times_purchased > 0 && (
                  <span style={{ marginLeft: 8, fontWeight: 700, fontSize: 10, color: "#a5ce3a", border: "1px solid #a5ce3a55", borderRadius: 999, padding: "2px 7px", whiteSpace: "nowrap" }}>
                    bought {p.times_purchased}×
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {(p.prices || []).map((pr, j) => (
                  <button key={j} type="button" style={SA_PICK.priceBtn} title={pr.supplier_description} onClick={() => onPick(p, pr)}>
                    <span style={{ color: "#a5ce3a", fontWeight: 700 }}>{money(pr.unit_price)}</span>
                    <span>{pr.supplier_name}</span>
                    {pr.date_recorded && <span style={{ color: "#98a2b2", fontSize: 10 }}>{String(pr.date_recorded).slice(0, 10)}</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── ✂️ Piece out a lump line (Phase 4) ──────────────────────────────────────
// The ⚡ split only appears when an assembly TEMPLATE already exists. For every
// other lump — the ones we haven't learned yet — the tech had no affordance at
// all: delete the row, retype three, re-search each one. This is that gesture,
// and it is also where learned templates come from. Piecing the same lump into
// the same parts on two quotes across two days proposes a template (nothing
// goes live without approval — see api/quote-teach.js piece_out).
//
// The remainder lot defaults to whatever the estimate has LEFT OVER after the
// real parts, which is the honest number: the lump's price was a guess at the
// whole thing, so what the named parts don't account for is exactly the smalls.
function SAPieceOutModal({ line, onApply, onClose }) {
  const origEst = Number(line?.est_unit_cost ?? line?.est_cost) || 0;
  const origDesc = String(line?.description || "");
  const blank = () => ({ qty: 1, unit: "ea", description: "", est_unit_cost: 0, master_product_id: null, supplier_sku: null });
  const [rows, setRows] = useState([blank(), blank()]);
  const [keepRemainder, setKeepRemainder] = useState(true);
  const [remainderDesc, setRemainderDesc] = useState("Misc connectors, straps, fittings");
  const [remainderOverride, setRemainderOverride] = useState(null); // null = auto
  const [pickRow, setPickRow] = useState(null);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const named = rows.filter((r) => String(r.description || "").trim());
  const partsTotal = rows.reduce((a, r) => a + (Number(r.qty) || 0) * (Number(r.est_unit_cost) || 0), 0);
  const autoRemainder = Math.max(0, Math.round((origEst - partsTotal) * 100) / 100);
  const remainderAmt = remainderOverride == null ? autoRemainder : Number(remainderOverride) || 0;
  const grand = partsTotal + (keepRemainder ? remainderAmt : 0);
  const money = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const cell = { background: "#12151a", color: "#e6e9ee", border: "1px solid #333a45", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontFamily: "inherit" };
  const canApply = named.length >= 2;

  return (
    <>
      <div onClick={onClose} style={SA_PICK.overlay}>
        <div onClick={(e) => e.stopPropagation()} style={{ ...SA_PICK.panel, maxWidth: 700 }}>
          <div style={{ ...SA_PICK.head, flexDirection: "column", gap: 4, alignItems: "stretch" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>✂️ Piece out this line</div>
              <button type="button" style={SA_PICK.x} onClick={onClose}>✕</button>
            </div>
            <div style={{ fontSize: 11, color: "#98a2b2" }}>
              “{origDesc.slice(0, 90)}”{origEst > 0 ? ` · estimated ${money(origEst)}` : ""}
            </div>
          </div>

          <div style={{ padding: 12 }}>
            <div style={{ fontSize: 11, color: "#98a2b2", marginBottom: 8 }}>
              Break it into the real parts. Use 🔍 on each to load the price book’s own wording — that is what makes
              the line land a book price at Convert.
            </div>

            {rows.map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                <input style={{ ...cell, width: 46 }} type="number" value={r.qty} title="Qty"
                  onChange={(e) => setRow(i, { qty: e.target.value === "" ? "" : Number(e.target.value) })} />
                <select style={{ ...cell, width: 58 }} value={r.unit} onChange={(e) => setRow(i, { unit: e.target.value })}>
                  <option value="ea">ea</option><option value="m">m</option><option value="ft">ft</option>
                </select>
                <input style={{ ...cell, flex: 1, minWidth: 0 }} placeholder="Part description" value={r.description}
                  onChange={(e) => setRow(i, { description: e.target.value, master_product_id: null, supplier_sku: null })} />
                <input style={{ ...cell, width: 78 }} type="number" step="0.01" title="Unit cost" value={r.est_unit_cost}
                  onChange={(e) => setRow(i, { est_unit_cost: e.target.value === "" ? "" : Number(e.target.value) })} />
                <button type="button" style={{ ...SA_PICK.x, padding: "5px 8px" }} title="Search the price book"
                  onClick={() => setPickRow(i)}>🔍</button>
                <button type="button" style={{ ...SA_PICK.x, padding: "5px 8px" }} title="Remove this part"
                  onClick={() => setRows((rs) => (rs.length > 1 ? rs.filter((_, j) => j !== i) : rs))}>✕</button>
              </div>
            ))}
            <button type="button" style={{ ...SA_PICK.x, marginTop: 2 }} onClick={() => setRows((rs) => [...rs, blank()])}>+ part</button>

            <div style={{ borderTop: "1px solid #333a45", marginTop: 12, paddingTop: 10 }}>
              <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12 }}>
                <input type="checkbox" checked={keepRemainder} onChange={(e) => setKeepRemainder(e.target.checked)} />
                <span>Keep a smalls lot for what’s left</span>
              </label>
              {keepRemainder && (
                <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
                  <input style={{ ...cell, flex: 1, minWidth: 0 }} value={remainderDesc} onChange={(e) => setRemainderDesc(e.target.value)} />
                  <input style={{ ...cell, width: 78 }} type="number" step="0.01" value={remainderAmt}
                    onChange={(e) => setRemainderOverride(e.target.value === "" ? 0 : Number(e.target.value))} />
                  {remainderOverride != null && (
                    <button type="button" style={{ ...SA_PICK.x, padding: "5px 8px" }} title="Back to what the estimate leaves over"
                      onClick={() => setRemainderOverride(null)}>↺</button>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, fontSize: 12 }}>
              <span style={{ color: "#98a2b2" }}>
                Parts {money(partsTotal)}{keepRemainder ? ` + smalls ${money(remainderAmt)}` : ""} ={" "}
                <b style={{ color: Math.abs(grand - origEst) > 0.01 && origEst > 0 ? "#f2b53c" : "#a5ce3a" }}>{money(grand)}</b>
                {origEst > 0 && Math.abs(grand - origEst) > 0.01 && (
                  <span> · was {money(origEst)}</span>
                )}
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <button type="button" style={SA_PICK.x} onClick={onClose}>Cancel</button>
                <button type="button" disabled={!canApply}
                  style={{ ...SA_PICK.x, borderColor: canApply ? "#a5ce3a" : "#333a45", color: canApply ? "#a5ce3a" : "#667085", cursor: canApply ? "pointer" : "default" }}
                  onClick={() => onApply(
                    named.map((r) => ({ ...r, qty: Number(r.qty) || 1, est_unit_cost: Number(r.est_unit_cost) || 0 })),
                    keepRemainder && remainderAmt > 0 ? { description: remainderDesc, est_unit_cost: remainderAmt } : null,
                  )}>
                  Piece it out
                </button>
              </div>
            </div>
            {!canApply && <div style={{ fontSize: 11, color: "#98a2b2", marginTop: 6 }}>Name at least two parts — one part is a rename, not a piece-out.</div>}
          </div>
        </div>
      </div>
      {pickRow != null && (
        <SAPickModal
          initialTerm={String(rows[pickRow]?.description || origDesc)}
          onPick={(product, price) => {
            setRow(pickRow, {
              description: price.display_description || price.supplier_description || product.description,
              unit: product.unit || "ea",
              est_unit_cost: Number(price.unit_price) || 0,
              master_product_id: product.master_product_id || null,
              supplier_sku: price.supplier_sku || null,
            });
            setPickRow(null);
          }}
          onClose={() => setPickRow(null)}
        />
      )}
    </>
  );
}

// ── 📦 Package picker (SA materials card) ───────────────────────────────────
// Same saved_material_packages the takeoff and auto-fold use; picking one runs
// the SERVER fold (quote-brain foldPackage) — the same appendPackage +
// overlap-supersede engine as the auto path, so a hand-added package can never
// behave differently from a triggered one (no client-side re-implementation).
function SAPackageModal({ onPicked, onClose }) {
  const [pkgs, setPkgs] = useState(null);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null); // package with its line list expanded
  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch("/api/takeoff", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "listPackages" }),
        });
        const j = await r.json();
        if (!r.ok || j.ok === false) { setErr(j.error || `Couldn't load packages (${r.status})`); setPkgs([]); return; }
        setPkgs(Array.isArray(j.packages) ? j.packages : []);
      } catch (e) { setErr(e.message); setPkgs([]); }
    })();
  }, []);
  return (
    <div onClick={onClose} style={SA_PICK.overlay}>
      <div onClick={(e) => e.stopPropagation()} style={SA_PICK.panel}>
        <div style={SA_PICK.head}>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 700, alignSelf: "center" }}>📦 Add a material package</div>
          <button type="button" style={SA_PICK.x} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: 12 }}>
          {err && <div style={{ ...SA_PICK.muted, color: "#fca5a5" }}>{err}</div>}
          {pkgs === null && !err && <div style={SA_PICK.muted}>Loading packages…</div>}
          {pkgs !== null && pkgs.length === 0 && !err && (
            <div style={SA_PICK.muted}>No saved packages yet — save one from a takeoff sheet (📦 on any selection).</div>
          )}
          {(pkgs || []).map((p) => (
            <div key={p.id} style={{ ...SA_PICK.card, color: "#e6e9ee" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <button type="button" onClick={() => onPicked(p)}
                  style={{ flex: 1, textAlign: "left", cursor: "pointer", fontFamily: "inherit", color: "inherit", background: "none", border: "none", padding: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: "#98a2b2", marginTop: 3 }}>
                    {p.item_count} line{p.item_count === 1 ? "" : "s"}
                    {p.use_count > 0 ? ` · used ${p.use_count}×` : ""}
                    {p.auto_apply ? ` · auto: “${String(p.auto_apply).slice(0, 40)}”` : ""}
                    {p.source_job_number ? ` · from job #${p.source_job_number}` : ""}
                  </div>
                </button>
                <button type="button" title="See what's in this package"
                  onClick={() => setOpenId(openId === p.id ? null : p.id)}
                  style={{ background: "none", border: "1px solid #333a45", borderRadius: 6, color: "#98a2b2", fontSize: 10.5, padding: "3px 8px", cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
                  {openId === p.id ? "▾ hide" : "▸ view"}
                </button>
              </div>
              {openId === p.id && (
                <div style={{ marginTop: 6, borderTop: "1px solid #262c36", paddingTop: 5 }}>
                  {(p.items || []).filter(it => String(it?.description || "").trim()).map((it, k) => (
                    <div key={k} style={{ fontSize: 11.5, color: "#c6ccd8", padding: "1px 0" }}>
                      {it.qty ?? 1} {it.unit || "ea"} · {it.description}
                      {it.when && <span style={{ color: "#f2b53c" }}> · only if “{it.when.split(",")[0].trim()}”</span>}
                      {it.unless && <span style={{ color: "#98a2b2" }}> · unless “{it.unless.split(",")[0].trim()}”</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScopeDocCard({ doc, onEdit, disabled, onConvert, converting, error, family, assessmentId }) {
  const bodyRef = useRef(null);
  const hasScope = !!(doc && (
    (Array.isArray(doc.scope) && doc.scope.length) ||
    (Array.isArray(doc.sections) && doc.sections.some(s =>
      (Array.isArray(s?.scope) && s.scope.length) || (Array.isArray(s?.materials) && s.materials.length)))
  ));
  const familyLabel = doc && doc.family
    ? (SERVICE_TYPES.find((s) => s.key === doc.family)?.label || doc.family)
    : "";

  // ── Section vs Option tabs (pre-Convert) ──────────────────────────────
  // SECTIONS (rough-in + finishing …) are ADDITIVE phases — every one is built
  // and the prices SUM. OPTIONS are mutually-exclusive (pick one); their top
  // level mirrors the recommended entry. A doc is one or the other — sections
  // win when present. Sections are authoritative at Convert (read from
  // doc.sections), so section edits don't need a top-level mirror.
  const sections = doc && Array.isArray(doc.sections) ? doc.sections : [];
  const hasSections = sections.length >= 2;
  const totalSecHrs = sections.reduce((a, s) => a + (Number(s?.labour_hours) || 0), 0);
  const options = doc && !hasSections && Array.isArray(doc.options) ? doc.options : [];
  const hasOptions = options.length >= 2;
  let recIdx = options.findIndex((o) => o && o.recommended);
  if (recIdx < 0) recIdx = 0;
  const [optIdx, setOptIdx] = useState(null);
  const [secIdx, setSecIdx] = useState(0);
  const activeIdx = hasOptions ? Math.min(optIdx ?? recIdx, options.length - 1) : null;
  const activeSec = hasSections ? Math.min(secIdx, sections.length - 1) : 0;
  // What the editors below render: the active section, else the doc itself for
  // the recommended option (its live mirror), else a translated option view.
  const view = hasSections
    ? scopeOptionView(doc, sections[activeSec] || {})
    : (!doc || !hasOptions || activeIdx === recIdx
      ? doc
      : scopeOptionView(doc, options[activeIdx] || {}));

  // Re-fit bullet textareas whenever the doc or active option changes.
  useEffect(() => {
    const root = bodyRef.current;
    if (!root) return;
    root.querySelectorAll("textarea").forEach((el) => {
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 120) + "px";
    });
  }, [doc, optIdx, secIdx]);

  // ── Edit helpers — every change funnels through editActive ────────────
  // Routes the mutation to the ACTIVE option when options exist: the mutator
  // runs on the option's top-level view, the result folds back into
  // options[activeIdx], and into the doc top level too when the active
  // option is the recommended one (the mirror must never drift).
  const editActive = (mutator) => onEdit((d) => {
    // Sections take precedence: route the edit into sections[activeSec]. No
    // top-level mirror needed — Convert reads sections[] directly.
    const secs = Array.isArray(d.sections) ? d.sections : [];
    if (secs.length >= 2) {
      const ai = Math.min(secIdx, secs.length - 1);
      const next = mutator(scopeOptionView(d, secs[ai] || {}));
      if (!next || typeof next !== "object") return d;
      const newSecs = secs.slice();
      newSecs[ai] = { ...secs[ai], ...Object.fromEntries(SCOPE_OPT_FIELDS.map((f) => [f, next[f]])) };
      return { ...d, sections: newSecs };
    }
    const opts = Array.isArray(d.options) ? d.options : [];
    if (opts.length < 2) return mutator(d);
    let ri = opts.findIndex((o) => o && o.recommended);
    if (ri < 0) ri = 0;
    const ai = Math.min(optIdx ?? ri, opts.length - 1);
    const next = mutator(ai === ri ? d : scopeOptionView(d, opts[ai] || {}));
    if (!next || typeof next !== "object") return d;
    const newOpts = opts.slice();
    newOpts[ai] = { ...opts[ai], ...Object.fromEntries(SCOPE_OPT_FIELDS.map((f) => [f, next[f]])) };
    const merged = { ...next, options: newOpts };
    if (ai !== ri) for (const f of SCOPE_OPT_FIELDS) merged[f] = d[f]; // top level stays = recommended
    return merged;
  });
  const num = (v) => {
    if (v === "" || v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const setField = (field, value) => editActive((d) => ({ ...d, [field]: value }));
  // A rate typed in the box is the tech's: the stamp is how Convert tells it
  // from a rate the model wrote, and what keeps it through the model's next
  // reply. Stamped on the keystroke only — leaving the box (blur) just tidies
  // the number, so tabbing through an option never marks its rate as typed.
  // An emptied box drops the stamp and the option goes back to the job's rate.
  const typeRate = (value) => editActive((d) => ({ ...d, labour_rate: value, [RATE_BY_TECH]: true }));
  const settleRate = (value) => editActive((d) => {
    const n = num(value);
    return { ...d, labour_rate: n, [RATE_BY_TECH]: isTypedRate(d) && n > 0 ? true : undefined };
  });
  const setListItem = (field, i, value) => editActive((d) => {
    const list = Array.isArray(d[field]) ? d[field].slice() : [];
    list[i] = value;
    return { ...d, [field]: list };
  });
  const removeListItem = (field, i) => editActive((d) => ({
    ...d,
    [field]: (Array.isArray(d[field]) ? d[field] : []).filter((_, j) => j !== i),
  }));
  const addListItem = (field, value) => editActive((d) => ({
    ...d,
    [field]: [...(Array.isArray(d[field]) ? d[field] : []), value],
  }));
  const setMaterial = (i, key, value) => editActive((d) => {
    const mats = Array.isArray(d.materials) ? d.materials.slice() : [];
    const before = mats[i] || {};
    const next = { ...before, [key]: value }; // other fields (incl. est_unit_cost/est_cost) untouched
    // Retyping the description breaks the pin — same convention as the quote
    // editor: the pin was an identity for the OLD wording, and a load-bearing
    // stale pin would price the old product under the new text at Convert.
    if (key === "description" && before.master_product_id && String(value) !== String(before.description ?? "")) {
      next.master_product_id = null;
    }
    mats[i] = next;
    return { ...d, materials: mats };
  });

  // One editable bullet-list section (Scope of Work / Notes / Exclusions).
  const renderBullets = (field, label, addLabel) => {
    const list = view && Array.isArray(view[field]) ? view[field] : [];
    return (
      <>
        <div className="sa-doc-h">{label}</div>
        {list.map((s, i) => (
          <div key={i} className="sa-doc-row">
            <span className="sa-doc-bullet">•</span>
            <textarea
              className="sa-doc-edit-line"
              rows={1}
              value={s}
              disabled={disabled}
              onChange={(e) => setListItem(field, i, e.target.value)}
              onBlur={(e) => { if (!e.target.value.trim()) removeListItem(field, i); }}
            />
            <button
              type="button"
              className="sa-doc-del"
              disabled={disabled}
              title="Remove line"
              onClick={() => removeListItem(field, i)}
            >✕</button>
          </div>
        ))}
        <button
          type="button"
          className="sa-doc-add"
          disabled={disabled}
          onClick={() => addListItem(field, "")}
        >{addLabel}</button>
      </>
    );
  };

  const mats = view && Array.isArray(view.materials) ? view.materials : [];

  // ── Price-book match preview + pick + split (Phase 3) ─────────────────────
  // matchPreview asks the REAL Convert matcher for each line's verdict (plus
  // which lines an assembly template would split). Chips render only while the
  // doc still matches the checked signature — any edit greys them until the
  // next check, because a stale chip lies. Auto-runs when a reply finishes
  // streaming (the AI just rewrote the doc); NEVER per keystroke — each check
  // is real matcher work server-side.
  const [matchInfo, setMatchInfo] = useState(null); // { sig, lines }
  const [checking, setChecking] = useState(false);
  const [pickIdx, setPickIdx] = useState(null);
  const [pieceIdx, setPieceIdx] = useState(null);
  const [pkgOpen, setPkgOpen] = useState(false);
  const [pkgMsg, setPkgMsg] = useState("");
  // Pending "fix the rest of the doc too?" offer from the last 🔍 pick.
  const [propose, setPropose] = useState(null);
  // Outcome of that offer ("Fixed 3 more lines ✓" / already-changed) — shown
  // where the bar was, cleared on the next pick.
  const [propMsg, setPropMsg] = useState("");

  // The doc's own words — drives the package auto-fold preview and the
  // per-item when/unless variant gates ("ATS outside" → 2× WP connectors) so
  // a manual 📦 add behaves exactly like the Convert-time auto-fold will.
  const lineTxtSA = (t) => String(t?.text ?? t ?? "");
  const scopeTextOf = () => [
    doc?.title, doc?.client_name, view?.label, view?.title,
    ...(Array.isArray(view?.scope) ? view.scope : []).map(lineTxtSA),
    ...(Array.isArray(view?.notes) ? view.notes : []).map(lineTxtSA),
    ...(Array.isArray(doc?.site_notes) ? doc.site_notes : []).map(lineTxtSA),
  ].filter(Boolean).join("\n");

  // 📦 fold runs SERVER-side so manual and auto behave identically (overlap
  // supersede included — the package keeps the product, an overlapping draft
  // line keeps its quantity).
  const applyPackage = async (p) => {
    setPkgOpen(false); setPkgMsg("Folding in “" + p.name + "”…");
    try {
      const r = await apiFetch("/api/quote-brain", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "foldPackage", package_id: p.id, materials: mats, price: false, context_text: scopeTextOf() }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) { setPkgMsg("⚠ " + (j.error || `Fold failed (${r.status})`)); return; }
      editActive((d) => ({ ...d, materials: j.materials }));
      const bits = [`+${j.added} line${j.added === 1 ? "" : "s"}`];
      if (j.merged) bits.push(`${j.merged} overlap${j.merged === 1 ? "" : "s"} merged (kept your quantities)`);
      if (j.skipped) bits.push(`${j.skipped} already on the doc`);
      setPkgMsg(j.note || `📦 ${p.name}: ${bits.join(" · ")}`);
      setRecheckTick((t) => t + 1);
    } catch (e) { setPkgMsg("⚠ " + e.message); }
  };
  const [recheckTick, setRecheckTick] = useState(0);
  // est/purpose belong in the signature: the matcher's price-band and desc
  // derivation read them, so a doc differing only there has different verdicts.
  const matSig = (list) => JSON.stringify((list || []).map((m) => [m?.qty, m?.unit, m?.description, m?.master_product_id || null, m?.est_unit_cost ?? m?.est_cost ?? null, m?.purpose ?? null]));
  // Scope text is part of the freshness signature: the package preview and
  // its variant items are computed FROM it, so editing a bullet ("ATS
  // outside") must grey the chips + preview until the next check — an
  // Add-now on a stale card would fold the wrong variant.
  const sigNow = matSig(mats) + "|" + scopeTextOf();
  const fresh = !!(matchInfo && matchInfo.sig === sigNow);
  const checkMaterials = async (force = false) => {
    if (!mats.length) { setMatchInfo(null); return; }
    const sig = matSig(mats) + "|" + scopeTextOf();
    if (!force && matchInfo && matchInfo.sig === sig) return;
    setChecking(true);
    try {
      const r = await apiFetch("/api/quote-brain", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "matchPreview", family: family || view?.family || "other", materials: mats, scope_text: scopeTextOf() }),
      });
      const j = await r.json();
      if (r.ok && j.ok !== false && Array.isArray(j.lines)) setMatchInfo({ sig, lines: j.lines, pkgs: Array.isArray(j.packages_preview) ? j.packages_preview : [] });
    } catch { /* preview is best-effort — chips just stay grey */ }
    finally { setChecking(false); }
  };
  // hasMats flips false→true exactly once when a saved assessment's doc lands,
  // so loading an old assessment gets its chips without waiting for a turn.
  const hasMats = mats.length > 0;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!disabled && hasMats) checkMaterials(); }, [disabled, optIdx, secIdx, hasMats]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (recheckTick) checkMaterials(true); }, [recheckTick]);

  // Pick → rewrite the line in book wording, pin the product, and log the
  // correction (surface "assessment") so the nightly alias loop learns from
  // this screen exactly like it learns from the quote editor.
  const applyPickToLine = (i, product, price) => {
    const prev = mats[i] || {};
    const newDesc = price.display_description || price.supplier_description || product.description || prev.description;
    apiFetch("/api/quote-brain", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        // sheet_id is the assessment's identity. Without it the alias proposer
        // falls back to the event TIMESTAMP as the "distinct quote" key, so
        // every pick counted as its own quote and the ≥2-quotes bar meant
        // nothing on this surface.
        action: "logMatchCorrection", surface: "assessment", sheet_id: assessmentId || null,
        from: {
          description: prev.description || null,
          tier: (fresh ? matchInfo.lines?.[i]?.tier : null) || null,
          our_cost: Number(prev.est_unit_cost ?? prev.est_cost) || null,
          master_product_id: prev.master_product_id || null,
        },
        to: {
          description: newDesc,
          our_cost: Number(price.unit_price) || null,
          supplier_name: price.supplier_name || null,
          supplier_sku: price.supplier_sku || null,
          master_product_id: product.master_product_id || null,
        },
      }),
    }).catch(() => { /* the pick must never block on a log write */ });
    editActive((d) => {
      const list = Array.isArray(d.materials) ? d.materials.slice() : [];
      // Unit comes from the picked product — a lot line that gets picked IS a
      // single SKU now (ea, or m for wire), and a "lot" unit left behind would
      // send it down the allowance/explode paths at Convert.
      list[i] = { ...list[i], description: newDesc, unit: product.unit || "ea", est_unit_cost: Number(price.unit_price) || 0, master_product_id: product.master_product_id || null };
      return { ...d, materials: list };
    });
    setPickIdx(null);
    setRecheckTick((t) => t + 1);
    // Same defect, rest of the doc — offered, never auto-applied. The chip's
    // matched product is the key here (pre-Convert lines carry no
    // _price_source), so a "? no match" line only propagates by wording.
    const beforeProd = prev.master_product_id || (fresh ? matchInfo.lines?.[i]?.matched?.master_product_id : null) || null;
    // Preview matches keyed by wording, stamped into the offer. Apply
    // recomputes siblings against the LIVE list, where matchInfo's indices may
    // no longer line up (and the pick itself stales matchInfo.sig until the
    // recheck lands) — this map is the one view both moments share, so "Fix
    // all N" patches the same N lines the offer counted.
    const matchedByDesc = {};
    if (fresh) mats.forEach((m, j) => {
      const mp = matchInfo.lines?.[j]?.matched?.master_product_id;
      const k = mp && m && !m.master_product_id ? normDesc(m.description) : "";
      if (k) matchedByDesc[k] = mp;
    });
    const sibs = siblingLinesFor(augmentMatches(mats, matchedByDesc), i, { ...prev, master_product_id: beforeProd }, { master_product_id: product.master_product_id || null });
    setPropMsg("");
    setPropose(sibs.length ? { idxs: sibs, product, price, before: { ...prev, master_product_id: beforeProd }, desc: newDesc, matchedByDesc } : null);
  };

  // Apply the last pick to its sibling lines. Siblings are RECOMPUTED against
  // the live doc, not read from the offer — rows can be added, deleted or
  // reordered (or the AI can rewrite the doc) between the pick and the
  // confirm, and stale indices would patch the wrong lines. The recompute runs
  // over the offer's matched-product view (augmentMatches): the raw list has
  // no _price_source pre-Convert, so a bare recompute would silently drop
  // every "same book row" sibling the offer counted.
  const applyPropagation = () => {
    if (!propose) return;
    const { product, price, desc, before, matchedByDesc } = propose;
    const after = { master_product_id: product.master_product_id || null };
    // Empty case checked against the on-screen list — the bar's count came
    // from it, so "nothing left" here means the lines really did change.
    const now = siblingLinesFor(augmentMatches(mats, matchedByDesc), -1, before, after);
    if (!now.length) { setPropose(null); setPropMsg("Those lines have already changed — nothing left to fix ✓"); return; }
    editActive((d) => {
      const list = Array.isArray(d.materials) ? d.materials.slice() : [];
      const idxs = siblingLinesFor(augmentMatches(list, matchedByDesc), -1, before, after);
      for (const j of idxs) {
        list[j] = { ...list[j], description: desc, unit: product.unit || "ea", est_unit_cost: Number(price.unit_price) || 0, master_product_id: product.master_product_id || null };
      }
      return { ...d, materials: list };
    });
    setPropose(null);
    setPropMsg(`Fixed ${now.length} more line${now.length === 1 ? "" : "s"} ✓`);
    setRecheckTick((t) => t + 1);
  };

  // Split → replace the line with the assembly's component lines (or the
  // compound halves). Template splits add the misc remainder only when the doc
  // doesn't already carry a misc lot — one catch-all per package.
  const applySplit = (i) => {
    const info = fresh ? matchInfo.lines?.[i] : null;
    if (!info || (!info.explode && !info.compound_parts)) return;
    editActive((d) => {
      const list = Array.isArray(d.materials) ? d.materials.slice() : [];
      const m = list[i] || {};
      const lineQty = Math.max(1, Number(m.qty) || 1);
      let repl;
      if (info.explode) {
        repl = info.explode.components.map((c) => ({
          qty: (Number(c.qty) || 1) * lineQty, unit: c.unit || "ea", description: c.description,
          purpose: m.purpose || `part of: ${String(m.description || "").slice(0, 60)}`,
          est_unit_cost: Number(c.est_unit_cost) || 0,
        }));
        const hasMisc = list.some((x, j) => j !== i && String(x?.unit || "").toLowerCase() === "lot"
          && /\b(misc|msc|miscellaneous|sundries|consumables?|assorted)\b/i.test(String(x?.description || "")));
        if (info.explode.remainder && !hasMisc) {
          repl.push({ qty: 1, unit: "lot", description: info.explode.remainder.description, purpose: null, est_unit_cost: Number(info.explode.remainder.est_unit_cost) || 150 });
        }
      } else {
        // Server parity (expandCompoundMaterials): first half keeps the whole
        // line's estimate as the matcher's band anchor, the rest price fresh.
        repl = info.compound_parts.map((p, j) => ({ qty: Number(m.qty) || 1, unit: m.unit || "ea", description: p, purpose: m.purpose || null, est_unit_cost: j === 0 ? (Number(m.est_unit_cost ?? m.est_cost) || 0) : 0 }));
      }
      list.splice(i, 1, ...repl);
      return { ...d, materials: list };
    });
    setRecheckTick((t) => t + 1);
  };

  // ✂️ Piece out → the same replacement ⚡ does, but from parts the tech chose
  // rather than a template, AND recorded as composition evidence. This is the
  // only place a NEW assembly template can come from; ⚡ merely re-applies one
  // we already know, so it teaches nothing and logs nothing.
  const MISC_RE = /\b(misc|msc|miscellaneous|sundries|consumables?|assorted)\b/i;
  const applyPieceOut = (i, parts, remainder) => {
    const orig = mats[i] || {};
    const hasMisc = mats.some((x, j) => j !== i && String(x?.unit || "").toLowerCase() === "lot" && MISC_RE.test(String(x?.description || "")));
    const rem = remainder && !hasMisc ? { ...remainder, qty: 1, unit: "lot" } : null;
    apiFetch("/api/quote-brain", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "logComposition", surface: "assessment", sheet_id: assessmentId || null,
        family: family || view?.family || null,
        from: {
          description: orig.description || null, unit: orig.unit || "lot", qty: Number(orig.qty) || 1,
          est_unit_cost: Number(orig.est_unit_cost ?? orig.est_cost) || null,
          tier: (fresh ? matchInfo.lines?.[i]?.tier : null) || null,
        },
        into: [...parts, ...(rem ? [rem] : [])],
      }),
    }).catch(() => { /* the edit must never block on a log write */ });
    editActive((d) => {
      const list = Array.isArray(d.materials) ? d.materials.slice() : [];
      const m = list[i] || {};
      const lineQty = Math.max(1, Number(m.qty) || 1);
      const repl = parts.map((p) => ({
        qty: (Number(p.qty) || 1) * lineQty, unit: p.unit || "ea", description: p.description,
        purpose: m.purpose || `part of: ${String(m.description || "").slice(0, 60)}`,
        est_unit_cost: Number(p.est_unit_cost) || 0,
        // A picked part carries its pin through Convert exactly like a 🔍 pick.
        ...(p.master_product_id ? { master_product_id: p.master_product_id } : {}),
      }));
      if (rem) repl.push({ qty: 1, unit: "lot", description: rem.description, purpose: null, est_unit_cost: Number(rem.est_unit_cost) || 0 });
      list.splice(i, 1, ...repl);
      return { ...d, materials: list };
    });
    setPieceIdx(null);
    setRecheckTick((t) => t + 1);
  };

  // ✂️ offered only where a piece-out is the right move: a lot line, or a line
  // the book couldn't verify. Deliberately hidden when ⚡ is showing (a template
  // already knows this one) and on green book matches — offering to break apart
  // a line that just landed its exact product would be noise, and a tech who
  // really wants to can still edit the rows by hand.
  const canPieceOut = (i) => {
    const m = mats[i] || {};
    if (String(m.unit || "").toLowerCase() === "lot") {
      const info = fresh ? matchInfo.lines?.[i] : null;
      return !(info && (info.explode || info.compound_parts));
    }
    if (!fresh) return false;                       // no verdict yet — don't guess
    const info = matchInfo.lines?.[i];
    if (!info || info.explode || info.compound_parts) return false;
    return info.tier !== "supplier_prices" && info.tier !== "logbook_history";
  };

  // One small status chip per row. Grey dot = edited since the last check.
  const matChip = (i) => {
    const base = { fontSize: 11, lineHeight: "18px", width: 20, textAlign: "center", flexShrink: 0, cursor: "default", background: "none", border: "none", padding: 0, fontFamily: "inherit" };
    if (!fresh) return <span style={{ ...base, color: "#667085" }} title="Edited since the last check — hit 🔎 Check to refresh">·</span>;
    // Beyond the server's 30-line cap there IS no verdict — a red "no match"
    // here would be a lie.
    const info = matchInfo.lines?.[i];
    if (!info) return <span style={{ ...base, color: "#667085" }} title="Not checked — beyond the 30-line check limit">·</span>;
    if (info.explode || info.compound_parts) {
      const n = info.explode ? info.explode.components.length : info.compound_parts.length;
      return (
        <button type="button" disabled={disabled} onClick={() => applySplit(i)} style={{ ...base, color: "#f2b53c", cursor: "pointer" }}
          title={info.explode
            ? `Splits into ${n} book-matchable lines at Convert (${info.explode.name}) — click to split now`
            : `Names ${n} products (${info.compound_parts.join(" + ")}) — click to split into one line each`}>
          ⚡
        </button>
      );
    }
    if (info.tier === "supplier_prices" || info.tier === "logbook_history") {
      return <span style={{ ...base, color: "#7fc98b" }}
        title={`${info.via_pin ? "Pinned by you — " : ""}book match: ${info.matched?.description || "found"}${info.matched?.supplier_name ? ` (${info.matched.supplier_name})` : ""}${info.flag ? ` — ${info.flag}` : ""}`}>
        {info.via_pin ? "📌" : "✓"}
      </span>;
    }
    if (info.tier === "breaker_package") {
      return <span style={{ ...base, color: "#f2b53c" }} title={`AI-estimated breaker package — priced as count × the book's average cost per pole, never linked to one product. ${info.flag || ""}`}>🤖</span>;
    }
    if (info.tier === "allowance") {
      return <span style={{ ...base, color: "#f2b53c" }} title={info.non_electrical ? "Non-electrical judgment line — priced as an allowance, never book-matched" : "Judgment allowance — keeps your estimate, never book-matched"}>≈</span>;
    }
    return <span style={{ ...base, color: "#ef6a5a" }} title={`No price-book match yet${info.flag ? ` — ${info.flag}` : ""}. Tap 🔍 to pick the right product.`}>?</span>;
  };
  // ── Package groups: fold lines render COLLAPSED under one 📦 row ─────────
  // A folded package's lines all carry purpose "pkg: <name>" (the reconcile
  // step may append " · superseded…" — split it off). Contiguous runs of the
  // same name group under a single header row, collapsed by default, so a
  // 10-part termination kit reads as one line until you click it open. No
  // reordering ever happens — collapsed rows are merely not rendered, so
  // every positional mutator (setMaterial(i), removeListItem(i), chips[i])
  // keeps its index.
  const pkgNameOf = (m) => {
    const s = String(m?.purpose || "");
    return s.startsWith("pkg: ") ? s.slice(5).split(" · ")[0].trim() : null;
  };
  const [pkgShown, setPkgShown] = useState({}); // group name → expanded?
  const pkgGroups = (() => {
    const byStart = new Map(); const memberOf = new Map();
    let run = null;
    mats.forEach((m, i) => {
      const n = pkgNameOf(m);
      if (n && run && run.name === n) { run.idxs.push(i); memberOf.set(i, run); }
      else if (n) { run = { name: n, start: i, idxs: [i] }; byStart.set(i, run); memberOf.set(i, run); }
      else run = null;
    });
    // 1-line groups stay flat — a header row for one line is more noise, not less.
    for (const [s, g] of [...byStart]) if (g.idxs.length < 2) { byStart.delete(s); g.idxs.forEach(i => memberOf.delete(i)); }
    return { byStart, memberOf };
  })();
  // Every folded package name, from the raw purposes — NOT from the ≥2-line
  // groups (a 1-line fold still counts as "already on the doc" for the
  // auto-add preview, or its card re-offers a duplicate).
  const foldedPkgNames = new Set(mats.map(pkgNameOf).filter(Boolean));

  const matSummary = fresh ? (() => {
    const L = matchInfo.lines || [];
    const book = L.filter((x) => x.tier === "supplier_prices" || x.tier === "logbook_history").length;
    const allow = L.filter((x) => x.tier === "allowance").length;
    const pkg = L.filter((x) => x.tier === "breaker_package").length;
    const splits = L.filter((x) => x.explode || x.compound_parts).length;
    const guess = L.length - book - allow - pkg;
    return { book, allow, pkg, guess, splits };
  })() : null;

  return (
    <div className="sa-doc-card">
      <div className="sa-doc-eyebrow">Scope Document · live preview</div>
      {!doc ? (
        <div className="sa-doc-empty">
          Talk the job out on the left — the scope document builds here live.
        </div>
      ) : (
        <div className="sa-doc-body" ref={bodyRef}>
          {hasSections && (
            <div className="sa-doc-options">
              <div className="sa-doc-opt-tabs">
                {sections.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`sa-doc-opt-tab${i === activeSec ? " on" : ""}`}
                    onClick={() => setSecIdx(i)}
                  >
                    {s.label || `Section ${i + 1}`}
                    {Number(s.labour_hours) > 0 && (
                      <span className="sa-doc-opt-hrs"> · {s.labour_hours}h</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="sa-doc-options-cap">
                Phases add up{totalSecHrs > 0 ? ` · ${totalSecHrs}h total` : ""} — editing {sections[activeSec]?.label || `Section ${activeSec + 1}`}. Each becomes its own line item at Convert.
              </div>
            </div>
          )}
          {hasOptions && (
            <div className="sa-doc-options">
              <div className="sa-doc-opt-tabs">
                {options.map((o, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`sa-doc-opt-tab${i === activeIdx ? " on" : ""}`}
                    onClick={() => setOptIdx(i)}
                  >
                    {o.recommended ? "★ " : ""}{o.label || `Option ${i + 1}`}
                    {Number(o.labour_hours) > 0 && (
                      <span className="sa-doc-opt-hrs"> · {o.labour_hours}h</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="sa-doc-options-cap">
                Editing {options[activeIdx]?.label || `Option ${activeIdx + 1}`}
                {activeIdx === recIdx ? " (recommended)" : ""} — every option builds at Convert with your edits.
              </div>
            </div>
          )}
          <input
            className="sa-doc-title-input"
            value={doc.title || ""}
            placeholder="Title"
            disabled={disabled}
            onChange={(e) => setField("title", e.target.value)}
          />
          <div className="sa-doc-caption-edit">Edit anything — your edits ride along with your next message.</div>
          {(familyLabel || doc.client_name) && (
            <div className="sa-doc-meta">
              {familyLabel && <span className="sa-doc-chip">{familyLabel}</span>}
              {doc.client_name && <span className="sa-doc-client">{doc.client_name}</span>}
            </div>
          )}

          {renderBullets("scope", "Scope of Work", "+ line")}

          <div className="sa-doc-h">Labour</div>
          <div className="sa-doc-labour">
            <input
              type="number"
              step="0.5"
              className="sa-doc-num"
              disabled={disabled}
              value={view.labour_hours ?? ""}
              onChange={(e) => setField("labour_hours", e.target.value)}
              onBlur={(e) => setField("labour_hours", num(e.target.value))}
            />
            <span>h × $</span>
            <input
              type="number"
              className="sa-doc-num"
              disabled={disabled}
              value={view.labour_rate ?? ""}
              onChange={(e) => typeRate(e.target.value)}
              onBlur={(e) => settleRate(e.target.value)}
            />
            <span>/h</span>
            {view !== doc && isTypedRate(view) && Number(view.labour_rate) > 0 && (
              <span
                style={{ fontSize: 10.5, color: "#7fc98b", marginLeft: 6 }}
                title="You typed this rate, so it stays through chat replies and is used at Convert. Clear the box to go back to the job's rate."
              >
                your rate for this {hasSections ? "section" : "option"}
              </span>
            )}
          </div>

          <div className="sa-doc-h" style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <span>Materials</span>
            {mats.length > 0 && (
              <button type="button" disabled={disabled || checking} onClick={() => checkMaterials(true)}
                style={{ background: "none", border: "1px solid #333a45", color: "#98a2b2", borderRadius: 999, padding: "1px 9px", fontSize: 10.5, cursor: disabled || checking ? "default" : "pointer", fontFamily: "inherit" }}
                title="Run every line through the real price-book matcher (same verdicts Convert will use)">
                {checking ? "checking…" : "🔎 Check vs price book"}
              </button>
            )}
            {matSummary && (
              <span style={{ fontSize: 10.5, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
                <span style={{ color: "#7fc98b" }}>{matSummary.book} book</span>
                {matSummary.allow > 0 && <span style={{ color: "#f2b53c" }}> · {matSummary.allow} allowance</span>}
                {matSummary.pkg > 0 && <span style={{ color: "#f2b53c" }}> · 🤖 {matSummary.pkg} breaker est.</span>}
                {matSummary.guess > 0 && <span style={{ color: "#ef6a5a" }}> · {matSummary.guess} no match</span>}
                {matSummary.splits > 0 && <span style={{ color: "#f2b53c" }}> · ⚡{matSummary.splits} can split</span>}
              </span>
            )}
          </div>
          {mats.length > 0 && (
            <div className="sa-doc-mat-head">
              <span className="sa-doc-mat-qty">Qty</span>
              <span className="sa-doc-mat-unit">Unit</span>
              <span style={{ flex: 1 }}>Description</span>
              <span style={{ width: 62, flexShrink: 0 }} />
            </div>
          )}
          {mats.map((m, i) => {
            const g = pkgGroups.byStart.get(i) || null;
            const member = pkgGroups.memberOf.get(i);
            const open = member ? !!pkgShown[member.name] : true;
            const header = g ? (() => {
              const L = matchInfo?.lines || [];
              const book = fresh ? g.idxs.filter(ix => ["supplier_prices", "logbook_history"].includes(L[ix]?.tier)).length : null;
              const bad = fresh ? g.idxs.filter(ix => L[ix] && !["supplier_prices", "logbook_history", "allowance"].includes(L[ix].tier) && !L[ix].non_electrical).length : null;
              const cost = g.idxs.reduce((a, ix) => a + (Number(mats[ix]?.qty) || 1) * (Number(mats[ix]?.est_unit_cost ?? mats[ix]?.est_cost) || 0), 0);
              return (
                <div key={`pkgh_${g.start}`} onClick={() => setPkgShown(s => ({ ...s, [g.name]: !s[g.name] }))}
                  title="A saved material package — click to open or close its lines"
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 9px", margin: "3px 0", border: "1px solid rgba(165,206,58,.35)", background: "rgba(165,206,58,.07)", borderRadius: 8, cursor: "pointer", fontSize: 12, color: "#e6e9ee" }}>
                  <span style={{ color: "#98a2b2", fontSize: 10 }}>{open ? "▾" : "▸"}</span>
                  <span>📦</span>
                  <b style={{ color: "#a5ce3a" }}>{g.name}</b>
                  <span style={{ color: "#98a2b2" }}>{g.idxs.length} parts{cost > 0 ? ` · ~$${cost.toFixed(0)} cost` : ""}</span>
                  {fresh && (
                    <span style={{ fontSize: 10.5 }}>
                      <span style={{ color: "#7fc98b" }}>{book}✓</span>
                      {bad > 0 && <span style={{ color: "#ef6a5a" }}> {bad}?</span>}
                    </span>
                  )}
                  <span style={{ flex: 1 }} />
                  {!open && <span style={{ fontSize: 10.5, color: "#98a2b2" }}>click to view</span>}
                </div>
              );
            })() : null;
            // Collapsed: the header stands in for the whole run; member rows
            // render nothing (indices are untouched — they're just hidden).
            if (member && !open) return header;
            return (
              <React.Fragment key={`row_${i}`}>
                {header}
            <div className="sa-doc-row">
              <input
                type="number"
                className="sa-doc-cell sa-doc-mat-qty"
                disabled={disabled}
                value={m.qty ?? ""}
                onChange={(e) => setMaterial(i, "qty", e.target.value)}
                onBlur={(e) => setMaterial(i, "qty", num(e.target.value))}
              />
              <select
                className="sa-doc-cell sa-doc-mat-unit"
                disabled={disabled}
                value={m.unit || "ea"}
                onChange={(e) => setMaterial(i, "unit", e.target.value)}
              >
                {/* Legacy docs may carry a unit outside the standard four — keep
                    it selectable so opening the doc never silently rewrites it. */}
                {!["ea", "m", "ft", "lot", "pole"].includes(m.unit || "ea") && (
                  <option value={m.unit}>{m.unit}</option>
                )}
                <option value="ea">ea</option>
                <option value="m">m</option>
                <option value="ft">ft</option>
                <option value="lot">lot</option>
                <option value="pole">pole</option>
              </select>
              <input
                className="sa-doc-cell sa-doc-mat-desc"
                disabled={disabled}
                value={m.description ?? ""}
                placeholder="Description"
                onChange={(e) => setMaterial(i, "description", e.target.value)}
              />
              {matChip(i)}
              <button
                type="button"
                disabled={disabled}
                title="Search the price book and load the right product onto this line"
                onClick={() => setPickIdx(i)}
                style={{ background: "none", border: "none", cursor: disabled ? "default" : "pointer", fontSize: 12, width: 20, flexShrink: 0, padding: 0, fontFamily: "inherit" }}
              >🔍</button>
              {canPieceOut(i) && (
                <button
                  type="button"
                  disabled={disabled}
                  title="Break this lump into its real parts — and teach the engine to do it next time"
                  onClick={() => setPieceIdx(i)}
                  style={{ background: "none", border: "none", cursor: disabled ? "default" : "pointer", fontSize: 12, width: 20, flexShrink: 0, padding: 0, fontFamily: "inherit" }}
                >✂️</button>
              )}
              <button
                type="button"
                className="sa-doc-del"
                disabled={disabled}
                title="Remove material"
                onClick={() => removeListItem("materials", i)}
              >✕</button>
            </div>
              </React.Fragment>
            );
          })}
          {pickIdx != null && (
            <SAPickModal
              initialTerm={String(mats[pickIdx]?.description || "")}
              onPick={(product, price) => applyPickToLine(pickIdx, product, price)}
              onClose={() => setPickIdx(null)}
            />
          )}
          {pieceIdx != null && (
            <SAPieceOutModal
              line={mats[pieceIdx]}
              onApply={(parts, remainder) => applyPieceOut(pieceIdx, parts, remainder)}
              onClose={() => setPieceIdx(null)}
            />
          )}
          {propose && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", padding: "8px 10px", margin: "6px 0", border: "1px solid #f2b53c", borderRadius: 8, background: "rgba(242,181,60,0.08)", fontSize: 12, color: "#e6e9ee" }}>
              <span>
                <b>{siblingReason(propose.idxs.length, propose.before)}</b> — fix {propose.idxs.length === 1 ? "it" : "them"} the same way?
              </span>
              <div style={{ flex: 1 }} />
              <button type="button" style={{ ...SA_PICK.x, padding: "5px 8px" }} onClick={() => setPropose(null)}>Just this one</button>
              <button type="button" style={{ ...SA_PICK.x, padding: "5px 8px", borderColor: "#a5ce3a", color: "#a5ce3a" }} onClick={applyPropagation}>
                Fix {propose.idxs.length === 1 ? "it" : `all ${propose.idxs.length}`}
              </button>
            </div>
          )}
          {!propose && propMsg && (
            <div style={{ fontSize: 12, color: "#a5ce3a", padding: "4px 2px", margin: "2px 0" }}>{propMsg}</div>
          )}
          {/* Packages that WILL auto-fold at Convert (trigger matched this doc's
              text) — the tech's early warning, expandable to the exact lines,
              with an Add-now that folds through the same server engine. */}
          {fresh && (matchInfo?.pkgs || []).length > 0 && (
            <div style={{ margin: "4px 0" }}>
              {(matchInfo.pkgs || []).map((pp) => {
                if (pp.conflict_skipped) {
                  return (
                    <div key={pp.id} style={{ fontSize: 11.5, color: "#f2b53c", padding: "4px 8px" }}>
                      ⚠ 📦 “{pp.name}” matches too but loses to “{pp.lost_to}” (one size package per quote) — double-check the generator size.
                    </div>
                  );
                }
                if (foldedPkgNames.has(pp.name)) return null; // already on the doc (any line count) — no duplicate offer
                const open = !!pkgShown[`preview_${pp.id}`];
                return (
                  <div key={pp.id} style={{ border: "1px dashed rgba(165,206,58,.45)", background: "rgba(165,206,58,.05)", borderRadius: 8, padding: "6px 9px", margin: "3px 0", fontSize: 12, color: "#e6e9ee" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
                      onClick={() => setPkgShown(s => ({ ...s, [`preview_${pp.id}`]: !open }))}>
                      <span style={{ color: "#98a2b2", fontSize: 10 }}>{open ? "▾" : "▸"}</span>
                      <span>📦</span>
                      <b style={{ color: "#a5ce3a" }}>{pp.name}</b>
                      <span style={{ color: "#98a2b2" }}>will auto-add at Convert · {(pp.items || []).length} parts</span>
                      <span style={{ flex: 1 }} />
                      <button type="button" disabled={disabled}
                        title="Fold it into the doc now so you can see and edit the lines before Convert"
                        onClick={(e) => { e.stopPropagation(); applyPackage(pp); }}
                        style={{ ...SA_PICK.x, padding: "3px 8px", fontSize: 10.5 }}>Add now</button>
                    </div>
                    {open && (pp.items || []).map((it, k) => (
                      <div key={k} style={{ fontSize: 11.5, color: it.already_listed ? "#667082" : "#c6ccd8", padding: "1px 0 1px 24px", textDecoration: it.already_listed ? "line-through" : "none" }}>
                        {it.qty} {it.unit} · {it.description}{it.already_listed ? " — already on the doc" : ""}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              className="sa-doc-add"
              disabled={disabled}
              onClick={() => addListItem("materials", { qty: 1, unit: "ea", description: "", purpose: null, est_unit_cost: 0 })}
            >+ material</button>
            <button
              type="button"
              className="sa-doc-add"
              disabled={disabled}
              title="Fold a saved material package into this doc — overlapping lines merge instead of doubling"
              onClick={() => { setPkgMsg(""); setPkgOpen(true); }}
            >📦 + package</button>
            {pkgMsg && <span style={{ fontSize: 11, color: pkgMsg.startsWith("⚠") ? "#ef6a5a" : "#98a2b2" }}>{pkgMsg}</span>}
          </div>
          {pkgOpen && <SAPackageModal onPicked={applyPackage} onClose={() => setPkgOpen(false)} />}

          {renderBullets("notes", "Notes", "+ note")}
          {renderBullets("exclusions", "Exclusions", "+ exclusion")}
        </div>
      )}
      <button
        type="button"
        className="sa-ai-btn sa-doc-convert"
        onClick={onConvert}
        disabled={!hasScope || converting}
      >
        {converting ? "Pricing…" : "⚡ Convert to Quote →"}
      </button>
      <div className="sa-doc-caption">Prices are matched from the pricebook + your won jobs on convert.</div>
      {error && <div className="sa-ai-error">{error}</div>}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// CSS — Mobile-first, dark theme, lime green accents
// ═══════════════════════════════════════════════════════════════════════════

const SMART_ASSESSMENT_CSS = `
  /* ── Wrap ── */
  .sa-wrap {
    font-family: 'Roboto', sans-serif;
    background: #0d0f12;
    min-height: auto;
    color: #e8eaf0;
    max-width: 540px;
    margin: 0 auto;
    padding-bottom: 100px;
  }

  /* ── Top Bar ── */
  .sa-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px 12px;
    background: #161a20;
    border-bottom: 1px solid #2a3140;
    position: sticky;
    top: 0;
    z-index: 90;
    gap: 8px;
  }
  .sa-back-btn {
    background: none;
    border: none;
    color: #a5ce3a;
    font-size: 22px;
    cursor: pointer;
    padding: 4px 8px;
    font-family: 'Roboto', sans-serif;
    flex-shrink: 0;
  }
  .sa-topbar-center {
    flex: 1;
    text-align: center;
    min-width: 0;
  }
  .sa-topbar-title {
    font-size: 14px;
    font-weight: 700;
    color: #a5ce3a;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-family: 'Bebas Neue', sans-serif;
  }
  .sa-topbar-client {
    font-size: 12px;
    color: #8892a4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sa-save-indicator {
    flex-shrink: 0;
    min-width: 60px;
    text-align: right;
  }
  .sa-save-status {
    font-size: 11px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 4px;
  }
  .sa-save-status.saving { color: #f59e0b; background: rgba(245,158,11,0.12); }
  .sa-save-status.saved  { color: #10b981; background: rgba(16,185,129,0.12); }
  .sa-save-status.error  { color: #ef4444; background: rgba(239,68,68,0.12); }

  /* ── Sections ── */
  .sa-section {
    padding: 0 16px;
    margin-bottom: 8px;
  }

  /* ── Workflow badge ── */
  .sa-workflow-badge {
    font-size: 11px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 12px;
    border: 1px solid;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    flex-shrink: 0;
  }

  /* ── Fields ── */
  .sa-field {
    margin-bottom: 14px;
  }
  .sa-field-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 14px;
  }
  .sa-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #8892a4;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 6px;
  }
  .sa-input {
    width: 100%;
    background: #1e2328;
    border: 1px solid #2a3140;
    color: #e8eaf0;
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    padding: 10px 12px;
    border-radius: 8px;
    outline: none;
    transition: border-color 0.2s;
    box-sizing: border-box;
  }
  .sa-input:focus { border-color: #a5ce3a; }
  .sa-input::placeholder { color: #555f72; }

  .sa-select {
    width: 100%;
    background: #1e2328;
    border: 1px solid #2a3140;
    color: #e8eaf0;
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    padding: 10px 12px;
    border-radius: 8px;
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238892a4' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    cursor: pointer;
    box-sizing: border-box;
  }
  .sa-select:focus { border-color: #a5ce3a; }

  .sa-textarea {
    width: 100%;
    background: #1e2328;
    border: 1px solid #2a3140;
    color: #e8eaf0;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    padding: 10px 12px;
    border-radius: 8px;
    outline: none;
    resize: vertical;
    transition: border-color 0.2s;
    box-sizing: border-box;
  }
  .sa-textarea:focus { border-color: #a5ce3a; }
  .sa-textarea::placeholder { color: #555f72; }

  /* ── Voice input button ── */
  .sa-voice-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: #1e2328;
    border: 1.5px solid #2a3140;
    border-radius: 10px;
    color: #8892a4;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    margin-bottom: 14px;
    width: 100%;
    justify-content: center;
  }
  .sa-voice-btn:active { background: #252c38; }
  .sa-voice-btn.listening {
    border-color: #ef4444;
    color: #ef4444;
    background: rgba(239,68,68,0.08);
    animation: sa-pulse 1.5s ease-in-out infinite;
  }
  .sa-voice-btn.transcribing {
    border-color: #f59e0b;
    color: #f59e0b;
    background: rgba(245,158,11,0.08);
    opacity: 0.8;
    cursor: wait;
  }
  .sa-voice-icon { font-size: 18px; }
  @keyframes sa-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.3); }
    50% { box-shadow: 0 0 0 8px rgba(239,68,68,0); }
  }

  /* ── Job Basics — compact expandable header ── */
  .sa-basics {
    background: #161a20;
    border: 1px solid #2a3140;
    border-radius: 12px;
    margin-top: 12px;
    overflow: hidden;
  }
  .sa-basics-head {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    background: none;
    border: none;
    cursor: pointer;
    color: #e8eaf0;
    font-family: 'Roboto', sans-serif;
    text-align: left;
  }
  .sa-basics-summary {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }
  .sa-basics-name {
    font-size: 14px;
    font-weight: 700;
    color: #e8eaf0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sa-basics-sub {
    font-size: 11px;
    color: #8892a4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sa-basics-body {
    padding: 4px 12px 14px;
    border-top: 1px solid #232a35;
  }
  .sa-basics-body .sa-field:first-child { margin-top: 12px; }
  .sa-chevron {
    color: #555f72;
    font-size: 12px;
    transition: transform 0.2s;
    flex-shrink: 0;
  }
  .sa-chevron.open { transform: rotate(180deg); }

  /* ── Scope Notes card ── */
  .sa-notes-card {
    background: #0d0f12;
    border: 1px solid #2a3140;
    border-radius: 12px;
    padding: 12px;
    margin-top: 12px;
    box-sizing: border-box;
  }
  .sa-notes-empty {
    border: 2px dashed #2a3140;
    border-radius: 10px;
    color: #555f72;
    font-size: 12.5px;
    text-align: center;
    padding: 16px 12px;
    margin: 4px 0 6px;
  }

  /* ── Site photos ── */
  .sa-photo-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    gap: 8px;
    margin: 8px 0 10px;
  }
  .sa-photo-card {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    background: #161a20;
    border: 1px solid #2a3140;
  }
  .sa-photo-img {
    width: 100%;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    display: block;
    background: #000;
  }
  .sa-photo-ph {
    width: 100%;
    aspect-ratio: 1 / 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5a6473;
    font-size: 20px;
  }
  .sa-photo-del {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 6px;
    background: rgba(13,15,18,0.75);
    color: #e8eaf0;
    font-size: 12px;
    line-height: 1;
    cursor: pointer;
  }
  .sa-photo-del:hover:not(:disabled) { color: #ef4444; }
  .sa-photo-badge {
    position: absolute;
    left: 4px;
    bottom: 4px;
    right: 4px;
    background: rgba(13,15,18,0.8);
    color: #f59e0b;
    font-size: 9.5px;
    font-weight: 700;
    border-radius: 5px;
    padding: 2px 5px;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sa-photo-badge.ok  { color: #10b981; }
  .sa-photo-badge.err { color: #ef4444; }
  .sa-media-hint {
    margin: 2px 0 8px;
    padding: 8px 10px;
    border-radius: 8px;
    background: rgba(165,206,58,0.08);
    border: 1px solid rgba(165,206,58,0.28);
    color: #b7d96a;
    font-size: 12px;
    line-height: 1.4;
  }
  .sa-media-hint strong { color: #a5ce3a; }
  .sa-media-section {
    background: #161a20;
    border: 1px solid #2a3140;
    border-radius: 12px;
    padding: 12px;
    margin: 12px 16px 8px;
  }
  .sa-media-section .sa-doc-eyebrow { margin-bottom: 6px; }

  /* ── Action buttons ── */
  .sa-actions-section {
    padding: 16px 16px 32px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .sa-action-btn {
    width: 100%;
    padding: 15px;
    border-radius: 12px;
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    transition: all 0.15s;
  }
  .sa-save-draft {
    background: #1e2328;
    border: 1.5px solid #2a3140;
    color: #8892a4;
  }
  .sa-save-draft:active { background: #252c38; }
  .sa-ready-btn {
    background: #a5ce3a;
    color: #0d0f12;
  }
  .sa-ready-btn:active { transform: scale(0.98); }

  /* ── Media action rows ── */
  .sa-hero-actions {
    display: flex;
    gap: 8px;
    margin-top: 8px;
  }
  .sa-hero-actions .sa-voice-btn { flex: 1; }
  .sa-ai-btn {
    flex: 2;
    background: #a5ce3a;
    color: #0d0f12;
    border: none;
    border-radius: 10px;
    padding: 14px 16px;
    font-size: 14px;
    font-weight: 700;
    font-family: 'Roboto', sans-serif;
    cursor: pointer;
    transition: transform 0.08s, opacity 0.2s;
  }
  .sa-ai-btn:hover:not(:disabled)  { transform: scale(1.01); }
  .sa-ai-btn:active:not(:disabled) { transform: scale(0.98); }
  .sa-ai-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ── Media capture row (Talk / Record / Upload) ── */
  .sa-media-actions { flex-wrap: wrap; }
  .sa-media-actions .sa-voice-btn {
    flex: 1 1 30%;
    min-width: 96px;
    margin-bottom: 0;
    padding: 10px 8px;
    font-size: 13px;
  }
  .sa-media-actions .sa-voice-icon { font-size: 16px; }

  /* ── Video list ── */
  .sa-video-list { margin-top: 10px; display: flex; flex-direction: column; gap: 8px; }
  .sa-video-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: #161a20;
    border: 1px solid #2a3140;
    border-radius: 10px;
  }
  .sa-video-thumb { font-size: 22px; flex-shrink: 0; }
  .sa-video-meta { flex: 1; min-width: 0; }
  .sa-video-name {
    font-size: 13px; font-weight: 600; color: #e6ebf2;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .sa-video-sub { font-size: 11px; color: #8892a4; margin-top: 2px; }
  .sa-video-status { font-weight: 600; }
  .sa-vs-uploading { color: #f59e0b; }
  .sa-vs-queued    { color: #f59e0b; }
  .sa-vs-uploaded  { color: #a5ce3a; }
  .sa-vs-error     { color: #ef4444; }
  .sa-video-act {
    flex-shrink: 0;
    width: 34px; height: 34px;
    border-radius: 8px;
    border: 1px solid #2a3140;
    background: #1e2328;
    color: #a5ce3a;
    font-size: 14px;
    cursor: pointer;
  }
  .sa-video-act:disabled { opacity: 0.4; cursor: not-allowed; }
  .sa-video-del { color: #ef4444; }

  /* ── Inline video player ── */
  .sa-video-player { margin-top: 10px; }
  .sa-video-close {
    display: block;
    width: 100%;
    margin-top: 6px;
    padding: 8px;
    background: #1e2328;
    border: 1px solid #2a3140;
    border-radius: 8px;
    color: #8892a4;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }

  /* ── Video AI analysis ── */
  .sa-video-card {
    background: #161a20;
    border: 1px solid #2a3140;
    border-radius: 10px;
  }
  .sa-video-card .sa-video-item { background: transparent; border: none; }
  .sa-video-analyze { color: #c9a8ff; }
  .sa-analysis-status {
    padding: 6px 12px 10px; font-size: 12px; color: #c9a8ff; font-weight: 600;
  }
  .sa-analysis-err {
    padding: 6px 12px 10px; font-size: 12px; color: #ef4444;
  }
  .sa-analysis { padding: 0 12px 12px; }
  .sa-analysis-head {
    font-size: 12px; font-weight: 700; color: #c9a8ff;
    text-transform: uppercase; letter-spacing: 0.4px;
    margin-bottom: 10px;
  }
  .sa-stop {
    border-top: 1px solid #232a35;
    padding: 10px 0;
  }
  .sa-stop-title {
    font-size: 14px; font-weight: 700; color: #e6ebf2;
    display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap;
  }
  .sa-stop-time { font-size: 11px; color: #8892a4; font-weight: 500; }
  .sa-stop-summary { font-size: 13px; color: #c2cad6; margin-top: 4px; line-height: 1.4; }
  .sa-stop-tags { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 6px; }
  .sa-tag {
    font-size: 11px; font-weight: 600; color: #a5ce3a;
    background: rgba(165,206,58,0.1); border: 1px solid rgba(165,206,58,0.3);
    border-radius: 6px; padding: 2px 7px;
  }
  .sa-frames {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 8px; margin-top: 8px;
  }
  .sa-frame { margin: 0; }
  .sa-frame-img {
    width: 100%; aspect-ratio: 16 / 9; object-fit: cover;
    border-radius: 8px; background: #000; display: block;
  }
  .sa-frame-ph {
    width: 100%; aspect-ratio: 16 / 9; border-radius: 8px;
    background: #1e2328; display: flex; align-items: center; justify-content: center;
    color: #5a6473; font-size: 18px;
  }
  .sa-frame-cap { font-size: 11px; color: #aeb6c2; margin-top: 4px; line-height: 1.35; }

  .sa-ai-error {
    margin-top: 8px;
    padding: 10px 12px;
    border-radius: 6px;
    background: rgba(239, 68, 68, 0.12);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ff8888;
    font-size: 12px;
    line-height: 1.5;
  }

  /* ── Scope Chat / Scope Document cards (stacked full-width) ── */
  .sa-chat-card {
    background: #0d0f12;
    border: 1px solid #2a3140;
    border-radius: 12px;
    padding: 12px;
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }
  .sa-doc-card {
    background: #0d0f12;
    border: 1px solid #2a3140;
    border-radius: 12px;
    padding: 12px;
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }
  .sa-chat-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 8px;
  }
  .sa-chat-title {
    font-size: 13px;
    font-weight: 700;
    color: #a5ce3a;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .sa-chat-select {
    background: #1e2328;
    border: 1px solid #2a3140;
    color: #c4cad6;
    border-radius: 6px;
    font-size: 11px;
    padding: 4px 6px;
    font-family: 'Roboto', sans-serif;
    outline: none;
    cursor: pointer;
  }
  .sa-chat-select:focus { border-color: #a5ce3a; }
  .sa-chat-list {
    flex: 1;
    max-height: min(44vh, 420px);
    min-height: 140px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 2px;
    margin-bottom: 8px;
  }
  .sa-chat-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    color: #555f72;
    font-size: 12px;
    text-align: center;
    padding: 18px 8px;
    margin: auto 0;
  }
  .sa-chat-chip {
    background: rgba(165,206,58,0.1);
    border: 1px dashed rgba(165,206,58,0.4);
    color: #a5ce3a;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 12px;
    cursor: pointer;
    font-family: 'Roboto', sans-serif;
  }
  .sa-chat-chip:hover { background: rgba(165,206,58,0.18); }
  .sa-chat-hint {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 300px;
    background: rgba(255,255,255,0.03);
    border: 1px dashed #2a3140;
    color: #8892a4;
    border-radius: 10px;
    font-size: 11.5px;
    line-height: 1.4;
    padding: 8px 12px;
    cursor: pointer;
    text-align: center;
    font-family: 'Roboto', sans-serif;
  }
  .sa-chat-hint:hover { border-color: #3a4150; color: #c4cad6; background: rgba(255,255,255,0.05); }
  .sa-chat-msg-user {
    align-self: flex-end;
    background: rgba(165,206,58,0.18);
    color: #e8eaf0;
    border-radius: 10px 10px 2px 10px;
    padding: 8px 10px;
    font-size: 13px;
    max-width: 85%;
    white-space: pre-wrap;
    line-height: 1.4;
  }
  .sa-chat-msg-ai {
    align-self: flex-start;
    background: #1e2328;
    border: 1px solid #2a3140;
    border-radius: 10px 10px 10px 2px;
    padding: 8px 10px;
    font-size: 13px;
    max-width: 92%;
    line-height: 1.45;
    color: #e8eaf0;
  }
  .sa-chat-thinking {
    font-style: italic;
    color: #8892a4;
    font-size: 12px;
    max-height: 110px;
    overflow-y: auto;
    white-space: pre-wrap;
    margin-bottom: 4px;
    line-height: 1.4;
  }
  .sa-chat-think-toggle {
    display: block;
    background: transparent;
    border: none;
    color: #8892a4;
    font-style: italic;
    font-size: 11px;
    cursor: pointer;
    padding: 0;
    margin-bottom: 4px;
    font-family: 'Roboto', sans-serif;
  }
  .sa-chat-think-toggle:hover { color: #c4cad6; }
  .sa-chat-status {
    font-size: 11px;
    color: #8892a4;
    font-style: italic;
    margin-top: 4px;
  }
  .sa-chat-mem {
    display: inline-block;
    margin-top: 6px;
    padding: 4px 9px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.35;
    border: 1px solid transparent;
  }
  .sa-chat-mem-good  { background: rgba(165,206,58,0.12); border-color: rgba(165,206,58,0.35); color: #a5ce3a; }
  .sa-chat-mem-bad   { background: rgba(229,90,90,0.12);  border-color: rgba(229,90,90,0.4);   color: #e57070; }
  .sa-chat-mem-muted { background: rgba(136,146,164,0.1); border-color: rgba(136,146,164,0.28); color: #8892a4; }
  .sa-chat-inputrow {
    display: flex;
    gap: 6px;
    align-items: flex-end;
  }
  .sa-chat-input {
    flex: 1;
    min-height: 44px;
    max-height: 140px;
    resize: none;
    font-size: 14px;
  }
  .sa-chat-mic {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: 8px;
    border: 1.5px solid #2a3140;
    background: #1e2328;
    font-size: 16px;
    cursor: pointer;
  }
  .sa-chat-mic:disabled { opacity: 0.6; cursor: wait; }
  .sa-chat-mic.listening {
    border-color: #ef4444;
    background: rgba(239,68,68,0.08);
    animation: sa-pulse 1.5s ease-in-out infinite;
  }
  .sa-chat-send {
    flex-shrink: 0;
    height: 40px;
    padding: 0 14px;
    border-radius: 8px;
    border: none;
    background: #a5ce3a;
    color: #0d0f12;
    font-weight: 700;
    font-size: 13px;
    cursor: pointer;
    font-family: 'Roboto', sans-serif;
  }
  .sa-chat-send:disabled { opacity: 0.5; cursor: not-allowed; }
  .sa-chat-send.stop {
    background: rgba(239,68,68,0.15);
    color: #ef4444;
    border: 1.5px solid rgba(239,68,68,0.4);
  }

  /* ── Chat attachments ── */
  .sa-chat-attach {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: 8px;
    border: 1.5px solid #2a3140;
    background: #1e2328;
    font-size: 16px;
    cursor: pointer;
  }
  .sa-chat-attach:hover:not(:disabled) { border-color: #3a4150; background: #232830; }
  .sa-chat-attach:disabled { opacity: 0.5; cursor: not-allowed; }
  .sa-chat-tray {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 6px 2px 8px;
  }
  .sa-chat-tray-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 220px;
    padding: 4px 6px 4px 8px;
    border-radius: 8px;
    border: 1.5px solid #2a3140;
    background: #1a1e24;
    font-size: 12px;
    color: #c4cad6;
  }
  .sa-chat-tray-chip.err { border-color: rgba(239,68,68,0.5); color: #ef8a8a; }
  .sa-chat-tray-ico { flex-shrink: 0; }
  .sa-chat-tray-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sa-chat-tray-x {
    flex-shrink: 0;
    border: none;
    background: transparent;
    color: #8892a4;
    cursor: pointer;
    font-size: 12px;
    padding: 0 2px;
    line-height: 1;
  }
  .sa-chat-tray-x:hover { color: #ef4444; }
  .sa-chat-msg-atts {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 6px;
  }
  .sa-chat-att {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    overflow: hidden;
  }
  .sa-chat-att-img {
    width: 96px;
    height: 96px;
    object-fit: cover;
    border-radius: 8px;
    display: block;
  }
  .sa-chat-att-ph {
    width: 96px;
    height: 96px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0,0,0,0.25);
    border-radius: 8px;
    color: #0d0f12;
  }
  .sa-chat-att-badge {
    position: absolute;
    right: 4px;
    bottom: 4px;
    font-size: 13px;
    text-shadow: 0 1px 2px rgba(0,0,0,0.6);
  }
  .sa-chat-att-file {
    gap: 5px;
    padding: 6px 9px;
    background: rgba(0,0,0,0.18);
    border-radius: 8px;
    font-size: 12px;
    max-width: 200px;
  }
  .sa-chat-att-fname {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* ── Scope Document card ── */
  .sa-doc-eyebrow {
    font-size: 10px;
    color: #8892a4;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    margin-bottom: 8px;
  }
  .sa-doc-empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    border: 2px dashed #2a3140;
    border-radius: 10px;
    color: #555f72;
    font-size: 13px;
    padding: 24px 14px;
    min-height: 140px;
    margin-bottom: 10px;
  }
  .sa-doc-body { flex: 1; overflow-y: auto; margin-bottom: 10px; }
  .sa-doc-title {
    font-size: 15px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #e8eaf0;
    margin-bottom: 6px;
  }
  .sa-doc-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 8px;
  }
  .sa-doc-chip {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #a5ce3a;
    background: rgba(165,206,58,0.1);
    border: 1px solid rgba(165,206,58,0.35);
    border-radius: 999px;
    padding: 2px 8px;
  }
  .sa-doc-client { font-size: 12px; color: #c4cad6; }
  .sa-doc-h {
    font-size: 10px;
    font-weight: 700;
    color: #8892a4;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 10px 0 4px;
  }
  .sa-doc-labour {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    color: #e8eaf0;
    font-weight: 600;
  }

  /* ── Editable scope document ── */
  .sa-doc-caption-edit {
    font-size: 10px;
    color: #555f72;
    font-style: italic;
    margin: 0 0 8px;
  }
  .sa-doc-subcaption {
    font-size: 10px;
    color: #555f72;
    font-style: italic;
    margin: 0 0 4px;
  }
  /* Options banner (read-only) */
  .sa-doc-options {
    background: rgba(165,206,58,0.06);
    border: 1px solid rgba(165,206,58,0.28);
    border-radius: 8px;
    padding: 8px 10px;
    margin-bottom: 10px;
  }
  .sa-doc-options-head {
    font-size: 12px;
    color: #cbd2dd;
    font-weight: 600;
    line-height: 1.45;
  }
  .sa-doc-option-name { color: #e8eaf0; }
  .sa-doc-options-cap {
    font-size: 10px;
    color: #8892a4;
    font-style: italic;
    margin-top: 4px;
    line-height: 1.4;
  }
  /* Option tabs — toggle which option the card edits, pre-Convert */
  .sa-doc-opt-tabs { display: flex; gap: 6px; flex-wrap: wrap; }
  .sa-doc-opt-tab {
    border: 1.5px solid rgba(165,206,58,0.28);
    background: transparent;
    color: #cbd2dd;
    border-radius: 8px;
    padding: 5px 10px;
    font-size: 12px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    text-align: left;
    line-height: 1.3;
  }
  .sa-doc-opt-tab.on {
    border-color: #a5ce3a;
    background: rgba(165,206,58,0.14);
    color: #a5ce3a;
  }
  .sa-doc-opt-hrs { font-weight: 400; opacity: 0.75; }
  /* VERIFY: site-note accent */
  .sa-doc-row-verify {
    border-left: 2px solid #f59e0b;
    padding-left: 4px;
    margin-left: -6px;
  }
  .sa-doc-verify { color: #f0b866; }
  .sa-doc-title-input {
    width: 100%;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 6px;
    color: #e8eaf0;
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 3px 6px;
    margin: -3px 0 2px -6px;
    outline: none;
    box-sizing: border-box;
  }
  .sa-doc-title-input:hover:not(:disabled):not(:focus) { border-color: #2a3140; }
  .sa-doc-title-input:focus { border-color: #a5ce3a; background: #11141a; }
  .sa-doc-title-input::placeholder { color: #555f72; }
  .sa-doc-row {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    margin-bottom: 2px;
  }
  .sa-doc-bullet {
    color: #555f72;
    font-size: 12px;
    margin-top: 6px;
    flex-shrink: 0;
  }
  .sa-doc-edit-line {
    flex: 1;
    min-width: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 6px;
    color: #cbd2dd;
    font-family: 'Roboto', sans-serif;
    font-size: 12.5px;
    line-height: 1.5;
    padding: 3px 6px;
    min-height: 26px;
    resize: none;
    overflow-y: hidden;
    outline: none;
    box-sizing: border-box;
  }
  .sa-doc-edit-line:hover:not(:disabled):not(:focus) { border-color: #2a3140; }
  .sa-doc-edit-line:focus { border-color: #a5ce3a; background: #11141a; }
  .sa-doc-del {
    flex-shrink: 0;
    width: 22px;
    height: 24px;
    margin-top: 2px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #555f72;
    font-size: 12px;
    line-height: 1;
    cursor: pointer;
  }
  .sa-doc-del:hover:not(:disabled) { color: #ef4444; background: rgba(239,68,68,0.1); }
  .sa-doc-add {
    display: block;
    background: transparent;
    border: 1px dashed #2a3140;
    border-radius: 6px;
    color: #8892a4;
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    margin-top: 4px;
    cursor: pointer;
    font-family: 'Roboto', sans-serif;
  }
  .sa-doc-add:hover:not(:disabled) { border-color: #a5ce3a; color: #a5ce3a; }
  .sa-doc-num {
    width: 64px;
    background: #11141a;
    border: 1px solid #2a3140;
    border-radius: 6px;
    color: #e8eaf0;
    font-family: 'Roboto', sans-serif;
    font-size: 12.5px;
    font-weight: 600;
    text-align: center;
    padding: 3px 6px;
    outline: none;
    box-sizing: border-box;
  }
  .sa-doc-num:focus { border-color: #a5ce3a; }
  .sa-doc-cell {
    background: #11141a;
    border: 1px solid #2a3140;
    border-radius: 6px;
    color: #cbd2dd;
    font-family: 'Roboto', sans-serif;
    font-size: 12px;
    padding: 3px 6px;
    outline: none;
    box-sizing: border-box;
  }
  .sa-doc-cell:focus { border-color: #a5ce3a; }
  .sa-doc-mat-qty { width: 52px; flex: none; text-align: center; }
  .sa-doc-mat-unit { width: 52px; flex: none; }
  .sa-doc-mat-desc { flex: 1; min-width: 0; }
  .sa-doc-mat-head {
    display: flex;
    gap: 4px;
    align-items: center;
    font-size: 10px;
    color: #8892a4;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
    padding: 0 0 2px;
  }
  .sa-doc-mat-head .sa-doc-mat-qty,
  .sa-doc-mat-head .sa-doc-mat-unit { padding: 0 6px; text-align: left; }
  .sa-doc-card input:disabled,
  .sa-doc-card textarea:disabled { opacity: 0.55; cursor: not-allowed; }
  .sa-doc-convert { width: 100%; flex: none; }
  .sa-doc-caption {
    font-size: 10px;
    color: #555f72;
    text-align: center;
    margin-top: 6px;
  }

  /* ── Responsive ── */
  /* Single stacked column on every screen (chat → notes → document);
     desktop just gets a wider, more readable column. */
  @media (min-width: 768px) {
    .sa-wrap { max-width: 760px; }
    .sa-chat-list { max-height: min(52vh, 560px); }
  }
`;
